/**
 * BMS Navigation Tools — Client-side DOM manipulation for LangGraph chatbot.
 * ADDED BY: LangGraph chatbot integration (Ben Fung / ML Team)
 *
 * Uses the __TOOL_CALL__ / __TOOL_RESULT__ protocol (same as dummy_website2).
 * Plain JS functions — no browser-specific APIs required.
 */

import moment from 'moment';
import history from '../history';  // React Router's createBrowserHistory()

// ==================== Navigation guard: protect unsaved edits ====================
// BMS edit screens (ShippingOrder, Consolidation, Party, Rate, ...) render a
// react-router <Prompt when={editable} message="...">. history.js uses plain
// createBrowserHistory() with NO custom getUserConfirmation, so on history.push
// the Prompt falls back to a NATIVE window.confirm() — the "Are you sure you
// want to leave?" box (the browser shows it as "insight.mrhkg.com says Are you
// sure you want to leave?"). When the chatbot navigates AWAY from a page that
// is in Edit mode (unsaved changes), we SURFACE that native confirm so the user
// decides (user instruction 2026-10-02: manual choice, plus an in-chat warning
// telling them to answer the browser dialog — the native box is APP-modal and
// easy to miss while watching the chatbox; it also blocks ALL page JS until
// answered, so the warning is the user's cue to go click it). On non-edit
// pages the Prompt's `when` is false, so no confirm fires and navigation stays
// silent as before. Callers get { confirmed: false } when the user stays and
// must abort post-navigation steps (see _navCanceledResult).
//
// FLOW-STEP EXCEPTION (2026-10-22): the native confirm blocks ALL page JS —
// timers, SSE reading, React renders — so an unattended multi-flow chain that
// trips the guard mid-run is DEAD until a human answers (live: the ultimate-
// prompt chain froze at the first navigation after an edit flow). A flow step,
// however, was already pre-approved whole by the user via the __FLOW_CONFIRM__
// card — asking again through a page-freezing dialog is redundant AND fatal.
// While Chatbot.js executes a flow-stamped tool call (marker carries
// "flow": true — see sub_agents._stamp_flow_marker), auto-answer the leave
// guard (Leave) WITHOUT showing the native dialog, and say so in the
// navigation result so the agent knows the previous page's edit session was
// left. Free-form LLM navigations keep the manual 2026-10-02 behavior.
let _chatbotNavigating = false;
let _navGuardMessage = null;   // the guard message that fired this push, if any
let _navGuardConfirmed = true; // did the user accept (Leave)? false == they stayed
let _navGuardAutoLeft = false; // did a flow step auto-answer Leave (no dialog)?
let _flowStep = false;         // currently executing a flow-stamped tool call?
export function setFlowStep(on) { _flowStep = !!on; }
const _origWindowConfirm = window.confirm.bind(window);
window.confirm = function (message) {
  if (_chatbotNavigating) {
    _navGuardMessage = message;
    if (_flowStep && /\bleave\b/i.test(String(message || ''))) {
      // Pre-approved flow step: answer Leave ourselves — the native dialog
      // would freeze the whole page (and the chain) until a human clicks.
      _navGuardAutoLeft = true;
      _navGuardConfirmed = true;
      console.warn('[Chatbot] navigation guard (flow step): auto-left edit session:', message);
      return true;
    }
    // Show the REAL confirm so the user can keep their unsaved edits if they wish.
    _navGuardConfirmed = !!_origWindowConfirm(message);
    console.warn('[Chatbot] navigation guard (window.confirm):', message, '->', _navGuardConfirmed ? 'leave' : 'stay');
    return _navGuardConfirmed;
  }
  return _origWindowConfirm(message);
};
// history.push that respects BMS's "Are you sure you want to leave?" guard.
// Returns { confirmed, message }:
//   confirmed === true  -> navigation proceeded (no guard was active, or the user
//                          clicked Leave).
//   confirmed === false -> the user clicked Stay/Cancel; the route is UNCHANGED. The
//                          caller MUST abort any post-navigation steps and tell the
//                          agent the user stayed to keep unsaved edits
//                          (see _navCanceledResult).
// In-chatbox warning shown when a navigation triggers the browser-level
// "Are you sure you want to leave?" confirm. The native dialog is APP-modal (it
// floats over the whole page, not inside the chatbox), so a user watching the
// chatbox can miss it; this banner tells them to go click it. Self-contained DOM
// (bmsTools is a DOM-automation module) — no React plumbing needed.
function _showNavGuardNotice() {
  let el = document.getElementById('chatbot-nav-guard-notice');
  if (!el) {
    el = document.createElement('div');
    el.id = 'chatbot-nav-guard-notice';
    el.setAttribute('role', 'status');
    el.textContent = 'This page may have unsaved changes. A "Are you sure you want to leave?" confirmation has opened in your browser — please click it (Leave to discard, or Cancel to stay on this page).';
    el.style.cssText = [
      'position:absolute', 'left:10px', 'right:10px', 'bottom:8px',
      'background:#fffbe6', 'border:1px solid #faad14', 'color:#614700',
      'padding:8px 10px', 'border-radius:6px', 'font-size:12px', 'line-height:1.35',
      'z-index:100000', 'box-shadow:0 2px 8px rgba(0,0,0,.15)',
      'display:none', 'pointer-events:none',
    ].join(';');
    const host = document.getElementById('chatWindow') || document.body;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    host.appendChild(el);
  }
  el.style.display = 'block';
}
function _hideNavGuardNotice() {
  const el = document.getElementById('chatbot-nav-guard-notice');
  if (el) el.style.display = 'none';
}

function safePush(url) {
  _navGuardMessage = null;
  _navGuardConfirmed = true;
  _navGuardAutoLeft = false;
  _chatbotNavigating = true;
  // Show the warning BEFORE the push. Browsers paint pending DOM mutations
  // before a modal window.confirm, so this is visible while the "Are you sure
  // want to leave?" dialog is up. On a non-edit page no confirm fires and
  // history.push returns within the same frame, so this never paints (no flicker).
  // A flow step never shows the native dialog (auto-left above), so skip the
  // banner there too — nothing is waiting on the user.
  if (!_flowStep) _showNavGuardNotice();
  try {
    history.push(url);
  } finally {
    _chatbotNavigating = false;
    _hideNavGuardNotice();
  }
  return { confirmed: _navGuardConfirmed, message: _navGuardMessage, autoLeft: _navGuardAutoLeft };
}

// Suffix appended to a navigation result when a flow step auto-left an edit
// session, so the agent (and the engine's step log) can see what happened to
// the previous page.
function _navAutoLeftNote() {
  return _navGuardAutoLeft
    ? ' (auto-left the previous page\'s edit session — flow step, pre-approved)'
    : '';
}

// Standard tool result when the user declines the "Are you sure you want to leave?"
// guard — i.e. they chose to stay and keep unsaved edits. Phrased for the agent so it
// stops the current navigation chain and asks the user to save/discard changes first.
function _navCanceledResult(_message) {
  return {
    content: [{
      type: "text",
      text: 'The user chose to stay on the current page to keep their unsaved edits, so navigation was canceled. Stop navigating and ask the user to save or discard their changes on this page before trying again.',
    }],
  };
}

// ==================== Live progress reporter (chain-of-thought liveness) ====================
// Compound tools (read_so = open + read) legitimately run 20-40s of DOM waits with NO
// SSE stream open — client-side execution happens BETWEEN two server runs — so the
// thinking panel used to show one frozen "Read booking" spinner the whole time.
// Chatbot.js registers a reporter via setToolProgressReporter() before executing a
// tool and clears it after; long-running tools call reportProgress(note) at phase
// boundaries to update the running step's label. Purely cosmetic: a reporter failure
// must never break tool execution.
let _progressReporter = null;
export function setToolProgressReporter(fn) {
  _progressReporter = typeof fn === 'function' ? fn : null;
}
function reportProgress(note) {
  try { if (_progressReporter) _progressReporter(note); } catch (e) { /* cosmetic only */ }
}

// ==================== Route Map ====================

const ROUTE_MAP = {
  // Booking
  booking_dashboard:       "/BMS/enhance_booking",
  search_booking:          "/BMS/enhance_booking/search_booking",
  create_manual_booking:   "/BMS/enhance_booking/shipping_order/new",
  reuse_booking:           "/BMS/enhance_booking/reuse_booking",
  new_e_booking:           "/BMS/enhance_booking/new_e_booking",
  new_manual_booking:      "/BMS/enhance_booking/new_manual_booking",
  amend_e_booking:         "/BMS/enhance_booking/amend_e_booking",
  booking_pending_si:      "/BMS/enhance_booking/booking_pending_si",
  cancel_e_booking:        "/BMS/enhance_booking/cancel_e_booking",
  booking_confirmed:       "/BMS/enhance_booking/booking_confirmed",
  booking_cancelled:       "/BMS/enhance_booking/booking_cancelled",

  // Booking Approval
  ba_dashboard:            "/BMS/enhance_booking_approval",
  search_booking_approval: "/BMS/enhance_booking_approval/search_booking_approval",
  pending_for_approval:    "/BMS/enhance_booking_approval/pending_for_approval",
  request_approval:        "/BMS/enhance_booking_approval/request_approval",
  pending_customer_approval: "/BMS/enhance_booking_approval/pending_customer_approval",
  booking_approved:        "/BMS/enhance_booking_approval/booking_approved",
  booking_rejected:        "/BMS/enhance_booking_approval/booking_rejected",
  booking_advice:          "/BMS/enhance_booking_approval/booking_advice",
  ba_updated_by_mr:        "/BMS/enhance_booking_approval/booking_approval_updated_by_mr",
  ba_updated_by_customer:  "/BMS/enhance_booking_approval/booking_approval_updated_by_customer",

  // Consolidation
  consol_dashboard:        "/BMS/consolidation",
  search_consolidation:    "/BMS/consolidation/search_consolidation",
  create_consol:           "/BMS/consolidation/consolidation_management/new",
  pending_consol_creation: "/BMS/consolidation/pending_consolidation_creation",
  request_consol_approval: "/BMS/consolidation/request_consolidation_approval",
  pending_customer_consol: "/BMS/consolidation/pending_customer_consolidation_approval",
  consol_approved:         "/BMS/consolidation/consolidation_approved",
  consol_rejected:         "/BMS/consolidation/consolidation_rejected",
  consol_advice:           "/BMS/consolidation/consolidation_advice",
  amended_booking_pending: "/BMS/consolidation/amended_booking_pending",
  load_plan_pending:       "/BMS/consolidation/load_plan_pending_allocation",

  // Master BL
  master_bl_dashboard:     "/BMS/master_bl",
  search_master_bl:        "/BMS/master_bl/search_master_bl",
  create_master_bl:        "/BMS/master_bl/master_bl/new",

  // Sailing Schedule & Rate
  search_sailing_schedule:     "/BMS/sailingScheduleAndRate/search",
  sailing_schedule_maintenance: "/BMS/sailingScheduleAndRate/sailing_schedule_maintenance",
  rate_maintenance:            "/BMS/sailingScheduleAndRate/rate_maintenance",

  // Party
  party_dashboard:             "/BMS/party",
  search_party:                "/BMS/party/search_party",
  create_party:                "/BMS/party/party_maintenance/new",
  party_pending_approval:      "/BMS/party/pending_party_approval",
  party_pending_mgmt_approval: "/BMS/party/pending_party_management_approval",
  mgmt_approved:               "/BMS/party/party_management_approved",
  party_approved:              "/BMS/party/party_approved",
  party_sunac_pending:         "/BMS/party/pending_party_sunac_approval",
  party_sunac_handled:         "/BMS/party/party_sunac_approved",
  party_sunac_rejected:        "/BMS/party/party_sunac_rejected",
  party_cam_pending:           "/BMS/party/pending_for_party_sunac_cam_approval",
  party_cam_handled:           "/BMS/party/party_sunac_approved_cam",
  party_cam_rejected:          "/BMS/party/party_sunac_rejected_cam",
  party_rejected:              "/BMS/party/party_admin_rejected",
  mgmt_rejected:               "/BMS/party/party_management_rejected",
  search_customer_group_code:  "/BMS/party/account_group_code_maintenance",

  // Notification
  email_notification: "/BMS/NotificationCenter/search_email_notification/",
  search_messenger:   "/BMS/NotificationCenter/search_messenger",
  sent_messages:      "/BMS/NotificationCenter/sent_messages",
  messenger:          "/BMS/NotificationCenter/messenger",
  messenger_old:      "/BMS/NotificationCenter/messenger_old/",

  // SOPMS
  routing_template: "/SOPMS/routing_template/search_routing_template",

  // Analytics (22 Power BI pages)
  analytics_air_agent:           "/BMS/AirAgentReportPBI",
  analytics_air_shipment:        "/BMS/AirShipmentReportPBI",
  analytics_air_target:          "/BMS/AirTargetReportPBI",
  analytics_booking:             "/BMS/BookingPBI",
  analytics_carrier:             "/BMS/CarrierReportPBI",
  analytics_carrier_mqc:         "/BMS/CarrierMQC",
  analytics_carrier_performance: "/BMS/CarrierPerformance",
  analytics_carrier_perf_summary: "/BMS/CarrierPerformanceSummary",
  analytics_co2e:                "/BMS/CO2eEmissionReportPBI",
  analytics_insight_tracker:     "/BMS/InsightTrackerReportPBI",
  analytics_invoice_gp:          "/BMS/InvoiceGPPBI",
  analytics_manor:               "/BMS/ManorReportPBI",
  analytics_operation:           "/BMS/OperationReportPBI",
  analytics_operation_alert:     "/BMS/OperationAlertDashboard",
  analytics_productivity:        "/BMS/ProductivityReportPBI",
  analytics_profitability:       "/BMS/ProfitabilityReportPBI",
  analytics_sales_profitability: "/BMS/SalesProfitabilityReportPBI",
  analytics_profitability_target: "/BMS/ProfitabilityTargetReportPBI",
  analytics_sea_agent:           "/BMS/AgentReportPBI",
  analytics_sea_shipment:        "/BMS/ShipmentReportPBIDataImport",
  analytics_sea_target:          "/BMS/SeaTargetReportPBI",
  analytics_vendor:              "/BMS/VendorReportPBI",
  analytics_weblog:              "/BMS/WeblogTrackerReportPBI",

  // Admin
  seq_maintenance:       "/BMS/Admin/SEQ/SequenceSearch",
  party_group:           "/BMS/Admin/party_group_maintenance",
  tradelane:             "/BMS/Admin/tradelane_maintenance",
  user_maintenance:      "/BMS/Admin/user_maintenance",
  warehouse:             "/BMS/Admin/warehouse_maintenance",
  carrier:               "/BMS/Admin/carrier_maintenance",
  sys_notice:            "/BMS/Admin/sys_notice_maintenance",
  user_registration:     "/BMS/Admin/user_registration_maintenance",

  // EDI
  edi_recipients:    "/BMS/EDI/email_recipients/edisystem",
  oms_recipients:    "/BMS/EDI/email_recipients/oms",
  aldo_recipients:   "/BMS/EDI/email_recipients/omsaldo",
  steinel_recipients: "/BMS/EDI/email_recipients/omsse",
  schedule_email:    "/BMS/EDI/schedule_email",

  // Setting
  user_profile:     "/BMS/Setting/user_profile",
  change_password:  "/BMS/Setting/update_user_password",
  template_library: "/BMS/Setting/TMPLIB/TemplateSearch",
};


// ==================== DOM Helpers ====================

/**
 * Wait for an element matching the selector to appear in the DOM.
 * Uses MutationObserver — non-polling, React-friendly.
 * Returns the element or null if timeout.
 */
function waitForElement(selector, timeoutMs = 3000) {
  return new Promise((resolve) => {
    const el = document.querySelector(selector);
    if (el) return resolve(el);

    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        observer.disconnect();
        resolve(el);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    setTimeout(() => {
      observer.disconnect();
      resolve(document.querySelector(selector)); // last try
    }, timeoutMs);
  });
}

/**
 * Wait for React to finish rendering after an action.
 * Uses requestAnimationFrame for one tick — enough for React's sync renders.
 */
function waitForRender(timeoutMs = 1000) {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
    // Safety timeout
    setTimeout(resolve, timeoutMs);
  });
}


// ==================== Popup Detection & Handling ====================

/**
 * Robust visibility check for an element. Used instead of the antd
 * `ant-modal-wrap-hidden` class toggle, which is unreliable during open/close
 * transitions and in custom React roots (Modal.useModal / App.useApp). NEVER use
 * the jQuery-style `:visible` pseudo-selector with querySelectorAll — it throws.
 */
function isElementVisible(el) {
  if (!el) return false;
  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  if (el.getAttribute('aria-hidden') === 'true') return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

/**
 * Wait up to timeoutMs for a BLOCKING popup (modal/popconfirm) to appear.
 * BMS confirm dialogs (e.g. "Do you want to overwrite the following e-Booking
 * info by PRS info?") are produced by an async server round-trip and can mount
 * 1-3s AFTER the click. A single snapshot taken right after the click misses
 * them, so the LLM never learns a popup is blocking (M1). This polls + observes
 * DOM mutations until a blocking popup is present or the budget elapses.
 */
function waitForPopup(timeoutMs = 4000) {
  return new Promise((resolve) => {
    const hasBlocking = () => detectPopups().some(p => p.type === 'modal' || p.type === 'popconfirm');
    if (hasBlocking()) return resolve(true);
    const start = Date.now();
    let done = false;
    let poll;
    const finish = (val) => {
      if (done) return;
      done = true;
      observer.disconnect();
      clearInterval(poll);
      resolve(val);
    };
    const observer = new MutationObserver(() => { if (hasBlocking()) finish(true); });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'aria-hidden'] });
    poll = setInterval(() => {
      if (hasBlocking()) finish(true);
      else if (Date.now() - start >= timeoutMs) finish(false);
    }, 200);
  });
}

/**
 * Wait up to timeoutMs for a visible confirm-style modal and return its
 * ELEMENT (or null). Used by the save chain drainer, which must answer every
 * modal in sequence — Warning → Yes, send-email → No — before the commit.
 */
function waitForPopupEl(timeoutMs = 8000) {
  return new Promise((resolve) => {
    const start = Date.now();
    const poll = setInterval(() => {
      const wraps = [...document.querySelectorAll('.ant-modal-wrap')]
        .map(wrap => wrap.querySelector('.ant-modal'))
        .filter(m => m && m.getBoundingClientRect().width > 0);
      if (wraps.length > 0) { clearInterval(poll); resolve(wraps[0]); }
      else if (Date.now() - start >= timeoutMs) { clearInterval(poll); resolve(null); }
    }, 250);
  });
}

/**
 * Detect all visible popups on the page.
 * Returns array of { type, title, body, buttons } for each popup.
 * Types: 'modal', 'popconfirm', 'notification', 'toast'
 */
/**
 * Return the action-button container of an antd modal. Modal.confirm() (the
 * imperative API used for "Warning"/"Do you want to overwrite..." prompts)
 * renders its OK/Cancel buttons in .ant-modal-confirm-btns, whereas the <Modal>
 * component renders them in .ant-modal-footer. Tools must check both or
 * Modal.confirm popups look like they have no buttons and can never be clicked.
 */
function modalFooterContainer(modal) {
  return modal.querySelector('.ant-modal-confirm-btns') || modal.querySelector('.ant-modal-footer');
}

function detectPopups() {
  const popups = [];

  // 1. Ant Design Modal dialogs (Modal.confirm, Modal.warning, Modal.info, <Modal>)
  //    Drive visibility off the INNER .ant-modal (isElementVisible), NOT the
  //    .ant-modal-wrap-hidden class toggle, so modals mid-transition or in custom
  //    React roots are detected. Title/body read from multiple antd slot variants.
  for (const wrap of document.querySelectorAll('.ant-modal-wrap')) {
    const modal = wrap.querySelector('.ant-modal');
    if (!modal || !isElementVisible(modal)) continue;

    const title = (modal.querySelector('.ant-modal-title, .ant-modal-header-title, [class*="confirm-title"]')?.textContent || '').trim();
    // 800 chars (2026-10-13): the print-draft confirm's decisive line ("Are you
    // sure to print draft?") sits ~230 chars in, AFTER the SO#/HBL# and party
    // block — the old 300-char cap cut it off exactly when the parties were
    // long, and the docs flow's modal parse then FlowFailed into recovery
    // roulette instead of running its deterministic toggle step.
    const body = (modal.querySelector('.ant-modal-body, [class*="confirm-content"]')?.textContent || '').trim().substring(0, 800);
    const footer = modalFooterContainer(modal);
    const buttons = footer
      ? Array.from(footer.querySelectorAll('button')).map(b => b.textContent.trim()).filter(Boolean)
      : [];

    popups.push({ type: 'modal', title, body, buttons });
  }

  // 1b. Drawer-based confirmations (antd 5 pattern). Only treat as a blocking
  //     modal when its footer has OK/Cancel-style buttons, so the edit-side
  //     drawer (no such footer) is never misreported. Classified as type 'modal'
  //     so formatBlockingPopups/handlePopupConfirm handle it uniformly.
  for (const drawer of document.querySelectorAll('.ant-drawer')) {
    const content = drawer.querySelector('.ant-drawer-content');
    if (!content || !isElementVisible(content)) continue;
    const footer = drawer.querySelector('.ant-drawer-footer');
    const btnText = footer ? Array.from(footer.querySelectorAll('button')).map(b => b.textContent.trim()) : [];
    const isConfirm = btnText.some(t => /\b(ok|cancel|yes|no|confirm|submit)\b/i.test(t));
    if (!isConfirm) continue;
    const title = (drawer.querySelector('.ant-drawer-title, .ant-drawer-header-title')?.textContent || '').trim();
    const body = (drawer.querySelector('.ant-drawer-body')?.textContent || '').trim().substring(0, 800);
    popups.push({ type: 'modal', title, body, buttons: btnText });
  }

  // 2. Ant Design Popconfirm (small confirmation popovers)
  const popovers = document.querySelectorAll('.ant-popover:not(.ant-popover-hidden)');
  for (const popover of popovers) {
    const popconfirm = popover.querySelector('.ant-popconfirm');
    if (!popconfirm) continue;

    const titleEl = popconfirm.querySelector('.ant-popconfirm-title-text, .ant-popconfirm-message-text');
    const title = (titleEl?.textContent || '').trim();
    const buttonsContainer = popconfirm.querySelector('.ant-popconfirm-buttons');
    const buttons = buttonsContainer
      ? Array.from(buttonsContainer.querySelectorAll('button')).map(b => b.textContent.trim()).filter(Boolean)
      : [];

    popups.push({ type: 'popconfirm', title, body: '', buttons });
  }

  // 3. Notification banners and alerts (non-blocking, auto-dismiss targets)
  const notifications = document.querySelectorAll('.ant-notification-notice');
  for (const notice of notifications) {
    const msg = (notice.querySelector('.ant-notification-notice-message')?.textContent || '').trim();
    const desc = (notice.querySelector('.ant-notification-notice-description')?.textContent || '').trim();
    popups.push({ type: 'notification', title: msg, body: desc.substring(0, 200), buttons: [] });
  }

  const alerts = document.querySelectorAll('.ant-alert');
  for (const alertEl of alerts) {
    const msg = (alertEl.querySelector('.ant-alert-message')?.textContent || '').trim();
    const desc = (alertEl.querySelector('.ant-alert-description')?.textContent || '').trim();
    popups.push({ type: 'notification', title: msg, body: desc.substring(0, 200), buttons: [] });
  }

  // 4. Toast messages (antd Message)
  const toasts = document.querySelectorAll('.ant-message-notice');
  for (const toast of toasts) {
    const text = (toast.textContent || '').trim();
    popups.push({ type: 'toast', title: text, body: '', buttons: [] });
  }

  return popups;
}

/**
 * Format detected popups into a human-readable string for inclusion in tool results.
 * Only includes BLOCKING popups (modals, popconfirms) — toasts and notifications are
 * auto-dismissed silently.
 */
function formatBlockingPopups(popups) {
  const blocking = popups.filter(p => p.type === 'modal' || p.type === 'popconfirm');
  if (blocking.length === 0) return '';

  const lines = blocking.map((p, i) => {
    const label = p.type === 'modal' ? 'Modal' : 'Popconfirm';
    const titlePart = p.title ? `"${p.title}"` : '(no title)';
    const bodyPart = p.body ? ` — ${p.body.substring(0, 600)}` : '';
    const btnsPart = p.buttons.length > 0 ? ` [Buttons: ${p.buttons.join(', ')}]` : '';
    return `${i + 1}. [${label}] ${titlePart}${bodyPart}${btnsPart}`;
  });

  return `\n\n⚠️ ACTIVE POPUPS:\n${lines.join('\n')}`;
}

/**
 * Silently dismiss non-critical popups (notifications, alerts, toasts).
 * Does NOT touch modals or popconfirms — those need explicit LLM action.
 */
function autoDismissNonCriticalPopups() {
  // Close notification banners
  document.querySelectorAll('.ant-notification-notice-close').forEach(btn => {
    try { btn.click(); } catch (e) { /* ignore */ }
  });

  // Close alert banners (that have a close button)
  document.querySelectorAll('.ant-alert-close-icon, .ant-alert-close-text').forEach(btn => {
    try { btn.click(); } catch (e) { /* ignore */ }
  });

  // Toast messages auto-dismiss; no action needed
}


// ==================== Post-Navigation Search Helpers ====================

/**
 * Wait for data to load in the table after clicking Search.
 * Detects data by watching for table rows to appear or an empty-state placeholder.
 */
function waitForDataLoad(mainElement, timeoutMs = 8000) {
  return new Promise((resolve) => {
    const startTime = Date.now();

    const checkData = () => {
      const rows = mainElement.querySelectorAll(
        '.ant-table-tbody tr:not(.ant-table-placeholder)'
      );
      if (rows.length > 0) return true;
      const emptyText = mainElement.querySelector('.ant-empty-description, .ant-table-placeholder');
      if (emptyText) return true;
      return false;
    };

    // If data is already present, resolve quickly
    if (checkData()) {
      setTimeout(resolve, 500);
      return;
    }

    const observer = new MutationObserver(() => {
      if (checkData()) {
        observer.disconnect();
        setTimeout(resolve, 300);
      }
    });
    observer.observe(mainElement, { childList: true, subtree: true });

    // Poll as fallback — MutationObserver can miss some React updates
    const poll = setInterval(() => {
      if (checkData()) {
        clearInterval(poll);
        observer.disconnect();
        setTimeout(resolve, 300);
      } else if (Date.now() - startTime > timeoutMs) {
        clearInterval(poll);
        observer.disconnect();
        resolve();
      }
    }, 500);

    // Safety: clean up on timeout
    setTimeout(() => {
      clearInterval(poll);
      observer.disconnect();
      resolve();
    }, timeoutMs);
  });
}

/**
 * Trigger a BMS list page's data load and wait for the rows to appear.
 *
 * List pages (SearchBookingEnhance, SearchConsolidationEnhance, ...) only load
 * rows when antd Input.Search fires onSearch -> handleOnApplyFilter -> getData.
 * A synthetic element.click() on the search button does NOT reliably reach that
 * onSearch handler, so the grid stays empty and the agent loops retrying
 * different terms for minutes (root cause of the "show me a booking" failure:
 * the table is empty *until* Search truly fires, and a programmatic click never
 * fires it). Verified fix: walk the React 16 fiber from the search button up to
 * the list component class instance (e.g. BookingEnquiry) and call its getData()
 * directly — the same code path a real user click runs. Falls back to a click
 * for pages without a getData instance.
 *
 * Returns true if a search was triggered (and waited for), false otherwise.
 */
async function triggerAntdSearchAndLoad(mainElement, timeoutMs = 10000) {
  const main = mainElement || document.querySelector('main');
  if (!main) return false;
  const searchBtn = main.querySelector('.ant-input-search-button');

  // Preferred path: call the component's getData() directly via the React fiber.
  try {
    if (searchBtn) {
      const fiberKey = Object.keys(searchBtn).find(
        (k) => k.indexOf('__reactInternalInstance$') === 0
      );
      let fiber = fiberKey ? searchBtn[fiberKey] : null;
      let inst = null;
      let depth = 0;
      while (fiber && depth < 60) {
        if (fiber.stateNode && typeof fiber.stateNode.getData === 'function') {
          inst = fiber.stateNode;
          break;
        }
        fiber = fiber.return;
        depth += 1;
      }
      if (inst && typeof inst.getData === 'function') {
        console.log('[BMS Tools] Triggered list getData() via React fiber (depth ' + depth + ', ' + (inst.constructor && inst.constructor.name) + ')');
        try {
          inst.getData(function () {});
        } catch (e) {
          inst.getData();
        }
        await waitForDataLoad(main, timeoutMs);
        return true;
      }
    }
  } catch (e) {
    console.warn('[BMS Tools] fiber getData failed, falling back to click:', e.message);
  }

  // Fallback: synthetic click (for pages where onSearch fires from a click, or
  // where there is no getData instance).
  if (searchBtn && !searchBtn.disabled && !searchBtn.hasAttribute('disabled')) {
    searchBtn.click();
    await waitForDataLoad(main, timeoutMs);
    return true;
  }
  return false;
}

/**
 * After navigating to a list page, wait for the Search button to become
 * enabled (antd Input.Search enterButton), then trigger the data load.
 *
 * Why this is needed: Booking list pages (SearchBookingEnhance,
 * SearchConsolidationEnhance, etc.) do NOT auto-load data. The page's
 * componentDidMount sets searchBoxDisabled=true and only sets it to false
 * after async init (getCompsFilterSetting, getUserPreferenceList, etc.).
 * The data only loads when the Search button is clicked, which triggers
 * handleOnApplyFilter -> getData.
 *
 * Returns true if Search was triggered, false otherwise (e.g. no Search button).
 */
async function waitForSearchButtonAndClick(maxWaitMs = 10000) {
  const startTime = Date.now();
  const checkInterval = 500;

  while (Date.now() - startTime < maxWaitMs) {
    const main = document.querySelector('main');
    if (!main) {
      await new Promise(r => setTimeout(r, checkInterval));
      continue;
    }

    const searchBtn = main.querySelector('.ant-input-search-button');
    if (!searchBtn) {
      // Page might still be rendering — keep polling.
      // After maxWaitMs, we'll do a final check below to determine
      // if this is truly a non-list page (no Search button ever appears).
      await new Promise(r => setTimeout(r, checkInterval));
      continue;
    }

    // antd Input.Search disables via the disabled prop on the wrapper.
    // Check both the wrapper class and the button's own disabled attribute.
    const inputSearchWrapper = searchBtn.closest('.ant-input-search');
    const wrapperDisabled = inputSearchWrapper && (
      inputSearchWrapper.classList.contains('ant-input-search-disabled') ||
      inputSearchWrapper.querySelector('.ant-input-disabled')
    );

    if (!wrapperDisabled && !searchBtn.disabled && !searchBtn.hasAttribute('disabled')) {
      // Button is ready — trigger the list data load (getData via fiber, click fallback)
      console.log('[BMS Tools] Search button ready, triggering list data load');
      await triggerAntdSearchAndLoad(main, 10000);
      return true;
    }

    // Button exists but is still disabled — wait and retry
    await new Promise(r => setTimeout(r, checkInterval));
  }

  // Timed out waiting for the button to become enabled.
  // Try one last time anyway — maybe it just became ready.
  const main = document.querySelector('main');
  const searchBtn = main && main.querySelector('.ant-input-search-button');
  if (searchBtn && !searchBtn.disabled) {
    await triggerAntdSearchAndLoad(main, 10000);
    return true;
  }

  console.log('[BMS Tools] Timed out waiting for Search button to become enabled');
  return false;
}

// ==================== Antd Form Helpers ====================

/**
 * Detect the type of form control inside an antd FormItem.
 */
function detectFieldType(formItem) {
  if (!formItem) return 'unknown';
  // Shadowing widgets FIRST — they nest inside the generic classes below, so they must
  // be checked before .ant-select / .ant-picker / .ant-checkbox / <input>:
  if (formItem.querySelector('.ant-upload')) return 'upload';
  if (formItem.querySelector('.ant-cascader-picker') || formItem.querySelector('.ant-cascader')) return 'cascader';
  if (formItem.querySelector('.ant-select-tree')) return 'treeselect';
  if (formItem.querySelector('.ant-picker-range')) return 'rangepicker';
  if (formItem.querySelector('.ant-segmented')) return 'segmented';
  if (formItem.querySelector('.ant-rate')) return 'rate';
  if (formItem.querySelector('.ant-slider')) return 'slider';
  if (formItem.querySelector('.ant-transfer')) return 'transfer';
  if (formItem.querySelector('.ant-checkbox-group')) return 'checkboxgroup';
  // Generic controls:
  if (formItem.querySelector('.ant-select')) return 'select';
  if (formItem.querySelector('.ant-picker')) return 'datepicker';
  if (formItem.querySelector('.ant-checkbox')) return 'checkbox';
  if (formItem.querySelector('.ant-switch')) return 'switch';
  if (formItem.querySelector('.ant-radio-group')) return 'radio';
  if (formItem.querySelector('.ant-input-number')) return 'inputnumber';
  if (formItem.querySelector('textarea')) return 'textarea';
  if (formItem.querySelector('input')) return 'input';
  return 'unknown';
}

/**
 * Find form fields by matching antd FormItem label text.
 * Returns array of { formItem, control, label, type }.
 */
function findFormFieldsByLabel(labelText) {
  const results = [];
  if (!labelText) return results;

  const normalized = labelText.toLowerCase().trim();
  const formItems = document.querySelectorAll('.ant-form-item');

  for (const formItem of formItems) {
    const labelEl = formItem.querySelector('.ant-form-item-label label');
    if (!labelEl) continue;

    const label = labelEl.textContent.trim();
    if (!label.toLowerCase().includes(normalized)) continue;

    const controlContainer = formItem.querySelector('.ant-form-item-control');
    if (!controlContainer) continue;

    const type = detectFieldType(formItem);

    let control;
    if (type === 'select') control = formItem.querySelector('.ant-select');
    else if (type === 'datepicker') control = formItem.querySelector('.ant-picker');
    else if (type === 'checkbox') control = formItem.querySelector('.ant-checkbox-wrapper') || formItem.querySelector('.ant-checkbox');
    else if (type === 'switch') control = formItem.querySelector('.ant-switch');
    else if (type === 'radio') control = formItem.querySelector('.ant-radio-group');
    else if (type === 'inputnumber') control = formItem.querySelector('.ant-input-number');
    else if (type === 'textarea') control = formItem.querySelector('textarea');
    else if (type === 'input') control = formItem.querySelector('input');
    else control = controlContainer;

    results.push({ formItem, control, label, type });
  }

  return results;
}

/**
 * Disambiguate a multi-match field lookup. antd Modals portal to the END of
 * document.body, so findFormFieldsByLabel()[0] returns the PAGE's copy first -
 * a label like "POL" inside a modal was shadowed by the read-only (disabled)
 * POL of the Confirm Booking tab behind it, and the modal fill silently
 * "skipped (disabled)" every field (2026-10-05 manual-sailing failure). When a
 * modal is open, prefer matches inside it; then prefer visible; then prefer
 * ENABLED controls - a disabled same-label field must never shadow an enabled
 * one.
 */
function preferUsableFields(found) {
  if (!found || found.length < 2) return found;
  const openModal = Array.from(document.querySelectorAll('.ant-modal-wrap'))
    .some(w => getComputedStyle(w).display !== 'none');
  if (openModal) {
    const inModal = found.filter(f => f.formItem && f.formItem.closest('.ant-modal-wrap'));
    if (inModal.length) found = inModal;
  }
  const visible = found.filter(f => f.formItem && f.formItem.offsetParent !== null);
  if (visible.length) found = visible;
  const enabled = found.filter(f => f.formItem && !f.formItem.querySelector(
    '[disabled], .ant-input-disabled, .ant-select-disabled, .ant-picker-disabled, .ant-input-number-disabled'));
  if (enabled.length) found = enabled;
  return found;
}

function findFormFieldsByName(fieldName) {
  const results = [];
  if (!fieldName) return results;

  const inputs = document.querySelectorAll(
    `input[name="${fieldName}"], input[id*="${fieldName}"], textarea[name="${fieldName}"], select[name="${fieldName}"]`
  );

  for (const input of inputs) {
    const formItem = input.closest('.ant-form-item');
    if (!formItem) continue;

    const labelEl = formItem.querySelector('.ant-form-item-label label');
    const label = labelEl ? labelEl.textContent.trim() : '';
    const type = detectFieldType(formItem);
    const controlContainer = formItem.querySelector('.ant-form-item-control');

    results.push({ formItem, control: input, label, type });
  }

  return results;
}

/**
 * Set value on a native input and trigger React synthetic events.
 * Works for input, textarea, and InputNumber inner inputs.
 */
function setReactInputValue(element, value) {
  if (!element) return false;

  element.focus();
  element.setSelectionRange(0, element.value.length);

  const ok = document.execCommand('insertText', false, value);
  if (!ok) {
    // Fallback for browsers/contexts where execCommand is disabled
    const tagName = element.tagName.toLowerCase();
    const prototype = tagName === 'textarea'
      ? window.HTMLTextAreaElement.prototype
      : window.HTMLInputElement.prototype;
    const nativeSetter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;
    if (nativeSetter) {
      nativeSetter.call(element, value);
    } else {
      element.value = value;
    }
    element.dispatchEvent(new Event('input', { bubbles: true }));
    element.dispatchEvent(new Event('change', { bubbles: true }));
  }
  return true;
}

/**
 * Select an option in an antd Select dropdown.
 * Handles both simple and searchable selects.
 */
function realWait(ms) {
  return new Promise(r => setTimeout(r, ms));
}

// Poll a predicate every `step` ms up to `budget` ms. Same checks as the flat
// sleeps it replaces, noticed the moment they hold instead of after the full
// sleep (speed pass 2026-09-25).
async function pollUntil(predicate, budget, step = 300) {
  const deadline = Date.now() + budget;
  while (Date.now() < deadline) {
    if (predicate()) return true;
    await realWait(step);
  }
  return predicate();
}

// Network settle gate (truth channel, 2026-10-12): wait until page XHR traffic
// has been quiet for `quietMs` AND the optional predicate holds. Always
// resolves within `timeout` — a busy background poller can never block a tool
// run; callers surface settled:false in the result text instead. Uses the
// armed-window in-flight counter (__bmsInflight), so it only judges traffic
// caused inside chatbot tool runs, never the user's manual page activity.
async function waitForQuiet({ timeout = 8000, quietMs = 400, predicate = null } = {}) {
  const start = Date.now();
  let lastBusy = start;
  for (;;) {
    const elapsed = Date.now() - start;
    if (elapsed >= timeout) {
      return { settled: false, waitedMs: Date.now() - start };
    }
    const busy = (window.__bmsInflight || 0) > 0
      || (typeof predicate === 'function' && !predicate());
    if (busy) {
      lastBusy = Date.now();
    } else if (Date.now() - lastBusy >= quietMs) {
      return { settled: true, waitedMs: Date.now() - start };
    }
    await realWait(150);
  }
}

async function triggerAntdSelectChange(selectElement, targetValue, fieldLabel) {
  if (!selectElement) return false;

  const selector = selectElement.querySelector('.ant-select-selector');
  if (!selector) return false;

  // Defer outside any React render cycle
  await realWait(50);

  // Click to open dropdown
  selector.click();

  // Brief wait for dropdown to render
  await realWait(300);

  // Check if it's a searchable select
  const searchInput = selectElement.querySelector('.ant-select-selection-search-input');

  if (searchInput) {
    // Type the value to filter options
    searchInput.focus();
    setReactInputValue(searchInput, targetValue);

    // Also trigger React Fiber's onChange/onInput to ensure antd's onSearch fires.
    // Native events alone may not reach antd's internal handler for API-backed selects.
    const fiberKey = Object.keys(searchInput).find(k => k.startsWith('__reactFiber$') || k.startsWith('__reactInternalInstance$'));
    if (fiberKey) {
      let fiber = searchInput[fiberKey];
      for (let depth = 0; depth < 10 && fiber; depth++) {
        const props = fiber.memoizedProps || fiber.pendingProps;
        if (props) {
          if (props.onChange) {
            props.onChange({ target: searchInput, currentTarget: searchInput });
            break;
          }
          if (props.onInput) {
            props.onInput({ target: searchInput, currentTarget: searchInput });
            break;
          }
        }
        fiber = fiber.return;
      }
    }

    // Wait for API-backed search to respond and update options.
    await realWait(1500);
  } else {
    // No search input — try typing into the selector itself to trigger antd's built-in filter.
    // Some antd Select components filter options by typed text even without a visible search input.
    selector.focus();
    setReactInputValue(selector, targetValue);
    await realWait(800);
  }

  // Wait for an actual selectable option using MutationObserver (adaptive timing)
  const dropdown = await waitForElement(
    '.ant-select-dropdown:not(.ant-select-dropdown-hidden) .ant-select-item-option',
    5000
  );

  if (!dropdown) {
    // Check dropdown state AFTER timeout for diagnostics
    const postCheckDropdowns = document.querySelectorAll('.ant-select-dropdown:not(.ant-select-dropdown-hidden)');
    const postCheckEmpty = [];
    for (const dd of postCheckDropdowns) {
      const emptyEl = dd.querySelector('.ant-select-item-empty, .ant-empty-description');
      postCheckEmpty.push(emptyEl ? emptyEl.textContent : 'no-empty-msg');
    }
    console.log(`[triggerAntdSelectChange] FAIL: no option found. Visible dropdowns: ${postCheckDropdowns.length}, empty states: [${postCheckEmpty.join(', ')}]`);
    return false;
  }

  console.log(`[triggerAntdSelectChange] option found, proceeding to match`);

  // Retry matching: options may be stale initially while API is still fetching.
  const MATCH_RETRIES = 3;
  const MATCH_RETRY_DELAY = 1000;
  const target = targetValue.toLowerCase();
  // Identity-bearing words of the target: normalized alphanumeric tokens (so
  // "FORWARDING (HK)" target "FORWARDING HK" still matches), corporate suffixes
  // ("LTD"/"LIMITED"/"GMBH"...) dropped because the stored display name often
  // spells them differently ("LTD." vs "LIMITED"). A 1-word target (e.g. the
  // party code "LSINOPASS") keeps that single token.
  const _matchStopwords = new Set(['ltd', 'limited', 'co', 'corp', 'inc', 'company', 'gmbh', 'ag', 'sa', 'bv', 'nv', 'plc', 'llc', 'pte', 'pty', 'the', 'and', 'of', 'for']);
  const targetWords = target
    .split(/[^a-z0-9]+/)
    .filter(w => w.length >= 2 && !_matchStopwords.has(w));

  // A display text matches when the whole target is a substring (fast path) or
  // EVERY significant target word appears in it. Word-level matching rescues
  // punctuation/formatting variants between the searched value and the rendered
  // option ("FORWARDING HK" vs "FORWARDING (HK)") that the strict substring
  // check rejects — the dropdown search itself already returned these rows for
  // that value, so they are the results the user asked for.
  const displayMatchesTarget = (displayText) => {
    if (displayText.includes(target)) return true;
    if (targetWords.length === 0) return false;
    return targetWords.every(w => displayText.includes(w));
  };

  const isEmptyStateOption = (option) => {
    const t = (option.textContent || '').trim().toLowerCase();
    return option.querySelector('.ant-select-item-empty') !== null
      || /no record found|not found|no data/.test(t);
  };

  for (let attempt = 0; attempt < MATCH_RETRIES; attempt++) {
    // Re-query dropdown and options each attempt (DOM may have updated)
    const dropdowns = document.querySelectorAll('.ant-select-dropdown:not(.ant-select-dropdown-hidden)');
    const matches = [];
    let realOptionCount = 0;
    let emptyStateCount = 0;

    for (const dd of dropdowns) {
      const options = dd.querySelectorAll('.ant-select-item-option');

      for (const option of options) {
        if (isEmptyStateOption(option)) {
          emptyStateCount += 1;
          continue;
        }
        realOptionCount += 1;
        const optionText = (option.textContent || '').trim().toLowerCase();
        const optionTitle = (option.getAttribute('title') || '').trim().toLowerCase();
        const displayText = optionText || optionTitle;

        // Skip if no match at all
        if (!displayMatchesTarget(displayText)) {
          continue;
        }

        // Determine priority level
        let priority = 0;

        // Priority 1: Exact match (highest)
        if (displayText === target) {
          priority = 3;
        }
        // Priority 2: Starts with target AND target appears as a complete word/phrase
        // This distinguishes "DUMMY DATA - TESTING AGENT" from containing "CONSIGNEE"
        else if (displayText.startsWith(target) ||
                 displayText.startsWith(target + ' ') ||
                 displayText.startsWith(target + '(')) {
          // Additional check: if target contains distinguishing words (like "AGENT" vs "CONSIGNEE"),
          // prefer options where those distinguishing words also appear
          const targetWords = target.split(/\s+/).filter(w => w.length > 2); // Only meaningful words (>2 chars)
          let wordBonus = 0;
          for (const word of targetWords) {
            if (displayText.includes(word)) {
              wordBonus += 1;
            }
          }
          priority = 2 + (wordBonus * 0.1);
        }
        // Priority 3: Contains target anywhere
        else {
          // For contains matches, prefer options where more of the target words appear
          const targetWords = target.split(/\s+/).filter(w => w.length > 0);
          let matchCount = 0;
          for (const word of targetWords) {
            if (displayText.includes(word)) {
              matchCount += 1;
            }
          }
          // Only consider if at least 50% of words match
          if (matchCount / targetWords.length >= 0.5) {
            priority = 1 + (matchCount / targetWords.length);
          }
        }

        // Field label context: boost options whose text contains words from the field label
        // e.g. "Shipper" field → prefer "DUMMY DATA - TESTING SHIPPER" over "DUMMY DATA - TESTING CONSIGNEE"
        if (fieldLabel && priority > 0) {
          const labelLower = fieldLabel.toLowerCase();
          const labelWords = labelLower.split(/[\s\-_/]+/).filter(w => w.length > 2);
          for (const word of labelWords) {
            if (displayText.includes(word)) {
              priority += 0.15;
            }
          }
        }

        if (priority > 0) {
          matches.push({ option, priority, displayText });
        }
      }
    }

    // Sort by priority (descending). When priorities are equal, maintain document order (first match wins).
    matches.sort((a, b) => b.priority - a.priority);

    if (matches.length > 0) {
      matches[0].option.click();
      await realWait(200);
      // Return the CLICKED option's display text (truthy) — callers verify
      // the rendered selection against THIS, not against the typed query
      // (see selectOption's stick guard).
      return matches[0].displayText;
    }

    // The search returned ONLY an empty state ("No record found") — retrying
    // cannot conjure rows the API did not return; fail fast with the honest
    // reason instead of burning the retry budget.
          if (attempt < MATCH_RETRIES - 1) {
        try {
          const input = selectElement.querySelector('.ant-select-selection-search-input');
          if (input) {
            input.focus();
            input.select();
            document.execCommand('insertText', false, targetValue);
          }
        } catch (err) { /* best effort re-fire */ }
      }

    // No text match, but the search returned EXACTLY ONE real option: the API
    // answered the query with a single party/row whose rendered label may spell
    // parts differently than the searched value. Selecting it mirrors what a
    // human does (type the code/name, click the one hit). Never triggered when
    // several rows are visible — ambiguity stays a mismatch.
    if (realOptionCount === 1) {
      const onlyOption = document.querySelector(
        '.ant-select-dropdown:not(.ant-select-dropdown-hidden) .ant-select-item-option');
      if (onlyOption && !isEmptyStateOption(onlyOption)) {
        console.log(`[triggerAntdSelectChange] single search result accepted for "${targetValue}"`);
        onlyOption.click();
        await realWait(200);
        return (onlyOption.textContent || '').trim().toLowerCase();
      }
    }

    // No match yet — options may still be loading from the API
    if (attempt < MATCH_RETRIES - 1) {
      const visibleCount = document.querySelectorAll('.ant-select-dropdown:not(.ant-select-dropdown-hidden) .ant-select-item-option').length;
      console.log(`[triggerAntdSelectChange] No match on attempt ${attempt + 1}/${MATCH_RETRIES} (${visibleCount} options visible), retrying in ${MATCH_RETRY_DELAY}ms...`);
      await realWait(MATCH_RETRY_DELAY);
    }
  }

  console.log(`[triggerAntdSelectChange] No matching option found after ${MATCH_RETRIES} attempts for "${targetValue}"`);
  return false;
}

// ==================== Tool Functions ====================

/**
 * Navigate to a page using React Router's history.push().
 * This triggers SPA navigation — no full page reload.
 */
async function navigateToPage({ page, so_number }) {
  if (so_number) {
    const nav = safePush(`/BMS/enhance_booking/search_booking/shipping_order/${so_number}`);
    if (!nav.confirmed) return _navCanceledResult(nav.message);
  } else {
    const route = ROUTE_MAP[page];
    if (!route) {
      return { content: [{ type: "text", text: `Unknown page: ${page}. Available: ${Object.keys(ROUTE_MAP).join(', ')}` }] };
    }
    const nav = safePush(route);
    if (!nav.confirmed) return _navCanceledResult(nav.message);
    // Fresh-create bookkeeping (dup-create guard in saveAndConfirm): arriving
    // at the create form by NAVIGATION starts a new form session — clear the
    // last-created-SO stamp so a legitimate back-to-back create is never
    // refused (only a re-executed Save WITHOUT a re-navigation is a dup).
    if (/shipping_order\/new(\?|#|$)/.test(route)) {
      window.__lastSaveSoNo = '';
      window.__lastSaveSoNoAt = 0;
    }
  }

  // Wait for SPA route change + React render
  await waitForRender(2000);

  // Auto-click Search button on LIST pages after the page is fully initialized.
  // This handles booking/consolidation/party list pages where data does not
  // auto-load — the user must click Search to trigger getData(). Create/edit
  // pages have no Search button, and waiting for one cost 100+ seconds of
  // dead time per navigation before the flow could continue.
  const SEARCH_ON_PAGES = new Set([
    'booking_dashboard', 'search_booking', 'search_party', 'party_dashboard',
    'party_pending_approval', 'party_pending_mgmt_approval', 'mgmt_approved',
    'party_approved', 'party_sunac_pending', 'party_sunac_handled',
    'party_sunac_rejected', 'party_cam_pending', 'party_cam_handled',
    'party_cam_rejected', 'party_rejected', 'mgmt_rejected',
    'search_customer_group_code', 'consolidation_dashboard', 'search_consolidation',
  ]);
  const pageKey = typeof page === 'string' ? page : '';
  const searchClicked = SEARCH_ON_PAGES.has(pageKey)
    ? await waitForSearchButtonAndClick(10000)
    : false;

  const url = window.location.pathname;
  const message = searchClicked
    ? `Navigated to ${url} (auto-searched)${_navAutoLeftNote()}`
    : `Navigated to ${url}${_navAutoLeftNote()}`;
  return { content: [{ type: "text", text: message }] };
}


/**
 * Read the currently visible page content.
 * Captures URL, breadcrumb, and main content area.
 */
async function readCurrentPage() {
  const url = window.location.pathname;
  const main = document.querySelector('main');

  // Safety net: if the page shows "Please either apply... click 'Search'"
  // it means data hasn't been loaded yet. Click Search and wait for data.
  if (main) {
    const pageText = main.innerText || '';
    if (pageText.includes("click 'Search'") || pageText.includes("click \"Search\"") ||
        pageText.includes("Please either apply")) {
      console.log('[BMS Tools] readCurrentPage: page needs Search click, clicking now...');
      const searchBtn = main.querySelector('.ant-input-search-button');
      if (searchBtn) {
        searchBtn.click();
        // Wait for data to load
        await new Promise(r => setTimeout(r, 5000));
      }
    }
  }

  // Auto-dismiss non-critical popups silently before reading
  autoDismissNonCriticalPopups();

  // Try <main> first (standard layout), fallback to body
  const content = main
    ? main.innerText?.substring(0, 8000) || ''
    : document.body.innerText?.substring(0, 8000) || '';

  // Detect any remaining blocking popups and include in result
  const popups = detectPopups();
  const popupInfo = formatBlockingPopups(popups);

  return {
    content: [{ type: "text", text: `URL: ${url}\n\n${content}${popupInfo}` }]
  };
}


/**
 * Click a button by its text content or aria-label.
 */

// State-changing buttons whose click outcome must be VERIFIED before reporting.
// In BMS, SO approval is submitted via the form "Save" button (there is NO
// "Approve" button), so "Save"/"Submit" are the real approval trigger words.
export const ACTION_VERIFY_BUTTONS = /^(save|submit|approve|approved|reject|rejected|confirm|delete|remove|ok|yes|release|send|publish|approve all|reject all|save & send|save & close)$/i;
// NOTE: "apply" was removed - the only BMS "Apply" button is the filter-bar Apply (a
// READ), so gating it caused a false Accept prompt on "show me bookings filtered by X".

/**
 * After a state-changing click, capture the real outcome (success toast / error
 * toast / persistent error notification / confirm modal) so the tool result
 * carries EVIDENCE instead of the blind "Clicked button: X" (M5: false success).
 * Only consumes toasts/notifications that appeared AFTER the click (pre-count +
 * dataset flag), distinguishing success vs error by antd icon classes. Modals
 * are detected via detectPopups() (broadened, M1). Runs BEFORE auto-dismiss so
 * the signal is not destroyed before the LLM sees it.
 */
async function captureActionResult(buttonName, timeoutMs = 6000) {
  const q = (s) => Array.from(document.querySelectorAll(s));
  const beforeNotif = q('.ant-notification-notice').length;
  const beforeToast = q('.ant-message-notice').length;
  const start = Date.now();
  const fresh = (el) => el && !el.dataset.__bmsCaptured;
  const isErrorIcon = (el) => el.querySelector('.anticon-close-circle, .anticon-exclamation-circle') || el.matches('.ant-message-error, .ant-notification-notice-error');
  const isSuccessIcon = (el) => el.querySelector('.anticon-check-circle, .anticon-check') || el.matches('.ant-message-success, .ant-notification-notice-success');

  while (Date.now() - start < timeoutMs) {
    await realWait(150);

    // (1) Persistent error notification (bottomRight, duration:0) — BMS's primary
    //     approval-validation failure signal (missing sailing schedule/rate, etc.).
    let newNotif = q('.ant-notification-notice').slice(beforeNotif).find(fresh);
    if (!newNotif) {
      newNotif = q('.ant-notification-notice').find(n => isErrorIcon(n) && fresh(n));
    }
    if (newNotif) {
      newNotif.dataset.__bmsCaptured = '1';
      return { outcome: 'error', evidence: (newNotif.textContent || '').trim().substring(0, 240) };
    }

    // (2) Error toast.
    const errToast = q('.ant-message-notice').find(t => (isErrorIcon(t)) && fresh(t));
    if (errToast) {
      errToast.dataset.__bmsCaptured = '1';
      return { outcome: 'error', evidence: (errToast.textContent || '').trim() };
    }

    // (3) Success toast.
    let okToast = q('.ant-message-notice').slice(beforeToast).find(fresh);
    if (!okToast) {
      okToast = q('.ant-message-notice').find(t => isSuccessIcon(t) && fresh(t));
    }
    if (okToast) {
      okToast.dataset.__bmsCaptured = '1';
      return { outcome: 'success', evidence: (okToast.textContent || '').trim() };
    }

    // (4) A confirm/warning modal (do NOT auto-click it — the LLM decides).
    const blocking = detectPopups().filter(p => p.type === 'modal' || p.type === 'popconfirm');
    if (blocking.length > 0) {
      const m = blocking[0];
      const ev = `"${m.title || '(no title)'}"${m.body ? ' - ' + m.body : ''}${m.buttons.length ? ' [Buttons: ' + m.buttons.join(', ') + ']' : ''}`;
      return { outcome: 'confirm_popup', evidence: ev };
    }
  }
  return { outcome: 'unknown', evidence: 'no success/error message or popup detected within timeout' };
}

async function clickButton({ button_name }) {
  // A modal button beats everything while its modal is open: modals portal to
  // the END of document.body, so DOM order puts page buttons first, and the
  // sailing modal's "Search" lost to the SO list page's Search behind it —
  // the click fired the list reload while the modal sat frozen with no toast,
  // no spinner and no results (2026-10-05 live finding, probe-proven: the
  // in-modal click renders result cards within ~1.5s).
  const inOpenModal = (el) => {
    const w = el.closest('.ant-modal-wrap');
    if (!w) return false;
    const m = w.querySelector('.ant-modal');
    return !!(m && m.offsetHeight > 0);
  };

  // PRE-CLICK SAFE-MODAL DRAIN (2026-10-12): the TBC-ETD Warning modal sits
  // open across the whole confirm phase (the site re-pops it on every form
  // change while TBC is on) and its overlay SWALLOWS page-button clicks — the
  // "Confirm Booking" click reported success while the modal was still up,
  // the booking was never confirmed, and the status poll correctly failed
  // 5/5. The post-action drain in the executeTool wrapper is too late for the
  // click ITSELF, so drain the known-safe modals FIRST (same rules: the
  // warning's answer is already decided by the flow — OK dismisses and the
  // ETD auto-update IS the point of TBC mode; auto-save recovery -> No), wait
  // for the modal to actually close, and only then resolve the target button.
  // Never drains when the requested button lives INSIDE an open modal — that
  // click is aimed at the modal itself (sailing Create, Assign Job# OK...).
  const _safeModalRules = [
    { label: 'TBC-ETD warning', re: /ETD of TBC Sailing Schedule/i, btn: /^(OK|Ok)$/i },
    { label: 'auto-save recovery', re: /auto save record is found/i, btn: /^No$/i },
    // ETD-vs-CargoReady gate (2026-10-22 live): Confirm Booking pops
    // "ETD is more than 14 days after Cargo Ready Date ... Are you sure to
    // continue? [No, Yes]" whenever the two user-supplied dates are far
    // apart. Unanswered, the confirm NEVER completes (the flow's status
    // polls hard-stopped over a confirm that sat waiting); answered No it
    // CANCELS. The dates come from the user's own request — proceed (Yes).
    { label: 'ETD-vs-CargoReady warning', re: /ETD is more than 14 days after Cargo Ready Date/i, btn: /^Yes$/i },
  ];
  const _drained = [];
  for (const wrap of document.querySelectorAll('.ant-modal-wrap')) {
    const modal = wrap.querySelector('.ant-modal');
    if (!modal || modal.offsetHeight === 0) continue;
    const body = modal.textContent || '';
    for (const r of _safeModalRules) {
      if (!r.re.test(body)) continue;
      // requested button inside THIS modal? leave it alone.
      const footer = modalFooterContainer(modal);
      const btns = footer ? Array.from(footer.querySelectorAll('button')) : [];
      const target = btns.find(x => r.btn.test((x.textContent || '').trim()));
      if (target) {
        // only drain when the user asked for a DIFFERENT button (page-level)
        const wantsThisModal = btns.some(x =>
          (x.textContent || '').trim().toLowerCase() === String(button_name || '').trim().toLowerCase());
        if (wantsThisModal) break;
        target.click();
        _drained.push(r.label);
      }
      break;
    }
  }
  if (_drained.length) {
    await waitForQuiet({ timeout: 3000, quietMs: 400 });
  }

  // Special case: clicking "Search" on a list page must fire the component's
  // getData() (via the React fiber), because a synthetic button click does NOT
  // trigger antd Input.Search onSearch and would leave the grid empty. Fall
  // through to normal click matching if there is no antd search button here —
  // and never when an OPEN MODAL carries its own Search button (that is the
  // sailing schedule modal's search, not a list page).
  if (/^search$/i.test((button_name || '').trim())) {
    const modalHasSearch = [...document.querySelectorAll('.ant-modal button')]
      .some(b => (b.textContent || '').trim().toLowerCase() === 'search'
                 && b.offsetParent !== null && inOpenModal(b));
    if (!modalHasSearch) {
      const main = document.querySelector('main');
      if (main && main.querySelector('.ant-input-search-button')) {
        const triggered = await triggerAntdSearchAndLoad(main, 10000);
        if (triggered) {
          return { content: [{ type: "text", text: `Clicked button: ${button_name} (list data loaded)` }] };
        }
      }
    }
  }

  // Special case: "Add New Sailing Option" is NOT a real button — the visible
  // text is a plain label next to the AddButton icon component (only the icon
  // button carries onClick -> onAddBtnClick). Clicking the label (or any
  // role=button wrapper) reports success but never opens the modal — the whole
  // manual-sailing branch then filled the page's disabled fields behind it
  // (2026-10-05). Click the icon button directly, scoped to the active tab's
  // first collapse panel.
  if (/add new sailing option/i.test((button_name || '').trim())) {
    const clicked = await (async () => {
      const pane = document.querySelector('.ant-tabs-tabpane-active');
      const scope = pane || document;
      const panel = scope.querySelector('.ant-collapse-item');
      const btns = [...(panel || scope).querySelectorAll('button')].filter(b => b.offsetParent !== null);
      const plus = btns.find(b => b.querySelector('.anticon-plus'));
      if (plus) { plus.click(); return true; }
      return false;
    })();
    if (clicked) {
      await waitForRender();
      return { content: [{ type: "text", text: `Clicked button: ${button_name} (AddButton icon — sailing modal)` }] };
    }
    // fall through to normal matching
  }

  // Case-insensitive matching: BMS buttons are capitalized ("Edit", "Save") but
  // the LLM often passes lowercase ("edit", "save"). Case-sensitive compares
  // silently miss them and return "Button not found" (root cause of the
  // "button not found: edit" failure), so normalize both sides to lowercase.
  const bn = (button_name || '').trim().toLowerCase();
  const buttons = document.querySelectorAll('button, [role="button"], input[type="submit"]');
  const matches = [];
  for (const btn of buttons) {
    // Skip hidden buttons (e.g., notification drawer buttons)
    if (btn.offsetParent === null) continue;
    const text = btn.textContent?.trim();
    const ariaLabel = btn.getAttribute('aria-label');
    const value = btn.getAttribute('value');
    const t = (text || '').toLowerCase();
    const al = (ariaLabel || '').toLowerCase();
    const v = (value || '').toLowerCase();
    if (t === bn || t.includes(bn) ||
        al === bn || al.includes(bn) ||
        v === bn) {
      matches.push(btn);
    }
  }
  // an open modal's matching button wins over same-named page buttons behind it
  const btn = matches.find(inOpenModal) || matches[0];
  if (btn) {
    btn.click();
    // For state-changing buttons, verify the real outcome (M5) — this also
    // waits long enough for a late async confirm modal to mount (M1).
    if (ACTION_VERIFY_BUTTONS.test((button_name || '').trim())) {
      const r = await captureActionResult(button_name);
      return { content: [{ type: "text", text: `Clicked button: ${button_name}. RESULT: ${r.outcome} (${r.evidence})` }] };
    }
    await waitForRender();
    return { content: [{ type: "text", text: `Clicked button: ${button_name}` }] };
  }
  // Also check <a> elements styled as buttons
  const links = document.querySelectorAll('a.btn, a[role="button"]');
  for (const link of links) {
    const lt = (link.textContent || '').trim().toLowerCase();
    if (lt === bn || lt.includes(bn)) {
      link.click();
      if (ACTION_VERIFY_BUTTONS.test((button_name || '').trim())) {
        const r = await captureActionResult(button_name);
        return { content: [{ type: "text", text: `Clicked link-button: ${button_name}. RESULT: ${r.outcome} (${r.evidence})` }] };
      }
      await waitForRender();
      return { content: [{ type: "text", text: `Clicked link-button: ${button_name}` }] };
    }
  }
  return { content: [{ type: "text", text: `Button not found: ${button_name}` }] };
}


/**
 * Click a list item (e.g., an SO# link in search results).
 */
async function clickListItem({ identifier }) {
  // Try <a> elements first (SO# links, party links)
  const links = document.querySelectorAll('a');
  for (const link of links) {
    if (link.textContent?.trim() === identifier) {
      link.click();
      await waitForRender(2000);
      return { content: [{ type: "text", text: `Clicked item: ${identifier}` }] };
    }
  }
  // Try table cells
  const cells = document.querySelectorAll('td');
  for (const cell of cells) {
    if (cell.textContent?.trim() === identifier) {
      cell.click();
      await waitForRender(2000);
      return { content: [{ type: "text", text: `Clicked cell: ${identifier}` }] };
    }
  }
  return { content: [{ type: "text", text: `Item not found: ${identifier}` }] };
}


// Shared "tab not found" diagnosis (2026-10-22): the site HIDES the Booking
// Approval / House Doc tabs while the booking's status history has no
// BookingConfirmed (ShippingOrder.js getTabs filters both keys), and hides
// Booking Approval without UAC access. Distinguish that honest case from a
// slow mount so the agent stops instead of retrying a tab that cannot exist.
function _soTabNotFoundResult(tab_name) {
  const tabsNow = Array.from(document.querySelectorAll('[role="tab"]'))
    .map(t => (t.textContent || '').trim()).filter(Boolean);
  const pageHead = ((document.querySelector('main') || {}).innerText || '')
    .split('\n').map(s => s.trim()).filter(Boolean).slice(0, 12);
  const statusLine = pageHead.find(l => /New Booking|Booking Confirmed|Booking Approved|Amend Booking|Cancel/i.test(l)) || '';
  const gated = tabsNow.length >= 6
    && !tabsNow.includes('Booking Approval') && !tabsNow.includes('House Doc');
  if (gated) {
    return { content: [{ type: "text", text:
      `ERROR: Tab not found: ${tab_name} — the booking is NOT CONFIRMED yet (current status: "${statusLine || 'unknown'}"). The site only shows the Booking Approval / House Doc tabs once the booking status history contains "Booking Confirmed" (ShippingOrder tab gate). Do NOT retry: confirm the booking first, then switch to this tab.` }] };
  }
  if (tabsNow.length < 3) {
    return { content: [{ type: "text", text:
      `ERROR: Tab not found: ${tab_name} — the SO page's tabs are still mounting (only ${tabsNow.length} tab(s) rendered). One retry is reasonable; if it repeats, the page failed to load.` }] };
  }
  return { content: [{ type: "text", text: `Tab not found: ${tab_name}. Available tabs: ${tabsNow.join(', ')}` }] };
}

/**
 * Switch to a tab on the Shipping Order detail page.
 */
async function switchSoTab({ tab_name }) {
  const findTabByName = () => {
    const tabs = document.querySelectorAll('[role="tab"]');
    for (const tab of tabs) {
      if (tab.textContent?.trim() === tab_name) {
        return tab;
      }
    }
    const tabLike = document.querySelectorAll('.nav-link, .tab, [data-tab]');
    for (const el of tabLike) {
      if (el.textContent?.trim() === tab_name) {
        return el;
      }
    }
    return null;
  };

  const waitForTabByName = (timeoutMs = 5000) => new Promise((resolve) => {
    const existingTab = findTabByName();
    if (existingTab) {
      resolve(existingTab);
      return;
    }

    const observer = new MutationObserver(() => {
      const nextTab = findTabByName();
      if (nextTab) {
        observer.disconnect();
        resolve(nextTab);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true, characterData: true });

    setTimeout(() => {
      observer.disconnect();
      resolve(findTabByName());
    }, timeoutMs);
  });

  const targetTab = await waitForTabByName(12000);
  if (!targetTab) {
    return _soTabNotFoundResult(tab_name);
  }
    const isTabActive = () => {
      const t = findTabByName();
      if (!t) return false;
      if (t.getAttribute('aria-selected') === 'true') return true;
      return /(^|\s)ant-tabs-tab-active(\s|$)/.test(t.className || '');
    };
    targetTab.click();
    await waitForRender();
    // Activation check (2026-10-22): ShippingOrder.js handleTabClick SILENTLY
    // refuses switching to the House-Doc/Booking-Approval tabs while the SO is
    // in edit mode — it only shows a warning toast ("Please save the existing
    // booking or cancel edit before performing House Doc") and returns. The
    // old code reported "Switched to tab: X" anyway, so a flow kept driving a
    // HIDDEN tab pane (live: the Assign Job# modal mounted invisible and
    // poll_popups correctly saw nothing → hard stop). Verify the tab actually
    // activated; if not, exit a chatbot-owned edit session and retry once.
    if (!isTabActive()) {
      const heal = await _exitSoEditMode();
      if (heal.exited) {
        const retry = findTabByName();
        if (retry) {
          retry.click();
          await waitForRender();
        }
      }
      if (!isTabActive()) {
        const why = heal.needed
          ? (heal.blocked === 'human'
              ? 'the SO page is in EDIT mode with human-made unsaved edits (save or cancel them first, then retry)'
              : `the SO page is in EDIT mode and the edit session could not be exited (${heal.blocked})`)
          : 'the tab did not activate after the click';
        return { content: [{ type: "text", text: `ERROR: could NOT switch to the "${tab_name}" tab — ${why}. Do NOT proceed as if the tab were open; later reads/clicks would target a hidden tab.` }] };
      }
      return { content: [{ type: "text", text: `Switched to tab: ${tab_name} (exited edit mode first — the site blocks this tab while the SO is being edited)` }] };
    }
    return { content: [{ type: "text", text: `Switched to tab: ${tab_name}` }] };
}


/**
 * Read the content of a specific tab on the SO detail page.
 * Switches to the tab first, then reads the panel content.
 */
async function readSoTab({ tab_name }) {
  // Guard: if no SO is open (e.g. open_shipping_order landed on the /AccessDeclined
  // 403 page because the SO number was mistyped), there are no tabs to read.
  // Return a clear error instead of a misleading "Tab not found: <name>".
  const _soUrl = window.location.pathname;
  const _soTitle = document.title || '';
  if (/\/AccessDeclined/i.test(_soUrl) || /403/i.test(_soTitle) || !/\/shipping_order\//i.test(_soUrl)) {
    const _where = /403/i.test(_soTitle) ? '403 Forbidden' : _soUrl;
    return { content: [{ type: "text", text: `ERROR: No Shipping Order is currently open (page is "${_where}"). open_shipping_order must succeed before reading a tab; the requested SO number may be mistyped or inaccessible. Do NOT retry read_so_tab.` }] };
  }

  const isVisible = (element) => {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    return !element.hidden
      && element.getAttribute('aria-hidden') !== 'true'
      && style.display !== 'none'
      && style.visibility !== 'hidden';
  };

  const getPanelText = (panel) => {
    if (!panel) return '';
    return (panel.innerText || panel.textContent || '').trim();
  };

  const normalizeCellText = (value) => (value || '').replace(/\s+/g, ' ').trim();

  const getStatusDataRows = (panel) => {
    if (!panel) {
      return [];
    }

    const headerLabels = new Set(['SO#', 'Status', 'Action', 'Remark', 'User', 'Updated Time']);

    return Array.from(panel.querySelectorAll('table tr'))
      .map((row) => {
        const cells = Array.from(row.querySelectorAll('th, td'))
          .map((cell) => {
            const value = normalizeCellText(cell.innerText || cell.textContent);
            return value === 'View History' ? '' : value;
          });
        const nonEmptyCells = cells.filter(Boolean);
        return {
          cells,
          nonEmptyCells,
          rowText: normalizeCellText(row.innerText || row.textContent)
        };
      })
      .filter(({ nonEmptyCells, rowText }) => {
        if (!nonEmptyCells.length || !rowText || /^no data$/i.test(rowText)) {
          return false;
        }

        const isHeaderRow = nonEmptyCells.every((cell) => headerLabels.has(cell));
        if (isHeaderRow) {
          return false;
        }

        return nonEmptyCells.length >= 2 || /\d{4}-\d{2}-\d{2}/.test(rowText);
      })
      .map(({ cells }) => {
        const dataCells = cells.length >= 7 ? cells.slice(1) : cells;
        const paddedCells = [...dataCells];
        while (paddedCells.length < 6) {
          paddedCells.push('');
        }
        return paddedCells.slice(0, 6);
      });
  };

  const waitForStatusRows = async (panel, timeoutMs = 10000) => {
    const startTime = Date.now();
    let latestRows = getStatusDataRows(panel);

    while (!latestRows.length && (Date.now() - startTime) < timeoutMs) {
      await waitForRender(500);
      latestRows = getStatusDataRows(panel);
    }

    return latestRows;
  };

  const waitForPanelContent = (panel, timeoutMs = 4000) => new Promise((resolve) => {
    const existingText = getPanelText(panel);
    if (existingText) {
      resolve(existingText);
      return;
    }

    const observer = new MutationObserver(() => {
      const nextText = getPanelText(panel);
      if (nextText) {
        observer.disconnect();
        resolve(nextText);
      }
    });

    observer.observe(panel, { childList: true, subtree: true, characterData: true });

    setTimeout(() => {
      observer.disconnect();
      resolve(getPanelText(panel));
    }, timeoutMs);
  });

  const formatStatusTab = (statusRows, fallbackText) => {
    if (!statusRows.length) {
      return fallbackText;
    }

    const historyLines = statusRows.map((cells) => {
      const [soNumber, status, action, remark, user, updatedTime] = cells;
      const displayStatus = status || action;
      const parts = [updatedTime, user].filter(Boolean);
      if (displayStatus) {
        parts.push(`Status: ${displayStatus}`);
      }
      if (action && action !== displayStatus) {
        parts.push(`Action: ${action}`);
      }
      if (remark) {
        parts.push(`Remark: ${remark}`);
      }
      return `- ${parts.join(' | ')}`;
    });

    const explicitStatuses = statusRows
      .map((cells) => cells[1] || cells[2])
      .filter(Boolean);

    const latestExplicitStatus = explicitStatuses[0] || 'Not explicitly shown';

    return [
      'Status History:',
      `Latest explicit status shown in history: ${latestExplicitStatus}`,
      ...historyLines
    ].join('\n');
  };

  const findTabByName = () => {
    const tabs = document.querySelectorAll('[role="tab"]');
    for (const tab of tabs) {
      if (tab.textContent?.trim() === tab_name) {
        return tab;
      }
    }

    const tabLike = document.querySelectorAll('.nav-link, .tab, [data-tab]');
    for (const el of tabLike) {
      if (el.textContent?.trim() === tab_name) {
        return el;
      }
    }

    return null;
  };

  const waitForTabByName = (timeoutMs = 5000) => new Promise((resolve) => {
    const existingTab = findTabByName();
    if (existingTab) {
      resolve(existingTab);
      return;
    }

    const observer = new MutationObserver(() => {
      const nextTab = findTabByName();
      if (nextTab) {
        observer.disconnect();
        resolve(nextTab);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true, characterData: true });

    setTimeout(() => {
      observer.disconnect();
      resolve(findTabByName());
    }, timeoutMs);
  });

  const resolvePanelForTab = (tab) => {
    if (!tab) return null;

    const controls = tab.getAttribute('aria-controls');
    if (controls) {
      const controlledPanel = document.getElementById(controls);
      if (controlledPanel) {
        return controlledPanel;
      }
    }

    const tabId = tab.id;
    if (tabId) {
      const labelledPanel = document.querySelector(`[role="tabpanel"][aria-labelledby="${tabId}"]`);
      if (labelledPanel) {
        return labelledPanel;
      }
    }

    const visiblePanels = Array.from(document.querySelectorAll('[role="tabpanel"]')).filter(isVisible);
    if (visiblePanels.length === 1) {
      return visiblePanels[0];
    }

    const activePanel = document.querySelector('.tab-content .active, .tab-pane.active');
    if (activePanel) {
      return activePanel;
    }

    return visiblePanels[0] || document.querySelector('[role="tabpanel"]');
  };

  // Switch to the tab first. 12s (was 5): loadSO lands late on a loaded box
  // and the status-driven tab set (Booking Approval / House Doc) re-renders
  // after it — 5s gave false "Tab not found" on confirmed bookings.
  const targetTab = await waitForTabByName(12000);
  if (!targetTab) {
    return _soTabNotFoundResult(tab_name);
  }

  targetTab.click();

  // Wait for React to render the tab panel
  await waitForRender(1500);

  // Read the selected tab's panel rather than the first tabpanel in the DOM.
  const selectedTab = Array.from(document.querySelectorAll('[role="tab"]')).find(
    (tab) => tab.getAttribute('aria-selected') === 'true' && tab.textContent?.trim() === tab_name
  ) || targetTab;
  const panel = resolvePanelForTab(selectedTab);

  const statusRows = panel && tab_name === 'Status'
    ? await waitForStatusRows(panel)
    : [];
  let content;
  if (panel && tab_name === 'Status') {
    const statusPanelText = await waitForPanelContent(panel, 10000);
    content = formatStatusTab(statusRows, statusPanelText?.substring(0, 8000) || 'No content visible').substring(0, 8000);
  } else if (panel) {
    // Form tab: read field VALUES (innerText misses <input>/<textarea> .value) after
    // letting party Select labels + input values settle.
    await waitForFormStable(panel);
    content = (readFormFieldsText(panel) || (await waitForPanelContent(panel, 4000))).substring(0, 8000) || 'No content visible';
    // Booking Approval only: the form RESETS itself after a successful save
    // (Approval Status select cleared), so the form values alone can never
    // verify a landed approval. The DOM proof is the approval HISTORY table
    // below the form (status + remark + timestamp rows) — append its rows so
    // the flow's verify read can find the status. Rows are picked by their
    // date column; the header/form rows carry none.
    if (tab_name === 'Booking Approval') {
      const historyRows = Array.from(panel.querySelectorAll('table tr'))
        .map((row) => (row.innerText || '').replace(/\s+/g, ' ').trim())
        .filter((t) => t && !/^no data$/i.test(t) && /\d{4}-\d{2}-\d{2}/.test(t));
      if (historyRows.length) {
        content += '\n\nApproval history:\n' + historyRows.map((r) => '- ' + r).join('\n');
      }
    }
  } else {
    content = 'No content visible';
  }

  return {
    content: [{ type: "text", text: `Tab: ${tab_name}\n\n${content}` }]
  };
}


/**
 * Read the header information of the currently open Shipping Order.
 */
async function readSoHeader() {
  // Validate we're on a Shipping Order detail page before reading.
  const url = window.location.pathname;
  const isSoPage = /\/shipping_order\/S?\d+/i.test(url);
  if (!isSoPage) {
    return {
      content: [{ type: "text", text: 'ERROR: No Shipping Order is currently open. Please open a shipping order first (e.g. open_shipping_order or search_booking) before reading the header.' }]
    };
  }

  // Find the Header tab. Only click it if it isn't already active — re-clicking an
  // active tab re-mounts the panel and makes party/agent Selects flash their raw VALUE
  // (a party ID) before async-resolving to the label, which readSoHeader would capture.
  const tabs = document.querySelectorAll('[role="tab"]');
  const headerTab = Array.from(tabs).find(t => t.textContent?.trim() === 'Header');
  if (!headerTab) {
    return { content: [{ type: "text", text: 'Header tab not found on this page.' }] };
  }

  const headerActive = headerTab.getAttribute('aria-selected') === 'true' ||
                       headerTab.classList.contains('ant-tabs-tab-active');
  if (!headerActive) {
    headerTab.click();
    await waitForRender(1500);
  }

  // Resolve the Header tab's panel element
  const getHeaderPanel = () => {
    const controls = headerTab.getAttribute('aria-controls');
    if (controls) return document.getElementById(controls);
    const panel = document.querySelector('[role="tabpanel"]:not([hidden])') ||
                  document.querySelector('.ant-tabs-tabpane-active');
    return panel;
  };

  // Wait for form values to populate AND for Select labels to STABILIZE.
  // Party/agent Selects render their raw VALUE (a party ID, e.g. "978437") first,
  // then async-resolve to the display label (e.g. "DEMO CONSIGNEE 1 (4DEMOCOHKG)").
  // Breaking as soon as ANY select has text captures the raw ID. Instead, poll until
  // the set of selection-item texts stops changing for two consecutive polls, meaning
  // the labels have resolved. Timeout guards against pages that never resolve.
  // Two race modes to avoid (on top of the label-resolution race described above):
  //   (a) Reading while the form is still EMPTY — the party fetch lands late, so
  //       “stable” on an empty form means nothing. Only accept the form once at
  //       least one Select is populated (a saved SO always has Shipper/Consignee);
  //       plain input values alone are NOT enough evidence of a loaded form.
  //   (b) Breaking early because an INPUT populated while the Selects were still
  //       pending — the signature used to cover Selects only, so an empty Select
  //       set looked “stable”. Inputs are part of the signature now, so a late
  //       populating field keeps the loop alive.
  const maxWait = 10000;
  const startTime = Date.now();
  let prevSig = null;
  let stableRounds = 0;
  let sawPopulatedSelect = false;

  const snapshot = (panel) => {
    const selectItems = panel.querySelectorAll('.ant-select-selection-item');
    const inputs = panel.querySelectorAll('.ant-form-item input');
    const selectTexts = Array.from(selectItems).map(el => (el.textContent || '').trim());
    const inputVals = Array.from(inputs).map(el => (el.value || '').trim());
    return {
      populatedSelects: selectTexts.filter(t => t.length > 0).length,
      populatedInputs: inputVals.filter(v => v.length > 0).length,
      //  separator: concatenating raw values would let adjacent fields blur
      // into one another and fake stability (same trick the original code used).
      sig: selectTexts.join('') + '|' + inputVals.join(''),
    };
  };

  while (Date.now() - startTime < maxWait) {
    const panel = getHeaderPanel();
    if (panel) {
      const snap = snapshot(panel);
      if (snap.populatedSelects >= 1) sawPopulatedSelect = true;
      if (snap.populatedSelects >= 1 || snap.populatedInputs >= 3) {
        if (snap.sig === prevSig) {
          stableRounds += 1;
          if (stableRounds >= 2) break;  // unchanged across 2 polls -> labels resolved
        } else {
          stableRounds = 0;
        }
        prevSig = snap.sig;
      }
    }
    await waitForRender(300);
  }

  // Still no populated Select after the wait — the party fetch can land just
  // past the window. One delayed re-check before concluding the form is empty.
  if (!sawPopulatedSelect) {
    await waitForRender(2000);
    const panel2 = getHeaderPanel();
    if (panel2 && snapshot(panel2).populatedSelects >= 1) {
      // Data landed during the grace wait — let the labels settle before reading.
      await waitForRender(600);
    }
  }

  // Read the panel text directly — do NOT delegate to readSoTab which would
  // re-click the tab and trigger a re-render, losing the loaded data.
  const panel = getHeaderPanel();
  const panelText = (readFormFieldsText(panel) || (panel ? (panel.innerText || '').trim() : '') || 'No content visible').substring(0, 8000);

  return {
    content: [{ type: "text", text: `Tab: Header\n\n${panelText}` }]
  };
}


/**
 * Best-effort lookup of SO numbers similar to one that just failed to open.
 * Used by openShippingOrder when the SO redirects to /AccessDeclined, so a typo
 * (e.g. SHK-BNEVA-1421 instead of the real SHK-NBEVA-1421) surfaces a useful
 * suggestion instead of a dead-end "contact support" message.
 *
 * Searches the trailing numeric segment of the SO number on the booking list
 * (robust to letter transpositions — the tail is usually intact) and returns up
 * to 5 matching SO numbers, excluding the exact failed number.
 */
async function findSimilarShippingOrders(so_number) {
  try {
    const tail = (so_number.match(/(\d+)\s*$/) || [])[1] || so_number;
    const nav = safePush('/BMS/enhance_booking/search_booking');
    if (!nav.confirmed) return [];  // user stayed on the edited page; cannot search
    await waitForRender(2000);

    const main = document.querySelector('main');
    if (!main) return [];

    // The booking list's main search box is input.ant-input[placeholder="Search"].
    // Do NOT use .ant-select-selection-search-input here — those are per-column
    // filters and do not drive the list query.
    const searchInput = main.querySelector('input.ant-input[placeholder="Search"]');
    if (searchInput) {
      const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
      searchInput.focus();
      setter.call(searchInput, tail);
      searchInput.dispatchEvent(new Event('input', { bubbles: true }));
      searchInput.dispatchEvent(new Event('change', { bubbles: true }));
      // Fire Enter so antd Input.Search onSearch updates the component's search
      // term before getData() reads it. Setting the DOM value alone does NOT
      // update React state, so getData would query an empty term.
      ['keydown', 'keypress', 'keyup'].forEach(function (type) {
        searchInput.dispatchEvent(new KeyboardEvent(type, { key: 'Enter', code: 'Enter', keyCode: 13, which: 13, bubbles: true }));
      });
      await realWait(600);
    }

    // Trigger the list query via the component's getData() — a synthetic click
    // does not fire antd Input.Search onSearch. Same React-fiber walk used by
    // triggerAntdSearchAndLoad.
    const searchBtn = main.querySelector('.ant-input-search-button');
    if (searchBtn) {
      const fiberKey = Object.keys(searchBtn).find(
        (k) => k.indexOf('__reactInternalInstance$') === 0 || k.startsWith('__reactFiber$')
      );
      let fiber = fiberKey ? searchBtn[fiberKey] : null;
      let depth = 0;
      while (fiber && depth < 60) {
        if (fiber.stateNode && typeof fiber.stateNode.getData === 'function') {
          try { fiber.stateNode.getData(function () {}); } catch (e) { fiber.stateNode.getData(); }
          break;
        }
        fiber = fiber.return;
        depth += 1;
      }
    }
    await waitForDataLoad(main, 8000);

    const matches = Array.from(main.querySelectorAll('td, a[href*="shipping_order"]'))
      .map((el) => (el.textContent || '').trim())
      .filter((t) => /^SHK-|^S\d{6}/i.test(t));
    return Array.from(new Set(matches))
      .filter((m) => m !== so_number)
      .slice(0, 5);
  } catch (e) {
    console.warn('[BMS Tools] findSimilarShippingOrders failed:', e.message);
    return [];
  }
}


/**
 * Open a shipping order by navigating to its detail page.
 */
async function openShippingOrder({ so_number }) {
  const target = `/BMS/enhance_booking/search_booking/shipping_order/${so_number}`;
  // React Router does NOT remount the SO page when the SAME route is pushed
  // again, and the page's own post-save refresh (loadSO) can fail or lag, so a
  // read-after-write verify would keep reading the PRE-write DOM forever
  // (2026-10-09 live: Assign Job# landed in the DB but the House-Doc "Job#:
  // Not assigned" label stayed stale through both verify retries). Bounce via
  // the booking list first so the SO page unmounts and re-mounts with a fresh
  // loadSO when we are already sitting on the same SO.
  if (window.location.pathname === target) {
    const bounce = safePush('/BMS/enhance_booking/search_booking');
    if (!bounce.confirmed) return _navCanceledResult(bounce.message);
    await waitForRender(400);
  }
  // Navigate to the SO detail page
  const nav = safePush(target);
  if (!nav.confirmed) return _navCanceledResult(nav.message);
  await waitForRender(2000);

  // The SPA's access-check redirect to /AccessDeclined (HTTP 403) is ASYNC and
  // can take several seconds to fire, so a single URL snapshot right after the
  // push mis-reads a forbidden/non-existent SO as successfully opened. Poll
  // until the page settles: the SO's tabs render (real success) or the URL/title
  // flip to /AccessDeclined / "403" (failure). Timeout -> treat as not opened.
  let url = window.location.pathname;
  let title = document.title || '';
  const settled = await new Promise(function (resolve) {
    const start = Date.now();
    (function check() {
      url = window.location.pathname;
      title = document.title || '';
      const declined = /\/AccessDeclined/i.test(url) || /403/i.test(title);
      const tabsReady = /\/shipping_order\//i.test(url) && !declined
        && document.querySelectorAll('[role="tab"]').length > 0;
      if (declined) return resolve('declined');
      if (tabsReady) return resolve('ok');
      if (Date.now() - start > 6000) return resolve('timeout');
      setTimeout(check, 400);
    })();
  });

  // Detect access-decline / not-found. The SPA redirects unknown or forbidden
  // SO numbers to /AccessDeclined (HTTP 403). Previously this returned a SILENT
  // SUCCESS ("Opened shipping order X at /AccessDeclined"), which made the agent
  // proceed to read_so_tab (-> "Tab not found: Booking Approval") and
  // misdiagnose a typo in the SO number as a permission error, dead-ending the
  // user at "contact support". Instead: surface a clear ERROR and run a
  // best-effort search for similar SO numbers so the user gets a useful hint.
  const landedOnSoPage = settled === 'ok';
  if (!landedOnSoPage) {
    const where = /403/i.test(title) ? '403 Forbidden' : url;
    reportProgress('SO not accessible — searching for similar SO numbers…');
    const suggestions = await findSimilarShippingOrders(so_number);
    let text = `ERROR: Could not open shipping order ${so_number}. The record does not exist or this account cannot access it (page redirected to "${where}"). This is most often a typo in the SO number.`;
    if (suggestions.length > 0) {
      const likely = suggestions.length === 1 ? ` Most likely: ${suggestions[0]}.` : '';
      text += ` I searched and found ${suggestions.length} similar SO${suggestions.length > 1 ? 's' : ''}: ${suggestions.join(', ')}.${likely} STOP here: do NOT open, approve, or call any other tool on this SO (no open_shipping_order / read_so_tab / read_so_header / click_button). Reply to the user that the requested SO was NOT found, show the closest match${suggestions.length === 1 ? ' (' + suggestions[0] + ')' : ''}, and ask them to confirm the exact SO number before you proceed with ANY action.`;
    } else {
      text += ` Do NOT call read_so_tab or read_so_header (no SO is open). Ask the user to double-check the SO number.`;
    }
    return { content: [{ type: "text", text }] };
  }

  return { content: [{ type: "text", text: `Opened shipping order ${so_number} at ${url}${_navAutoLeftNote()}` }] };
}


/**
 * Apply a custom filter on the current list page.
 */
async function applyCustomFilter({ field, operator, value }) {
  // Step 1: Open the filter bar if not already visible
  let container = document.querySelector('.filterBarContainer');
  if (!container) {
    const filterIcon = document.querySelector('[class*="anticon-filter"]');
    if (!filterIcon) {
      return { content: [{ type: "text", text: 'Filter button not found on this page' }] };
    }
    const filterBtn = filterIcon.closest('button');
    if (!filterBtn) {
      return { content: [{ type: "text", text: 'Filter button not found on this page' }] };
    }
    filterBtn.click();
    container = await waitForElement('.filterBarContainer', 2000);
    if (!container) {
      return { content: [{ type: "text", text: 'Filter panel did not open' }] };
    }
    await waitForRender(300);
  }

  // Step 2: Ensure at least one clause row exists
  const clausesContainer = container.querySelector('.filterBarClausesContainer');
  let clauseRows = clausesContainer ? clausesContainer.querySelectorAll('.filterBarRowDiv') : [];
  if (clauseRows.length === 0) {
    const addIcon = container.querySelector('[class*="anticon-plus"]');
    if (addIcon) {
      const addBtn = addIcon.closest('button');
      if (addBtn) addBtn.click();
      await waitForRender(500);
    }
    clauseRows = clausesContainer ? clausesContainer.querySelectorAll('.filterBarRowDiv') : [];
    if (clauseRows.length === 0) {
      return { content: [{ type: "text", text: 'Could not add filter clause' }] };
    }
  }

  // Step 3: Select the field (1st ant-select in clause row)
  if (field) {
    const row = clausesContainer.querySelector('.filterBarRowDiv');
    const selects = row.querySelectorAll('.ant-select');
    if (selects.length > 0) {
      await triggerAntdSelectChange(selects[0], field);
      await waitForRender(600);
    }
  }

  // Step 4: Select the operator (2nd ant-select, re-query after field re-render)
  if (operator) {
    const row = clausesContainer.querySelector('.filterBarRowDiv');
    const selects = row.querySelectorAll('.ant-select');
    if (selects.length >= 2) {
      await triggerAntdSelectChange(selects[1], operator);
      await waitForRender(400);
    }
  }

  // Step 5: Fill the value (3rd element in clause row, re-query after operator re-render)
  let valueSet = false;
  if (value) {
    const row = clausesContainer.querySelector('.filterBarRowDiv');
    const cols = row.querySelectorAll('.ant-col');
    // Last col (index 3) is the value column
    if (cols.length >= 4) {
      const valueCol = cols[3];

      // Wait for value input to become enabled (React enables it after field+operator are set)
      const valueInput = valueCol.querySelector('input');
      if (valueInput && valueInput.disabled) {
        await new Promise(resolve => {
          const check = () => {
            if (!valueInput.disabled) return resolve();
            requestAnimationFrame(check);
          };
          setTimeout(() => resolve(), 3000);
          check();
        });
      }

      // Detect value input type
      const select = valueCol.querySelector('.ant-select');
      const picker = valueCol.querySelector('.ant-picker');
      const numberInput = valueCol.querySelector('.ant-input-number-input');
      const textInput = valueCol.querySelector('input');

      if (select) {
        await triggerAntdSelectChange(select, value);
        valueSet = true;
      } else if (picker) {
        const pickerInput = picker.querySelector('input');
        if (pickerInput) {
          setReactInputValue(pickerInput, value);
          pickerInput.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
          valueSet = true;
        }
      } else if (numberInput) {
        setReactInputValue(numberInput, value);
        numberInput.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
        valueSet = true;
      } else if (textInput) {
        setReactInputValue(textInput, value);
        valueSet = true;
      }
    }
    await waitForRender(300);
  }

  // Step 6: Click Apply (button is a sibling of filterBarContainer, not inside it)
  const applyBtn = container.parentElement.querySelector('button.filterBarButton');
  if (applyBtn && !applyBtn.disabled) {
    applyBtn.click();
    await waitForRender(2000);
  }

  // Step 7: Trigger Search to execute the filtered query. Call the component's
  // getData() via the React fiber — synthetic clicks do not fire antd onSearch.
  const main = document.querySelector('main');
  await triggerAntdSearchAndLoad(main, 10000);

  return {
    content: [{ type: "text", text: valueSet || !value
      ? `Applied filter: ${field || '(existing)'} ${operator || 'Like'} ${value || '(existing)'}`
      : `Could not set filter value for field "${field}". The field type may not support text input.` }]
  };
}


/**
 * Apply multiple filters at once — adds a row per filter, clicks Apply once at the end.
 * PREFERRED over applyCustomFilter when more than one filter is needed.
 */
async function applyFilters({ filters }) {
  if (!filters || filters.length === 0) {
    return { content: [{ type: "text", text: 'No filters provided' }] };
  }

  // Step 1: Open the filter bar if not already visible
  let container = document.querySelector('.filterBarContainer');
  if (!container) {
    const filterIcon = document.querySelector('[class*="anticon-filter"]');
    if (!filterIcon) {
      return { content: [{ type: "text", text: 'Filter button not found on this page' }] };
    }
    const filterBtn = filterIcon.closest('button');
    if (!filterBtn) {
      return { content: [{ type: "text", text: 'Filter button not found on this page' }] };
    }
    filterBtn.click();
    container = await waitForElement('.filterBarContainer', 2000);
    if (!container) {
      return { content: [{ type: "text", text: 'Filter panel did not open' }] };
    }
    await waitForRender(300);
  }

  const clausesContainer = container.querySelector('.filterBarClausesContainer');
  if (!clausesContainer) {
    return { content: [{ type: "text", text: 'Filter clauses container not found' }] };
  }

  const results = [];

  for (let i = 0; i < filters.length; i++) {
    const { field, operator, value } = filters[i];

    // Step 2: Ensure row i exists — use first existing row for i=0, add new rows for i>0
    let rows = clausesContainer.querySelectorAll('.filterBarRowDiv');
    if (i >= rows.length) {
      // Click the "+" add button to create a new row
      const addIcon = container.querySelector('[class*="anticon-plus"]');
      if (addIcon) {
        const addBtn = addIcon.closest('button');
        if (addBtn) addBtn.click();
        await waitForRender(500);
      }
      rows = clausesContainer.querySelectorAll('.filterBarRowDiv');
      if (i >= rows.length) {
        results.push(`Row ${i + 1}: Could not add filter clause for "${field}"`);
        continue;
      }
    }

    const row = rows[i];
    let fieldSet = false, operatorSet = false, valueSet = false;

    // Step 3: Select the field (1st ant-select)
    if (field) {
      const selects = row.querySelectorAll('.ant-select');
      if (selects.length > 0) {
        fieldSet = await triggerAntdSelectChange(selects[0], field);
        await waitForRender(600);
      }
    }

    // Step 4: Select the operator (2nd ant-select, re-query after field re-render)
    if (operator) {
      const freshRow = clausesContainer.querySelectorAll('.filterBarRowDiv')[i];
      const selects = freshRow.querySelectorAll('.ant-select');
      if (selects.length >= 2) {
        operatorSet = await triggerAntdSelectChange(selects[1], operator);
        await waitForRender(400);
      }
    }

    // Step 5: Fill the value (re-query after operator re-render)
    if (value) {
      const freshRow = clausesContainer.querySelectorAll('.filterBarRowDiv')[i];
      const cols = freshRow.querySelectorAll('.ant-col');
      if (cols.length >= 4) {
        const valueCol = cols[3];

        // Wait for value input to become enabled (React enables it after field+operator are set)
        const valueInput = valueCol.querySelector('input');
        if (valueInput && valueInput.disabled) {
          await new Promise(resolve => {
            const check = () => {
              if (!valueInput.disabled) return resolve();
              requestAnimationFrame(check);
            };
            setTimeout(() => resolve(), 3000);
            check();
          });
        }

        const select = valueCol.querySelector('.ant-select');
        const picker = valueCol.querySelector('.ant-picker');
        const numberInput = valueCol.querySelector('.ant-input-number-input');
        const textInput = valueCol.querySelector('input');

        if (select) {
          valueSet = await triggerAntdSelectChange(select, value);
        } else if (picker) {
          const pickerInput = picker.querySelector('input');
          if (pickerInput) {
            setReactInputValue(pickerInput, value);
            pickerInput.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
            valueSet = true;
          }
        } else if (numberInput) {
          setReactInputValue(numberInput, value);
          numberInput.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
          valueSet = true;
        } else if (textInput) {
          setReactInputValue(textInput, value);
          valueSet = true;
        }
      }
      await waitForRender(300);
    }

    results.push(`${field || '(existing)'} ${operator || 'Like'} ${value || '(existing)'}${valueSet ? '' : ' [value may not have been set]'}`);
  }

  // Step 6: Click Apply ONCE after all filters are set (button is a sibling of filterBarContainer)
  const applyBtn = container.parentElement.querySelector('button.filterBarButton');
  if (applyBtn && !applyBtn.disabled) {
    applyBtn.click();
    await waitForRender(2000);
  }

  // Step 7: Trigger Search to execute the filtered query. Call the component's
  // getData() via the React fiber — synthetic clicks do not fire antd onSearch.
  const main = document.querySelector('main');
  await triggerAntdSearchAndLoad(main, 10000);

  return {
    content: [{ type: "text", text: `Applied ${filters.length} filter(s): ${results.join('; ')}` }]
  };
}


/**
 * Navigate pagination.
 */
async function paginate({ page_number, direction }) {
  if (page_number) {
    const pageItems = document.querySelectorAll('.ant-pagination-item');
    for (const item of pageItems) {
      const link = item.querySelector('a');
      if (link && link.textContent?.trim() === String(page_number)) {
        link.click();
        await waitForRender(2000);
        return { content: [{ type: "text", text: `Navigated to page ${page_number}` }] };
      }
    }
    return { content: [{ type: "text", text: `Page ${page_number} not found in pagination` }] };
  }

  if (direction === 'next') {
    const nextItem = document.querySelector('.ant-pagination-next');
    if (nextItem && !nextItem.classList.contains('ant-pagination-disabled')) {
      const link = nextItem.querySelector('a');
      if (link) {
        link.click();
        await waitForRender(2000);
        return { content: [{ type: "text", text: 'Navigated to next page' }] };
      }
    }
    return { content: [{ type: "text", text: 'Next page not available (already on last page or no pagination)' }] };
  } else if (direction === 'previous') {
    const prevItem = document.querySelector('.ant-pagination-prev');
    if (prevItem && !prevItem.classList.contains('ant-pagination-disabled')) {
      const link = prevItem.querySelector('a');
      if (link) {
        link.click();
        await waitForRender(2000);
        return { content: [{ type: "text", text: 'Navigated to previous page' }] };
      }
    }
    return { content: [{ type: "text", text: 'Previous page not available (already on first page or no pagination)' }] };
  }

  return { content: [{ type: "text", text: `Pagination action not found: ${direction || page_number}` }] };
}


/**
 * Search for bookings — navigates to search page, enters search term, clicks Search.
 */
async function searchBooking(params) {
  const searchTerm = params.search_term || params.so_number;

  // Navigate to search booking page if not already there. EXACT match: the
  // SO detail route '/search_booking/shipping_order/SNNNNNNN' also CONTAINS
  // '/search_booking', and that substring match made the tool believe it was
  // already on the list — the search fields never existed on the SO page and
  // the step failed (2026-10-13, P5 after opening S20261044).
  if (window.location.pathname.replace(/\/+$/, '') !== '/BMS/enhance_booking/search_booking') {
    const nav = safePush('/BMS/enhance_booking/search_booking');
    if (!nav.confirmed) return _navCanceledResult(nav.message);
    await waitForRender(2000);
  }

  // Enter search term into the booking page search bar (inside <main>, NOT the sidebar search)
  if (searchTerm) {
    const main = document.querySelector('main');
    const searchInput = main && (
      main.querySelector('input.ant-input[placeholder="Search"]')
      || main.querySelector('.ant-input-wrapper input.ant-input[placeholder="Search"]')
      || main.querySelector('.ant-select-selection-search-input')
    );
    if (searchInput) {
      searchInput.focus();
      setReactInputValue(searchInput, searchTerm);
      await waitForRender(500);
    } else {
      return { content: [{ type: "text", text: 'Search bar not found on this page.' }] };
    }
  }

  // Trigger the search and wait for results to load. A synthetic click on the
  // antd search button does NOT reliably fire onSearch -> getData, so we call
  // the list component's getData() directly via the React fiber (see
  // triggerAntdSearchAndLoad). Reading results any sooner raced the async load.
  const main = document.querySelector('main');
  await triggerAntdSearchAndLoad(main, 10000);

  // Read the results — count total and collect visible SO links
  const totalText = document.querySelector('.ant-pagination-total-text, [class*="total"]')?.textContent?.trim();
  const rows = document.querySelectorAll('a[href*="shipping_order"]');
  const results = Array.from(rows).slice(0, 10).map(a => ({
    so_number: a.textContent?.trim(),
    url: a.href
  }));

  const summary = totalText || (results.length > 0 ? `${results.length} results` : 'No results');
  return {
    content: [{ type: "text", text: results.length > 0
      ? `Search for "${searchTerm || 'all'}": ${summary}\n${results.map(r => r.so_number).join(', ')}`
      : `Search for "${searchTerm || 'all'}": No results found.` }]
  };
}


// ==================== New Tool Functions ====================

/**
 * Read form field VALUES (by control type) under `root` as "Label: value (type)" lines.
 * Unlike element.innerText, this captures <input>/<textarea> .value, which innerText
 * omits — so text fields (e.g. "Address in HBL") are read correctly instead of empty.
 */
/**
 * Read ONE form field's value given its detected type. Single source of truth for the
 * value-reading switch (used by readFormFieldsText; readForm delegates to it) so any
 * future selector fix is a one-line change. Covers all BMS control families incl.
 * multi-select/AutoComplete, Cascader/TreeSelect, RangePicker, Checkbox.Group, Upload,
 * Segmented/Rate/Slider/Transfer, plus a final fallback that captures display-only span
 * / read-only FormItem text (which reading only <input>.value would otherwise miss).
 */
function readFieldValue(formItem, type) {
  let value = '';
  if (type === 'select') {
    // multi-select / mode=tags -> join all tags; AutoComplete -> its search input value.
    const items = formItem.querySelectorAll('.ant-select-selection-item');
    if (items.length) {
      value = Array.from(items).map(e => e.textContent.trim()).join(', ');
    } else {
      const si = formItem.querySelector('.ant-select-selection-search-input');
      value = si ? si.value.trim() : '';
    }
  } else if (type === 'rangepicker') {
    value = Array.from(formItem.querySelectorAll('.ant-picker input')).map(i => i.value).filter(Boolean).join(' - ');
  } else if (type === 'datepicker') {
    const i = formItem.querySelector('.ant-picker input');
    value = i ? i.value : '';
  } else if (type === 'cascader' || type === 'treeselect') {
    value = Array.from(formItem.querySelectorAll('.ant-select-selection-item')).map(e => e.textContent.trim()).join(', ');
  } else if (type === 'segmented') {
    value = ((formItem.querySelector('.ant-segmented-item-selected') || {}).textContent || '').trim();
  } else if (type === 'rate') {
    value = String(formItem.querySelectorAll('.ant-rate-star-full').length + 0.5 * formItem.querySelectorAll('.ant-rate-star-half').length);
  } else if (type === 'slider') {
    const handle = formItem.querySelector('.ant-slider-handle');
    const track = formItem.querySelector('.ant-slider');
    value = (handle && handle.getAttribute('aria-valuenow')) || (track && track.getAttribute('aria-valuenow')) || '';
  } else if (type === 'upload') {
    value = Array.from(formItem.querySelectorAll('.ant-upload-list-item-name')).map(e => e.textContent.trim()).join(', ');
  } else if (type === 'transfer') {
    value = Array.from(formItem.querySelectorAll('.ant-transfer-list:last-child .ant-transfer-list-content-item:not(.ant-transfer-list-content-item-disabled)')).map(e => e.textContent.trim()).join(', ');
  } else if (type === 'checkboxgroup') {
    value = Array.from(formItem.querySelectorAll('.ant-checkbox-wrapper-checked')).map(e => e.textContent.trim()).join(', ');
  } else if (type === 'checkbox') {
    value = formItem.querySelector('.ant-checkbox-checked') ? 'checked' : 'unchecked';
  } else if (type === 'switch') {
    value = formItem.querySelector('.ant-switch-checked') ? 'on' : 'off';
  } else if (type === 'radio') {
    const r = formItem.querySelector('.ant-radio-button-wrapper-checked, .ant-radio-wrapper-checked');
    value = r ? r.textContent.trim() : '';
  } else if (type === 'inputnumber') {
    const i = formItem.querySelector('.ant-input-number input');
    value = i ? i.value : '';
  } else if (type === 'textarea') {
    const t = formItem.querySelector('textarea');
    value = t ? t.value : '';
  } else if (type === 'input') {
    const i = formItem.querySelector('input');
    value = i ? i.value : '';
  } else {
    // unknown / display-only span / read-only FormItem: capture whatever text the
    // control renders (e.g. region/country shown as a sibling <span>).
    const ctrl = formItem.querySelector('.ant-form-item-control');
    value = (ctrl ? (ctrl.innerText || '') : '').trim();
  }
  return value;
}

function readFormFieldsText(root) {
  const rootEl = root || document;
  const formItems = rootEl.querySelectorAll('.ant-form-item');
  const fields = [];

  for (const formItem of formItems) {
    const labelEl = formItem.querySelector('.ant-form-item-label label');
    if (!labelEl) continue;
    const label = labelEl.textContent.trim();
    if (!label || label === ':' || label === '*') continue;

    // ancestor-aware hidden check: getComputedStyle doesn't propagate ancestor
    // display:none, so use offsetParent / checkVisibility to also skip fields hidden by
    // an ancestor (e.g. an inactive antd Tab pane kept in the DOM).
    const hidden = formItem.checkVisibility ? !formItem.checkVisibility() : (formItem.offsetParent === null);
    if (hidden) continue;

    const type = detectFieldType(formItem);
    const isDisabled = !!formItem.querySelector('[disabled], .ant-input-disabled, .ant-select-disabled, .ant-radio-button-wrapper-disabled, .ant-switch-disabled');

    let value = readFieldValue(formItem, type);

    // Marks & No. / Goods Description render as a TextAreaWithLineNumbers in a
    // LABEL-LESS FormItem directly under the labeled one (which only carries
    // the template-adopt button) — the labeled row reads as an empty control.
    // If the labeled value is empty and a sibling label-less textarea exists,
    // read the textarea instead (2026-09-30 fill-details verify fix).
    if (!value) {
      let sib = formItem.nextElementSibling;
      while (sib && !sib.classList.contains('ant-form-item')) sib = sib.nextElementSibling;
      const sibArea = sib && !sib.querySelector('.ant-form-item-label label')
        ? sib.querySelector('.textarea-with-line-numbers textarea, textarea')
        : null;
      if (sibArea) value = sibArea.value || '';
    }

    fields.push(`${label}: "${value}" (${type}${isDisabled ? ', disabled' : ''})`);
  }

  return fields.join('\n');
}

/**
 * Wait for form values in `panel` to STABILIZE. Party/agent Selects render their raw
 * VALUE (a party ID) first then async-resolve to the label, and text-input values
 * populate after the labels. Poll until both the selection-item texts and the input
 * values stop changing for two consecutive polls, so a subsequent read captures
 * resolved labels and populated values instead of transient IDs / empties.
 */
async function waitForFormStable(panel, maxWait = 8000) {
  if (!panel) return;
  const startTime = Date.now();
  let prevSig = null;
  let stableRounds = 0;

  while (Date.now() - startTime < maxWait) {
    const selectItems = panel.querySelectorAll('.ant-select-selection-item');
    const inputs = panel.querySelectorAll('.ant-form-item input');
    const hasValues = Array.from(selectItems).some(el => (el.textContent || '').trim().length > 0)
                   || Array.from(inputs).some(el => (el.value || '').trim().length > 0);
    if (hasValues) {
      const sig = Array.from(selectItems).map(el => (el.textContent || '').trim()).join('')
                + '' + Array.from(inputs).map(el => (el.value || '').trim()).join('');
      if (sig === prevSig) {
        stableRounds += 1;
        if (stableRounds >= 2) break;  // unchanged across 2 polls -> settled
      } else {
        stableRounds = 0;
      }
      prevSig = sig;
    }
    await waitForRender(300);
  }
}


/**
 * Read all form field values from the current page.
 */
async function readForm() {
  // Delegate to the shared reader (same field-value logic as readFormFieldsText) so the
  // value switch lives in exactly one place (readFieldValue) — no duplicate to drift.
  const text = readFormFieldsText(document);
  return { content: [{ type: "text", text: text || "No form fields found on this page." }] };
}


/**
 * Fill multiple text/number/textarea form fields at once.
 */
async function fillForm({ fields }) {
  const results = [];

  for (const field of fields) {
    let found = [];
    for (let attempt = 0; attempt < 5; attempt++) {
      found = findFormFieldsByLabel(field.label);
      if (found.length === 0 && field.name) found = findFormFieldsByName(field.name);
      if (found.length > 0) break;
      await waitForRender(600);
    }
    // antd keeps INACTIVE tab panes mounted: a name match can hit a hidden
    // copy of the same field on another tab (the Confirm Booking email once
    // landed in the Details tab's hidden contact_email — the confirm was then
    // still refused for an empty email, 2026-10-05). Prefer visible matches -
    // and when a modal is open / an enabled twin exists, prefer THAT
    // (see preferUsableFields).
    found = preferUsableFields(found);

    if (found.length === 0) {
      results.push(`${field.label || field.name}: not found`);
      continue;
    }

    const { control, type, formItem } = found[0];

    // Check disabled
    const isDisabled = !!formItem.querySelector('[disabled], .ant-input-disabled, .ant-select-disabled');
    if (isDisabled) {
      results.push(`${field.label}: skipped (disabled)`);
      continue;
    }

    // Handle by type
    if (type === 'input' || type === 'textarea') {
      setReactInputValue(control, field.value);
      results.push(`${field.label}: filled`);
    } else if (type === 'inputnumber') {
      const innerInput = formItem.querySelector('.ant-input-number input') || control.querySelector('input');
      if (innerInput) {
        setReactInputValue(innerInput, field.value);
        results.push(`${field.label}: filled`);
      } else {
        results.push(`${field.label}: could not find inner input`);
      }
    } else if (type === 'select') {
      results.push(`${field.label}: skipped (use select_option instead)`);
    } else if (type === 'datepicker') {
      results.push(`${field.label}: skipped (use set_date instead)`);
    } else if (type === 'checkbox' || type === 'switch') {
      results.push(`${field.label}: skipped (use toggle_checkbox instead)`);
    } else if (type === 'radio') {
      results.push(`${field.label}: skipped (use select_radio instead)`);
    } else {
      results.push(`${field.label}: unsupported type ${type}`);
    }

    await waitForRender(300);
  }

  return {
    content: [{ type: "text", text: results.join('\n') }]
  };
}


/**
 * Select an option in an antd Select dropdown.
 */
async function selectOption({ label, name, value, party_id }) {
  // Retry field lookup — form fields may still be rendering after tab switch.
  let found = [];
  for (let attempt = 0; attempt < 5; attempt++) {
    found = findFormFieldsByLabel(label || name);
    if (found.length === 0 && name) found = findFormFieldsByName(name);
    if (found.length > 0) break;
    await waitForRender(600);
  }

  found = preferUsableFields(found);

  if (found.length === 0) {
    return { content: [{ type: "text", text: `Select not found: ${label || name}` }] };
  }

  const { control, formItem } = found[0];

  // Check disabled
  if (formItem.querySelector('.ant-select-disabled')) {
    return { content: [{ type: "text", text: `Select is disabled: ${label || name}` }] };
  }

  // Already-selected guard: the deterministic flows send fallback chains
  // (party code -> canonical name -> short tokens) for the SAME field because
  // they cannot know which spelling the site search will accept. Once one
  // attempt succeeded, the field shows a value — re-selecting it reopens the
  // dropdown, re-runs the API search and can even clear later fields. Skip
  // when the current selection already matches this attempt's value.
  const currentSelection = (formItem.querySelector('.ant-select-selection-item')?.textContent || '').trim().toLowerCase();
  const partyKey = SAVE_PARTY_RESTORE_KEYS[(label || name || '').toLowerCase()];
  if (currentSelection) {
    const target = String(value || '').trim().toLowerCase();
    const targetWords = target.split(/[^a-z0-9]+/).filter(w => w.length >= 2);
    const covered = currentSelection.includes(target)
      || (targetWords.length > 0 && targetWords.every(w => currentSelection.includes(w)));
    if (covered) {
      return { content: [{ type: "text", text: `${label || name}: already set to "${currentSelection}" (skipped redundant select)` }] };
    }
  }
  // Party variant of the guard: the fast path below sets the context/form
  // store, and the rendered label can stay blank — a later fallback attempt
  // for the SAME party must not re-fire the handler (a consignee re-select
  // re-arms the related-party callback that wipes the other fields).
  if (partyKey && !currentSelection) {
    try {
      const inst = findHeaderInstance(formItem.querySelector('.ant-select'));
      const soId = inst && inst.props && inst.props.so ? inst.props.so[partyKey.soField] : null;
      if (soId !== null && soId !== undefined && soId !== '') {
        return { content: [{ type: "text", text: `${label || name}: already set (party id ${soId} in context, skipped redundant select)` }] };
      }
    } catch (e) { /* fall through */ }
  }

  // Party fast path — set the field through the site's OWN select handlers
  // (context + antd form store) instead of typing into the dropdown: no search
  // round-trip, no suggestion list to click, no per-attempt retries. A
  // party_id param is only honored when it is a SITE-space id (the flows no
  // longer send the resolver's raw SYS_PARTY id — the site's profile-filtered
  // search uses a different id space for the same company, 2026-09-23); an id
  // miss fails fast instead of re-running doomed name searches. This is
  // both the speed fix (the typed path spent ~10-30s per party on search waits
  // and re-picks) and the accuracy fix (the bot typed the query but failed to
  // click the rendered suggestion — 2026-09-21 user report). Falls back to the
  // typed path below on any failure EXCEPT an id miss.
  if (partyKey) {
    try {
      const fast = await restoreHeaderParty((label || name || '').toLowerCase(), value, partyKey, party_id);
      if (fast.ok) {
        return { content: [{ type: "text", text: `Selected "${value}" in ${label || name}` }] };
      }
      if (fast.via === 'id-not-found') {
        return { content: [{ type: "text", text: `Could not select "${value}" in ${label || name}: party id ${party_id} not found in the party master` }] };
      }
      if (fast.via === 'empty-search') {
        return { content: [{ type: "text", text: `Could not select "${value}" in ${label || name}: the BMS party search returned no records for it` }] };
      }
    } catch (e) { /* fall through to the typed UI path */ }
  }

  const selectRoot = formItem.querySelector('.ant-select');
  // The option list can lag the select (async-loaded lists — the sailing
  // modal's Carrier options arrive with mstCarrierAlliance a beat later; the
  // first typed attempt found an empty dropdown and failed the whole action,
  // 2026-10-05). Retry the typed select a few times before giving up.
  // VERIFY-STICK GUARD (2026-10-12): an option click is not success. On the
  // Confirm Booking tab the Space Status select reports "Selected ..." while
  // the form read-back shows the field EMPTY — a late form reset (or the TBC
  // toggle's side effect) wipes the value right after the click, the site
  // then refuses Confirm Booking, and the flow only finds out five reads
  // later at the status poll. So: after each click, re-read the rendered
  // selection and only accept when it actually shows the value; a wipe
  // converts the attempt into a retry instead of a false success.
  const targetNorm = String(value || '').trim().toLowerCase();
  const targetWordList = targetNorm.split(/[^a-z0-9]+/).filter(w => w.length >= 2);
  const selectionMatches = () => {
    const shown = (formItem.querySelector('.ant-select-selection-item')?.textContent || '')
      .trim().toLowerCase();
    if (!shown) return false;
    if (shown.includes(targetNorm)) return true;
    return targetWordList.length > 0 && targetWordList.every(w => shown.includes(w));
  };
  let success = false;
  let clickedButNotStuck = false;
  for (let attempt = 0; attempt < 4 && !success; attempt++) {
    const clickedOptionText = await triggerAntdSelectChange(selectRoot, value, label || name);
    if (!clickedOptionText) { await realWait(1500); continue; }
    // poll the rendered selection for up to ~2.5s (late wipes land within a
    // beat of the click; antd also needs a frame to paint the selection).
    // Stickiness = the field shows the option we actually CLICKED. The click
    // already matched that option against the target value; judging the
    // render against the TYPED value instead false-failed every select whose
    // spelling differs from the option's ("Jebel Ali DXB" typed vs "JEBEL
    // ALI (AEJEA)" rendered — live 2026-10-23: the sailing-modal POD select
    // clicked the right option on attempt 1, then re-ran the port search
    // THREE more times and still reported "did not stick"). An empty or
    // different render (a genuine late wipe) still fails and retries.
    const clickedNorm = String(clickedOptionText);
    const clickedWords = clickedNorm.split(/[^a-z0-9]+/).filter(w => w.length >= 2);
    const selectionShowsClicked = () => {
      const shown = (formItem.querySelector('.ant-select-selection-item')?.textContent || '')
        .trim().toLowerCase();
      if (!shown) return false;
      if (shown.includes(clickedNorm)) return true;
      return clickedWords.length > 0 && clickedWords.every(w => shown.includes(w));
    };
    let stuck = false;
    for (let p = 0; p < 5 && !stuck; p++) {
      await realWait(500);
      stuck = selectionMatches() || selectionShowsClicked();
    }
    if (stuck) { success = true; }
    else { clickedButNotStuck = true; await realWait(800); }
  }

  return {
    content: [{ type: "text", text: success
      ? `Selected "${value}" in ${label || name} (verified in form)`
      : clickedButNotStuck
        ? `Could not select "${value}" in ${label || name}: the option was clicked but the selection did not stick in the form (a late form reset may have cleared it)`
        : `Could not find option "${value}" in ${label || name}` }]
  };
}


/**
 * Set a date value in an antd DatePicker.
 */
async function setDate({ label, name, value }) {
  let found = [];
  for (let attempt = 0; attempt < 5; attempt++) {
    found = findFormFieldsByLabel(label || name);
    if (found.length === 0 && name) found = findFormFieldsByName(name);
    if (found.length > 0) break;
    await waitForRender(600);
  }

  found = preferUsableFields(found);

  if (found.length === 0) {
    return { content: [{ type: "text", text: `DatePicker not found: ${label || name}` }] };
  }

  const { formItem } = found[0];

  if (formItem.querySelector('.ant-picker-disabled')) {
    return { content: [{ type: "text", text: `DatePicker is disabled: ${label || name}` }] };
  }

  const input = formItem.querySelector('.ant-picker input');
  if (!input) {
    return { content: [{ type: "text", text: `DatePicker input not found: ${label || name}` }] };
  }

  // Convert YYYY-MM-DD to DD/MM/YYYY (BMS display format) and Date object
  const parts = value.split('-');
  if (parts.length !== 3) {
    return { content: [{ type: "text", text: `Invalid date format, expected YYYY-MM-DD: ${value}` }] };
  }
  const formatted = `${parts[2]}/${parts[1]}/${parts[0]}`;
  const newDate = moment(value, 'YYYY-MM-DD');

  // Use React fiber to call antd's onChange directly.
  // Direct DOM manipulation doesn't work because antd DatePicker controls
  // the input value via React state (the input has readonly attribute).
  const picker = formItem.querySelector('.ant-picker');
  const fiberKey = Object.keys(picker).find(k => k.startsWith('__reactFiber$') || k.startsWith('__reactInternalInstance$'));
  if (!fiberKey) {
    return { content: [{ type: "text", text: `Could not access React internals for: ${label || name}` }] };
  }

  let current = picker[fiberKey];
  let onChange = null;
  let depth = 0;
  while (current && depth < 15) {
    if (current.memoizedProps && current.memoizedProps.onChange) {
      onChange = current.memoizedProps.onChange;
      break;
    }
    current = current.return;
    depth++;
  }

  if (!onChange) {
    return { content: [{ type: "text", text: `Could not find onChange handler for: ${label || name}` }] };
  }

  // antd DatePicker onChange signature: onChange(date: Date, dateString: string)
  // The dateString arg MUST be the site-canonical ISO 'YYYY-MM-DD': several
  // handlers store it VERBATIM into the sailing payload (the create modal's
  // onEtdEtaChange), and the server's JSON reader cannot parse a DD/MM/YYYY
  // string — live-captured 500 "Could not convert string to DateTime:
  // 24/10/2026. Path 'sailingSchedule.pod_eta'" on the manual-sailing upsert
  // (2026-10-05). The picker's own display comes from the moment, so the
  // display format is unaffected.
  onChange(newDate, value);
  await waitForRender(300);

  // Async modal lifecycles can wipe the value right after it lands: the
  // sailing modal's componentDidUpdate resetFields()s on every re-render while
  // its carrier-list AJAX is still in flight (a fresh-mount window of several
  // seconds), silently clearing form-only values like Departure From — the
  // Search click then no-ops on searchByAPI's empty-date warn toast (2026-10-05
  // live finding). Read the committed display back and re-assert until it
  // sticks; stop as soon as it does.
  for (let attempt = 0; attempt < 4; attempt++) {
    const shown = input.value || '';
    if (shown.includes(formatted) || shown.includes(value)) break;
    onChange(newDate, formatted);
    await waitForRender(900);
  }

  // Close picker popup if it opened
  if (picker.classList.contains('ant-picker-focused')) {
    document.body.click();
    await waitForRender(200);
  }

  return {
    content: [{ type: "text", text: `Set ${label || name} to ${value}` }]
  };
}


/**
 * Check or uncheck a Checkbox or Switch.
 */
async function toggleCheckbox({ label, name, checked }) {
  // Try checkbox by wrapper text (e.g., "S/O Reference Not Applicable")
  const checkboxWrappers = document.querySelectorAll('.ant-checkbox-wrapper');
  for (const wrapper of checkboxWrappers) {
    const text = wrapper.textContent.trim();
    if (text.toLowerCase().includes((label || '').toLowerCase())) {
      const cb = wrapper.querySelector('.ant-checkbox');
      if (!cb) continue;
      const isChecked = cb.classList.contains('ant-checkbox-checked');
      if (isChecked !== checked) {
        wrapper.click();
        await waitForRender(300);
      }
      return {
        content: [{ type: "text", text: `${label}: ${checked ? 'checked' : 'unchecked'}` }]
      };
    }
  }

  // Try switch by FormItem label (with retry for slow renders)
  let found = [];
  for (let attempt = 0; attempt < 5; attempt++) {
    found = findFormFieldsByLabel(label || name);
    if (found.length === 0 && name) found = findFormFieldsByName(name);
    if (found.length > 0) break;
    await waitForRender(600);
  }
  found = preferUsableFields(found);

  // Fallback: search switches directly (handles custom layouts without .ant-form-item)
  if (found.length === 0) {
    const searchTerm = (label || name || '').toLowerCase();
    const mainContent = document.querySelector('.ant-tabs-tabpane-active') || document.querySelector('main') || document.body;
    const allSwitches = mainContent.querySelectorAll('.ant-switch');
    for (const sw of allSwitches) {
      // Check if the switch's row or nearby text contains the label
      const row = sw.closest('.ant-row');
      if (row && row.textContent.toLowerCase().includes(searchTerm)) {
        const isOn = sw.classList.contains('ant-switch-checked');
        if (isOn !== checked) {
          sw.click();
          await waitForRender(300);
        }
        return {
          content: [{ type: "text", text: `${label}: ${checked ? 'on' : 'off'}` }]
        };
      }
    }
    // Last resort: if NO label/name was given and exactly one switch is visible,
    // use it. Never grab an arbitrary switch when the user named a field that did
    // not match - that would silently toggle the wrong control.
    if (allSwitches.length === 1 && !(label || name)) {
      const sw = allSwitches[0];
      const isOn = sw.classList.contains('ant-switch-checked');
      if (isOn !== checked) {
        sw.click();
        await waitForRender(300);
      }
      return {
        content: [{ type: "text", text: `${label}: ${checked ? 'on' : 'off'}` }]
      };
    }
  }

  if (found.length === 0) {
    return { content: [{ type: "text", text: `Checkbox/Switch not found: ${label || name}` }] };
  }

  const { control, formItem } = found[0];

  // Handle switch
  if (control && control.classList.contains('ant-switch')) {
    const isOn = control.classList.contains('ant-switch-checked');
    if (isOn !== checked) {
      control.click();
      await waitForRender(300);
    }
    return {
      content: [{ type: "text", text: `${label}: ${checked ? 'on' : 'off'}` }]
    };
  }

  // Handle checkbox inside FormItem
  const cbWrapper = formItem.querySelector('.ant-checkbox-wrapper');
  if (cbWrapper) {
    const cb = cbWrapper.querySelector('.ant-checkbox');
    const isChecked = cb && cb.classList.contains('ant-checkbox-checked');
    if (isChecked !== checked) {
      cbWrapper.click();
      await waitForRender(300);
    }
    return {
      content: [{ type: "text", text: `${label}: ${checked ? 'checked' : 'unchecked'}` }]
    };
  }

  return { content: [{ type: "text", text: `No toggleable control found for: ${label || name}` }] };
}


/**
 * Select a radio button in a RadioGroup.
 */
async function selectRadio({ label, name, value }) {
  let found = [];
  // Retry up to 5x/600ms like selectOption/setDate/toggleCheckbox: after a tab
  // switch the radio group often has not mounted yet, and a single lookup falsely
  // reports 'Radio group not found' for a field that renders ~1s later.
  for (let attempt = 0; attempt < 5; attempt++) {
    found = findFormFieldsByLabel(label || name);
    if (found.length === 0 && name) {
      found = findFormFieldsByName(name);
    }
    if (found.length > 0) break;
    await waitForRender(600);
  }

  if (found.length === 0) {
    // Label-less radio groups (the sailing modal's Search/Create mode switch
    // has no form label and antd puts no matching id on the radio inputs).
    // The radio BUTTON text is unique ("Search Sailing" / "Create Manual
    // Sailing"), so fall back to matching the wrapper by its own text.
    // RETRY the scan (live S202610125, 2026-10-23): the modal hosting the
    // group mounts ~1-2s AFTER the opener click's tool result returned (the
    // click's settle gate can pass while the modal's first AJAX is still
    // queued), and the old single scan read "Radio group not found" — the
    // whole confirm flow then died in recovery before ever touching the
    // form.
    const wanted = (value || '').toLowerCase().trim();
    let byText = null;
    for (let attempt = 0; attempt < 6 && !byText; attempt++) {
      const wrappers = [...document.querySelectorAll('.ant-radio-button-wrapper, .ant-radio-wrapper')]
        .filter(w => w.offsetParent !== null);
      byText = wrappers.find(w => ((w.textContent || '').trim().toLowerCase() === wanted));
      if (!byText) await waitForRender(700);
    }
    if (byText) {
      const isChecked = (w) => w.classList.contains('ant-radio-button-wrapper-checked') ||
        w.classList.contains('ant-radio-wrapper-checked');
      if (isChecked(byText)) {
        return { content: [{ type: "text", text: `${value}: already selected` }] };
      }
      // Click the INNER INPUT, not the wrapper label: a programmatic label
      // click can toggle twice (label -> input -> antd onChange fires on both)
      // and flip the group straight back - the sailing modal then ran its
      // whole create-form fill in create mode while the React STATE said
      // 'search', leaving the Create OK disabled (2026-10-05 live finding).
      const input = byText.querySelector('input[type="radio"]');
      (input || byText).click();
      await waitForRender(600);
      // Verify + one retry: the mode must actually latch.
      if (!isChecked(byText) && input) {
        input.click();
        await waitForRender(600);
      }
      return { content: [{ type: "text", text: `${value}: ${isChecked(byText) ? 'selected (by radio text)' : 'CLICKED BUT NOT CONFIRMED CHECKED - verify the mode'}` }] };
    }
    return { content: [{ type: "text", text: `Radio group not found: ${label || name}` }] };
  }

  const { formItem } = found[0];
  const radioGroup = formItem.querySelector('.ant-radio-group');
  if (!radioGroup) {
    return { content: [{ type: "text", text: `No radio group in: ${label || name}` }] };
  }

  // Find matching radio button by text
  const normalizedValue = value.toLowerCase().trim();
  const wrappers = radioGroup.querySelectorAll('.ant-radio-button-wrapper, .ant-radio-wrapper');

  for (const wrapper of wrappers) {
    const text = wrapper.textContent.trim().toLowerCase();
    if (text === normalizedValue || text.includes(normalizedValue)) {
      // Check if already selected
      if (wrapper.classList.contains('ant-radio-button-wrapper-checked') ||
          wrapper.classList.contains('ant-radio-wrapper-checked')) {
        return { content: [{ type: "text", text: `${label}: already set to ${value}` }] };
      }
      wrapper.click();
      await waitForRender(300);
      return { content: [{ type: "text", text: `${label}: selected ${value}` }] };
    }
  }

  // Also try matching by input value attribute
  const radioInputs = radioGroup.querySelectorAll('input[type="radio"]');
  for (const radio of radioInputs) {
    if (radio.value.toLowerCase() === normalizedValue) {
      radio.click();
      await waitForRender(300);
      return { content: [{ type: "text", text: `${label}: selected ${value}` }] };
    }
  }

  const available = Array.from(wrappers).map(w => w.textContent.trim()).join(', ');
  return { content: [{ type: "text", text: `Radio option "${value}" not found. Available: ${available}` }] };
}


/**
 * Handle any popup dialog — Modal (confirm/warning/info/success/error), Popconfirm, or Notification.
 * Clicks the appropriate button based on action or button_text.
 */
async function handlePopupConfirm({ action, button_text }) {
  // --- 0. Prefer ACTION dialogs (antd Modal.confirm / .ant-modal-confirm) ---
  //    When a plain form Modal sits open (e.g. "Add New Sailing Option") and a
  //    confirm() mounts on top (the duplicate-sailing Warning whose OK
  //    "Create Additional Sailing" force-saves the manual sailing), answering
  //    the WRONG modal clicked the form's disabled footer button and the save
  //    never happened (2026-10-05 live finding). Confirm dialogs always demand
  //    an answer, so they go first.
  let actionWrap = [...document.querySelectorAll('.ant-modal-wrap')]
    .find(w => { const m = w.querySelector('.ant-modal'); return m && isElementVisible(m) && m.querySelector('.ant-modal-confirm'); });
  // A confirm() can mount 1-3s AFTER the action that triggers it (async
  // server round-trip: the duplicate-sailing Warning answers the manual-
  // sailing Create). When nothing actionable is visible yet, WAIT briefly
  // for the dialog instead of falling through to 'No popup dialog found'
  // (2026-10-05: the follow-up ok raced the Warning and clicked a dead
  // button; the flow then hard-stopped).
  if (!actionWrap) {
    const anyVisible = () => [...document.querySelectorAll('.ant-modal-wrap')]
      .some(w => { const m = w.querySelector('.ant-modal'); return m && isElementVisible(m); });
    if (!anyVisible()) {
      for (let i = 0; i < 10; i++) {
        await realWait(500);
        actionWrap = [...document.querySelectorAll('.ant-modal-wrap')]
          .find(w => { const m = w.querySelector('.ant-modal'); return m && isElementVisible(m) && m.querySelector('.ant-modal-confirm'); });
        if (actionWrap || anyVisible()) break;
      }
    }
  }
  // --- 1. Try Ant Design Modal dialogs first (.ant-modal-wrap) ---
  //    Visibility is driven by the inner .ant-modal (isElementVisible) so modals
  //    in custom roots or mid-transition are still handled. (M1)
  const modalWraps = actionWrap ? [actionWrap] : document.querySelectorAll('.ant-modal-wrap');
  for (const wrap of modalWraps) {
    const modal = wrap.querySelector('.ant-modal');
    if (!modal || !isElementVisible(modal)) continue;

    const footer = modalFooterContainer(modal);
    if (!footer) {
      // No footer — try the X close button
      const closeBtn = modal.querySelector('.ant-modal-close');
      if (closeBtn) {
        closeBtn.click();
        await waitForRender(500);
        return { content: [{ type: "text", text: 'Closed modal (no footer)' }] };
      }
      continue;
    }

    const title = (modal.querySelector('.ant-modal-title')?.textContent || '').trim();
    const footerButtons = footer.querySelectorAll('button');

    // If button_text is specified, find the matching button
    if (button_text) {
      const targetLower = button_text.toLowerCase().trim();
      for (const btn of footerButtons) {
        const btnText = (btn.textContent || '').trim().toLowerCase();
        if (btnText === targetLower || btnText.includes(targetLower)) {
          let btnLabel = btn.textContent.trim();
          // Content guard (2026-10-22 live): a blind decline ("No"/"Cancel")
          // must never land on the ETD-vs-CargoReady gate — that modal mounts
          // 1-3s after the Confirm Booking click (past the click's drain
          // window), its "No" CANCELS the user's own confirm, and the booking
          // stayed New Booking through every status poll. Both dates are the
          // user's input: answer Yes (proceed) instead, and say so.
          const bodyText = modal.textContent || '';
          if (/cancel|no/i.test(targetLower)
              && /ETD is more than 14 days after Cargo Ready Date/i.test(bodyText)) {
            const yesBtn = [...footerButtons].find(b => /^yes$/i.test((b.textContent || '').trim()));
            if (yesBtn) {
              yesBtn.click();
              await waitForRender(500);
              return { content: [{ type: "text", text: 'Clicked "Yes" on the ETD-vs-CargoReady-date warning (proceed) — the requested "No" was redirected: it would have CANCELLED the booking confirmation; both dates came from the user\'s own request' }] };
            }
          }
          const wasDisabled = btn.disabled || btn.classList.contains('ant-btn-loading');
          btn.click();
          await waitForRender(500);
          // Post-click absorption (2026-10-05): a modal-footer commit like the
          // sailing modal's Create does its real work ASYNC — the upsert can
          // answer with the duplicate-sailing "Warning" confirm 1-3s later,
          // and the site resets the modal's mode radio (onOk's callback) while
          // the save is still in flight. Returning right after the bare click
          // left the confirm unclicked and the next read saw a disabled
          // Create. So: answer any confirm() that mounts (its primary button
          // "Create Additional Sailing" force-saves), then wait for the
          // clicked modal to actually close before reporting.
          const confirmWrapVisible = () => [...document.querySelectorAll('.ant-modal-wrap')]
            .find(w => { const m = w.querySelector('.ant-modal'); return m && isElementVisible(m) && m.querySelector('.ant-modal-confirm'); });
          const sourceGone = () => {
            const m = wrap.querySelector('.ant-modal');
            return !m || !isElementVisible(m);
          };
          if (wasDisabled) {
            // The click was a no-op; an earlier click's confirm may still be
            // pending — give it time to mount before declaring failure.
            for (let i = 0; i < 10 && !confirmWrapVisible() && !sourceGone(); i++) {
              await realWait(500);
            }
          }
          const answered = [];
          const t0 = Date.now();
          while (Date.now() - t0 < 18000 && answered.length < 3) {
            const cw = confirmWrapVisible();
            if (!cw) {
              if (sourceGone()) break;
              await realWait(500);
              continue;
            }
            const cModal = cw.querySelector('.ant-modal');
            const cTitle = (cModal.querySelector('.ant-modal-title')?.textContent || '').trim();
            const cBtn = cModal.querySelector('.ant-modal-footer .ant-btn-primary')
              || cModal.querySelector('.ant-modal-footer button:last-child');
            if (!cBtn) break; // unreadable confirm — report instead of guessing
            cBtn.click();
            await realWait(1200);
            answered.push(`"${cTitle}" (${cBtn.textContent.trim()})`);
          }
          // wait out the save so the caller's verification reads the final DOM
          for (let i = 0; i < 40 && !sourceGone(); i++) {
            await realWait(500);
          }
          const closed = sourceGone() ? '; modal closed' : '; modal still open — Create failed';
          if (answered.length) {
            return { content: [{ type: "text", text: `Clicked "${btnLabel}" on modal "${title}"; answered confirm ${answered.join(' then ')}${closed}` }] };
          }
          let bstate = wasDisabled ? ' [DISABLED/LOADING - click may be a no-op]' : '';
          if (bstate) {
            // read the modal component's own state to name the disabling cause
            try {
              const fk = Object.keys(btn).find(k => k.startsWith('__reactFiber$') || k.startsWith('__reactInternalInstance$'));
              let f = btn[fk];
              for (let d = 0; f && d < 25; d++) {
                const n = f.stateNode;
                if (n && n.state && ('search_mode' in n.state || 'modalLoading' in n.state)) {
                  bstate += ` (search_mode=${n.state.search_mode}, modalLoading=${n.state.modalLoading})`;
                  break;
                }
                f = f.return;
              }
            } catch (e) {}
          }
          return { content: [{ type: "text", text: `Clicked "${btnLabel}" on modal "${title}"${bstate}${closed}` }] };
        }
      }
      // Also try close (X) button if button_text suggests closing
      if (targetLower === 'close' || targetLower === 'x') {
        const closeBtn = modal.querySelector('.ant-modal-close');
        if (closeBtn) {
          closeBtn.click();
          await waitForRender(500);
          return { content: [{ type: "text", text: `Closed modal "${title}" via X button` }] };
        }
      }
    }

    // Standard OK/Cancel action
    if (action === 'ok' || action === 'confirm' || action === 'yes') {
      const okBtn = footer.querySelector('.ant-btn-primary');
      if (okBtn) {
        okBtn.click();
        await waitForRender(500);
        return { content: [{ type: "text", text: `Confirmed modal "${title}"` }] };
      }
      // Fallback: click last button (some modals only have one)
      const lastBtn = footerButtons[footerButtons.length - 1];
      if (lastBtn) {
        lastBtn.click();
        await waitForRender(500);
        const lstate = lastBtn.disabled || lastBtn.classList.contains('ant-btn-loading') ? ' [DISABLED/LOADING - click may be a no-op]' : '';
        return { content: [{ type: "text", text: `Clicked "${lastBtn.textContent.trim()}" on modal "${title}"${lstate}` }] };
      }
    } else {
      // Cancel / dismiss
      const cancelBtn = footer.querySelector('.ant-btn-default');
      if (cancelBtn) {
        cancelBtn.click();
        await waitForRender(500);
        return { content: [{ type: "text", text: `Dismissed modal "${title}"` }] };
      }
      // Fallback: X button
      const closeBtn = modal.querySelector('.ant-modal-close');
      if (closeBtn) {
        closeBtn.click();
        await waitForRender(500);
        return { content: [{ type: "text", text: `Closed modal "${title}" via X button` }] };
      }
    }
  }

  // --- 2. Try Popconfirm (original behavior) ---
  const popovers = document.querySelectorAll('.ant-popover:not(.ant-popover-hidden)');
  for (const popover of popovers) {
    const popconfirm = popover.querySelector('.ant-popconfirm');
    if (!popconfirm) continue;

    const buttonsContainer = popconfirm.querySelector('.ant-popconfirm-buttons');
    if (!buttonsContainer) continue;

    // If button_text specified, try to match
    if (button_text) {
      const targetLower = button_text.toLowerCase().trim();
      const allBtns = buttonsContainer.querySelectorAll('button');
      for (const btn of allBtns) {
        const btnText = (btn.textContent || '').trim().toLowerCase();
        if (btnText === targetLower || btnText.includes(targetLower)) {
          btn.click();
          await waitForRender(500);
          return { content: [{ type: "text", text: `Clicked "${btn.textContent.trim()}" on popconfirm` }] };
        }
      }
    }

    if (action === 'ok' || action === 'confirm' || action === 'yes') {
      const okBtn = buttonsContainer.querySelector('.ant-btn-primary');
      if (okBtn) {
        okBtn.click();
        await waitForRender(500);
        return { content: [{ type: "text", text: 'Confirmed popconfirm' }] };
      }
    } else {
      const cancelBtn = buttonsContainer.querySelector('.ant-btn-default');
      if (cancelBtn) {
        cancelBtn.click();
        await waitForRender(500);
        return { content: [{ type: "text", text: 'Cancelled popconfirm' }] };
      }
    }
  }

  // --- 3. Try notification/alert close ---
  const notifClose = document.querySelector('.ant-notification-notice-close');
  if (notifClose) {
    notifClose.click();
    await waitForRender(300);
    return { content: [{ type: "text", text: 'Closed notification' }] };
  }

  const alertClose = document.querySelector('.ant-alert-close-icon, .ant-alert-close-text');
  if (alertClose) {
    alertClose.click();
    await waitForRender(300);
    return { content: [{ type: "text", text: 'Closed alert banner' }] };
  }

  return { content: [{ type: "text", text: 'No popup dialog found' }] };
}


/**
 * Dismiss ALL visible popups on the page.
 * For modals: clicks Cancel/X. For popconfirms: clicks Cancel.
 * For notifications/alerts: clicks close button.
 */
async function dismissAllPopups() {
  const results = [];
  const popups = detectPopups();

  if (popups.length === 0) {
    return { content: [{ type: "text", text: 'No popups found on the page' }] };
  }

  // 1. Dismiss Modal dialogs — click Cancel or X
  //    Visibility driven by the inner .ant-modal (isElementVisible). (M1)
  const modalWraps = document.querySelectorAll('.ant-modal-wrap');
  for (const wrap of modalWraps) {
    const modal = wrap.querySelector('.ant-modal');
    if (!modal || !isElementVisible(modal)) continue;

    const title = (modal.querySelector('.ant-modal-title, [class*="confirm-title"]')?.textContent || '').trim();

    // Try Cancel button first
    const cancelBtn = modalFooterContainer(modal)?.querySelector('.ant-btn-default');
    if (cancelBtn) {
      cancelBtn.click();
      results.push(`Closed modal "${title}" (Cancel)`);
      continue;
    }

    // Try X close button
    const closeBtn = modal.querySelector('.ant-modal-close');
    if (closeBtn) {
      closeBtn.click();
      results.push(`Closed modal "${title}" (X)`);
      continue;
    }

    // Last resort: click OK to dismiss
    const okBtn = modalFooterContainer(modal)?.querySelector('.ant-btn-primary');
    if (okBtn) {
      okBtn.click();
      results.push(`Closed modal "${title}" (OK — no cancel available)`);
    }
  }

  // 2. Dismiss Popconfirms — click Cancel
  const popovers = document.querySelectorAll('.ant-popover:not(.ant-popover-hidden)');
  for (const popover of popovers) {
    const popconfirm = popover.querySelector('.ant-popconfirm');
    if (!popconfirm) continue;
    const cancelBtn = popconfirm.querySelector('.ant-popconfirm-buttons .ant-btn-default');
    if (cancelBtn) {
      cancelBtn.click();
      results.push('Dismissed popconfirm');
    }
  }

  // 3. Dismiss notifications and alerts
  document.querySelectorAll('.ant-notification-notice-close').forEach(btn => {
    try { btn.click(); results.push('Closed notification'); } catch (e) { /* ignore */ }
  });
  document.querySelectorAll('.ant-alert-close-icon, .ant-alert-close-text').forEach(btn => {
    try { btn.click(); results.push('Closed alert banner'); } catch (e) { /* ignore */ }
  });

  await waitForRender(500);

  return {
    content: [{
      type: "text",
      text: results.length > 0
        ? `Dismissed ${results.length} popup(s): ${results.join('; ')}`
        : 'No dismissible popups found'
    }]
  };
}


/**
 * Fill a value in an inline-editable table cell.
 */
async function fillTableCell({ row_identifier, column_name, value }) {
  const table = document.querySelector('.ant-table') || document.querySelector('table');
  if (!table) {
    return { content: [{ type: "text", text: 'No table found on this page' }] };
  }

  // Map column headers to indices — exact match first, then fuzzy fallback
  const headers = Array.from(table.querySelectorAll('th, .ant-table-thead th'));
  let colIndex = -1;
  const colLower = column_name.toLowerCase().trim();

  // Exact match
  for (let i = 0; i < headers.length; i++) {
    const headerText = headers[i].textContent.trim().toLowerCase();
    if (headerText === colLower) {
      colIndex = i;
      break;
    }
  }
  // Fuzzy: match on alphanumerics only (so "PO" does NOT match "PORT"/"POD") and
  // require the token to be at least 3 chars to avoid ambiguous short prefixes.
  const colNorm = colLower.replace(/[^A-Za-z0-9]/g, '');
  if (colIndex === -1 && colNorm.length > 0) {
    for (let i = 0; i < headers.length; i++) {
      const headerNorm = headers[i].textContent.trim().toLowerCase().replace(/[^A-Za-z0-9]/g, '');
      if (headerNorm === colNorm) {
        colIndex = i;
        break;
      }
    }
  }

  if (colIndex === -1) {
    const headerNames = headers.map(h => h.textContent.trim()).join(', ');
    return { content: [{ type: "text", text: `Column "${column_name}" not found. Available: ${headerNames}` }] };
  }

  // Find target row
  const rows = table.querySelectorAll('tbody tr, .ant-table-tbody tr');
  // Prefer an EXACT cell-text match (e.g. the row item number "1") so a short
  // identifier never matches a container number or amount that merely contains
  // the digit. Fall back to substring only for longer (>=3 char) identifiers.
  const rowIdLower = String(row_identifier || '').toLowerCase().trim();
  const rowIdNorm = rowIdLower.replace(/[^A-Za-z0-9]/g, '');
  const shortId = rowIdLower.length < 3;
  for (const row of rows) {
    const cells = row.querySelectorAll('td');
    const cellTexts = Array.from(cells).map(c => c.textContent.trim().toLowerCase());
    const exactHit = cellTexts.indexOf(rowIdLower) >= 0;
    const normHit = rowIdNorm.length > 0 && cellTexts.some(ct => ct.replace(/[^A-Za-z0-9]/g, '') === rowIdNorm);
    const fuzzyHit = !shortId && cellTexts.some(ct => ct.includes(rowIdLower));
    if (exactHit || normHit || fuzzyHit) {
      const targetCell = cells[colIndex];
      if (!targetCell) {
        return { content: [{ type: "text", text: `Cell not found at column ${column_name} for row ${row_identifier}` }] };
      }

      // Check if cell is non-editable (prop-driven editable=false renders .tableEditDisplay)
      if (targetCell.querySelector('.tableEditDisplay')) {
        return { content: [{ type: "text", text: `Cell is not editable (table not in edit mode): ${column_name} / ${row_identifier}` }] };
      }

      // Detect cell type and fill — order matters: check specialized inputs first
      const numberInput = targetCell.querySelector('.ant-input-number-input');
      const select = targetCell.querySelector('.ant-select');
      const textarea = targetCell.querySelector('textarea');
      const input = targetCell.querySelector('input:not(.ant-input-number-input)');

      if (numberInput) {
        setReactInputValue(numberInput, value);
        await waitForRender(300);
      } else if (select) {
        await triggerAntdSelectChange(select, value);
      } else if (textarea) {
        setReactInputValue(textarea, value);
        await waitForRender(300);
      } else if (input) {
        setReactInputValue(input, value);
        await waitForRender(300);
      } else {
        return { content: [{ type: "text", text: `Cell has no editable control: ${column_name} / ${row_identifier}` }] };
      }

      return { content: [{ type: "text", text: `Filled ${column_name}="${value}" for row ${row_identifier}` }] };
    }
  }

  return { content: [{ type: "text", text: `Row "${row_identifier}" not found in table` }] };
}


// ==================== Batch / Combined Tools ====================

// Notify-party default patch (create booking): the BMS create endpoint cannot
// bind a null notify_party_id, and with customer "Other" no related-party
// default exists — so the save POST is rejected with "Error converting value
// {null} to type 'System.Int32'. Path 'notify_party_id'". Per BMS convention
// (every existing booking for a consignee uses the consignee itself as notify
// party), rewrite a null notify_party_id to the consignee_party_id carried in
// same payload. URL-scoped to the SO save endpoints (/shipment/...): matching
// payload shape alone also rewrote the double-stringified CONSOL save payload
// and blocked every consol save on UAT 2026-09-28..10-02 (fixed 2026-10-02).
(function installNotifyPartyPatch() {
  window.__notifyPatchInstalled = true;
  // Timestamp of the last SO-save POST emission (url contains /shipment/ and
  // method POST). save_and_confirm uses it to VERIFY its Save click actually
  // reached the server — a click swallowed by a re-render leaves no trace,
  // and the flow would hard-stop on "no popup" while nothing was saved
  // (observed 2026-09-21: restore → click → silence, no POST, no row).
  window.__lastSavePostAt = 0;
  // Arm-window scoping (2026-10-07): the wrapper cannot know the CALLER of an
  // XHR — the chatbot clicks the site's Save and axios creates the request, so
  // by send-time no chatbot frame exists on the stack. Instead every tool run
  // funnels through executeTool, which stamps __bmsWatchUntil; the logic below
  // only runs while that window is live. Outside a chatbot tool the hook is
  // inert: a manual SO or CONSOL save executes with zero chatbot logic (the
  // prototype patch itself stays installed — it is the only observation point
  // — but the open() stamps are two harmless property writes and send() goes
  // straight to origSend).
  window.__bmsWatchUntil = 0;
  // In-flight counter (truth channel, 2026-10-12): while armed, EVERY request
  // increments on send and decrements on load/error/abort, giving
  // waitForQuiet() a network-settled predicate. A tool result assembled while
  // requests are still pending is a stale snapshot — the #1 source of verify
  // races (false failures) and of the LLM re-doing actions it already did.
  window.__bmsInflight = 0;
  const origOpen = XMLHttpRequest.prototype.open;
  const origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.__bmsUrl = String(url || '');
    this.__bmsMethod = String(method || '').toUpperCase();
    return origOpen.call(this, method, url, ...rest);
  };
  XMLHttpRequest.prototype.send = function (body) {
    let counted = false;
    try {
      const chatbotArmed = Date.now() < (window.__bmsWatchUntil || 0);
      if (chatbotArmed) {
        counted = true;
        window.__bmsInflight = (window.__bmsInflight || 0) + 1;
        const dec = () => {
          if (!counted) return;
          counted = false;
          window.__bmsInflight = Math.max(0, (window.__bmsInflight || 0) - 1);
        };
        this.addEventListener('load', dec, { once: true });
        this.addEventListener('error', dec, { once: true });
        this.addEventListener('abort', dec, { once: true });
        // Leak guard: a hung request must not keep the counter >0 forever.
        setTimeout(dec, 60000);
      }
      if (chatbotArmed && this.__bmsMethod === 'POST' && /\/shipment(\/|$)/i.test(this.__bmsUrl)) {
        window.__lastSavePostAt = Date.now();
        // Also capture the save RESPONSE so save_and_confirm can report the
        // committed SO# from the server's own answer: the post-save redirect
        // can be blocked (or land on a list page full of OTHER SO numbers),
        // and the flow's SO capture must read the committed number, not the
        // page. Adding a load listener never clobbers the SPA's axios
        // handlers (2026-09-21: S20260990/S20260991 committed but the flow
        // could not prove it after the leave-confirm froze the page).
        if (!this.__bmsSaveLoadHooked) {
          this.__bmsSaveLoadHooked = true;
          this.addEventListener('load', function () {
            try {
              if (this.status >= 200 && this.status < 300) {
                const m = /"so_no"\s*:\s*"([^"]+)"/.exec(this.responseText || '');
                window.__lastSaveSoNo = m ? m[1] : '';
                window.__lastSaveSoNoAt = Date.now();
              }
            } catch (e) { /* read-only best effort */ }
          });
        }

        // SO-save payloads only (this URL branch): a create/update posting a
        // null notify_party_id together with a numeric consignee_party_id — a
        // shape the backend always rejects. Default the notify to the consignee.
        // Keep this INSIDE the /shipment/ branch: the escaped-key regex also
        // matches the double-stringified CONSOL save payload, and rewriting
        // that corrupted linked shipments' notify and blocked every consol
        // save (UAT 2026-09-28..10-02, C20121/C20124 ported by C20129).
        if (typeof body === 'string' && /notify_party_id\\+":null/.test(body)) {
          const cid = (body.match(/consignee_party_id\\+":(\d+)/) || [])[1];
          if (cid) {
            body = body.replace(/(notify_party_id\\+":)null/, '$1' + cid);
            console.log('[BMS Tools] notify_party_id defaulted to consignee party ' + cid);
          }
        }
      }
    } catch (e) {
      /* never break the save */
      if (counted) {
        counted = false;
        window.__bmsInflight = Math.max(0, (window.__bmsInflight || 0) - 1);
      }
    }
    return origSend.call(this, body);
  };
})();

// Intended header parties of the most recent batch_form_actions
// (label -> value). save_and_confirm re-applies them right before its Save
// click, because the SO header can drop a party selection at any moment via
// its async resetFields chain (observed: Consignee fetch completes late and
// clears the Shipper between the flow's verify step and the Save click).
let _lastHeaderParties = null;
// Resolver party_ids of the same batch (label -> party id): re-applies fetch
// the row exactly instead of re-running name searches.
let _lastHeaderPartyIds = null;
// Timestamp of the most recent fill batch that ended with the header CLEAN
// (all held fields holding through a quiet window — the consignee's async
// wipe chain fully absorbed). save_and_confirm skips its stabilization gate
// when nothing needs repair after this point (2026-10-05).
let _lastFillSettledAt = 0;

// Rendered value text of a found form field, by control type. Business Type /
// Port / Door Delivery are RADIO groups — they have no .ant-select-selection-item,
// so a select-based reader always sees "" and the hold/stabilize checks would
// re-apply (and log drift) every round. Radios read the checked wrapper's text.
function readHoldValueText(found) {
  if (!found || found.length === 0) return '';
  const { formItem, type } = found[0];
  if (type === 'radio') {
    const r = formItem.querySelector('.ant-radio-button-wrapper-checked, .ant-radio-wrapper-checked');
    return r ? (r.textContent || '').trim() : '';
  }
  return (formItem.querySelector('.ant-select-selection-item')?.textContent || '').trim();
}

// Re-apply a held non-party value (Salesman / Business Type) with the tool its
// control actually supports: select_option cannot drive a radio group (it
// silently no-ops — Business Type saved "Freehand" all day on 2026-09-21).
// A select that reports "Could not find option" is a FAILURE (ok:false) — the
// old ok:true-by-default made the stabilize gate re-apply forever.
async function reapplyHoldValue(label, value) {
  const found = findFormFieldsByLabel(label);
  const tool = found.length && found[0].type === 'radio' ? 'select_radio' : 'select_option';
  const r = await executeTool(tool, { label, value });
  const text = (r && r.result?.content?.[0]?.text) || (r && r.error) || 'Done';
  const failed = (r && r.error)
    || /could not find|not found|skipped|disabled/i.test(text);
  return { ok: !failed, via: failed ? 'no-option' : 'ok', text };
}


// ============================================================================
// house_doc_action — dedicated driver for the House Doc tab's Assign Job# /
// Print Draft modals and the Print/Other Actions hover menu (2026-10-06).
//
// LOCAL CHATBOT EDIT (never checked in from the langgraph repo; the CRA dev
// server on :3001 hot-reloads it). Dispatched ONLY via batch_form_actions by
// the deterministic booking_docs flow (bms_flows_docs.py) — same pattern as
// fill_details_field / fill_approval_remarks. The generic tools cannot reach
// these controls:
//   - the modal's Job Country / Job Service Selects and the Projected ETD
//     DatePicker are bare placeholder/adjacent-text widgets OUTSIDE any
//     ant-form-item (findFormFieldsByLabel misses them);
//   - the Auto-assign HBL# Switch lives in the print modal, which portals to
//     document.body (outside the tabpane scan toggle_checkbox falls back to);
//   - the action menu is a hover-triggered Dropdown whose items are
//     li[role=menuitem] (not buttons) and does not open from a plain click;
//   - the modal footer OK commits must NOT absorb follow-up confirms the way
//     handlePopupConfirm does (the assign modal's service "Warning" must be
//     answered "No" by the FLOW, not auto-primary-clicked), and click_button
//     is a mutating tool whose result gets an ACTIVE POPUPS appendix the flow
//     engine treats as unknown-outcome — so this op captures its own evidence
//     and leaves follow-up dialogs open for the flow's own steps.
// Output convention: 'HD <op>: OK — <detail>' on success,
// 'HD <op>: FAILED — <reason>' on failure (the flow's parse gates on FAILED).
// ============================================================================
// Drive one of the Assign-Job modal's Selects (Job Country / Job Service).
// triggerAntdSelectChange's single-click open is not enough there (live
// 2026-10-06 on S20261015: the dropdown stayed hidden after one click, and the
// option list is a VIRTUAL scroll positioned at the select's CURRENT value,
// so options above the fold — AUSTRIA (A) with the booking-derived INTER ASIA
// (Y) selected — never render). Recipe proven by hand: click (again +
// ArrowDown if still closed), page the virtual list from the top, click the
// best word-level match. Returns true, or a short failure reason.
async function driveModalSelect(sel, targetValue) {
  const selector = sel.querySelector('.ant-select-selector');
  if (!selector) return 'no selector';
  const openDropdowns = () => [...document.querySelectorAll(
    '.ant-select-dropdown:not(.ant-select-dropdown-hidden)')];
  selector.click();
  await realWait(350);
  if (!openDropdowns().length) {
    selector.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
    selector.click();
    selector.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowDown', keyCode: 40, bubbles: true }));
    await realWait(450);
  }
  if (!openDropdowns().length) return 'dropdown did not open';

  const target = String(targetValue || '').toLowerCase();
  const words = target.split(/[^a-z0-9]+/).filter(w => w.length >= 2);
  const matchScore = (t) => t.includes(target) ? 2
    : (words.length && words.every(w => t.includes(w))) ? 1 : 0;

  let chosen = null;
  for (const dd of openDropdowns()) {
    const holder = dd.querySelector('.rc-virtual-list-holder');
    if (!holder) continue;
    for (let page = 0; page < 12 && !chosen; page++) {
      holder.scrollTop = page * holder.clientHeight;
      holder.dispatchEvent(new Event('scroll', { bubbles: true }));
      await realWait(220);
      let fallback = null;
      for (const o of dd.querySelectorAll('.ant-select-item-option')) {
        const s = matchScore((o.textContent || '').trim().toLowerCase());
        if (s === 2) { chosen = o; break; }
        if (s === 1 && !fallback) fallback = o;
      }
      if (!chosen && page === 11) chosen = fallback;  // last page: take the word match
    }
    if (chosen) break;
  }
  if (!chosen) return 'no matching option';
  chosen.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
  chosen.click();
  await realWait(500);
  const item = sel.querySelector('.ant-select-selection-item');
  const now = (item && item.textContent || '').toLowerCase();
  return (now && (now.includes(target) || (words.length && words.every(w => now.includes(w)))))
    ? true : 'value did not change';
}

// confirm_manual_sailing — the Confirm Booking tab's manual-sailing STORE
// SYNC (2026-10-21). The tab's sailing/cut-off fields are rc-field-form
// controls: DOM writes (set_date's onChange walk, native input events) update
// the DISPLAY at best, while the site's Confirm gate reads the FORM STORE
// (validateFields) and the SO state (context.js validateSO), which only the
// form's onValuesChange->handleChange sync keeps current (live matrix
// 2026-10-20: cut-offs displayed-but-not-in-store; Confirm died "Please input
// CY/VGM/SI" on every attempt). Write the STORE directly: locate the form
// instance + the Form4 onValuesChange syncer via the React fiber,
// setFieldsValue with moment values cloned from a live picker prop, then run
// the same onValuesChange a user edit fires so the SO state carries them.
function _cmsFiberOf(el) {
  const k = Object.keys(el || {}).find(k => k.startsWith('__reactFiber$') || k.startsWith('__reactInternalInstance$'));
  return k ? el[k] : null;
}

async function confirmManualSailing(params) {
  const fail = (why) => `confirm_manual_sailing: FAILED — ${why}`;
  if (String(params.scope || '') === 'modal') {
    return await _confirmManualSailingModal(params, fail);
  }
  if (String(params.scope || '') === 'dup_resolve') {
    return await _confirmManualSailingDupResolve(params, fail);
  }
  // 1) anchor: any FormItemInput whose name starts with 'sailing_schedule'
  //    (the cut-off Form.Items) — proves we are on the Confirm Booking tab.
  let anchorFiber = null;
  for (const item of document.querySelectorAll('.ant-form-item')) {
    if (!item.offsetParent) continue;
    let cur = _cmsFiberOf(item), d = 0;
    while (cur && d < 30) {
      const name = (cur.memoizedProps || {}).name;
      if (Array.isArray(name) && name[0] === 'sailing_schedule') { anchorFiber = _cmsFiberOf(item); break; }
      cur = cur.return; d++;
    }
    if (anchorFiber) break;
  }
  if (!anchorFiber) return fail('no sailing_schedule form items visible (open the Confirm Booking tab in edit mode first)');
  // 2) walk up: the form instance (memoizedProps.form with getFieldsValue)
  //    and the Form4 onValuesChange sync (memoizedProps.onValuesChange).
  let form = null, onValuesChange = null;
  let cur = anchorFiber, d = 0;
  while (cur && d < 150) {
    const p = cur.memoizedProps || {};
    if (!form && p.form && typeof p.form.getFieldsValue === 'function') form = p.form;
    if (!onValuesChange && typeof p.onValuesChange === 'function') onValuesChange = p.onValuesChange;
    if (form && onValuesChange) break;
    cur = cur.return; d++;
  }
  if (!form) return fail('confirm form instance not found (fiber walk found no form with getFieldsValue)');
  // 3) moment factory: clone from any live DatePicker's showTime.defaultValue
  //    (the page bundles moment internally — no global is exposed).
  let momentTemplate = null;
  for (const pk of document.querySelectorAll('.ant-picker')) {
    if (!pk.offsetParent) continue;
    let c = _cmsFiberOf(pk), i = 0;
    while (c && i < 20) {
      const st = (c.memoizedProps || {}).showTime;
      if (st && st.defaultValue && typeof st.defaultValue.clone === 'function') { momentTemplate = st.defaultValue; break; }
      c = c.return; i++;
    }
    if (momentTemplate) break;
  }
  if (!momentTemplate) return fail('no moment template found on the visible date pickers');
  const mkMoment = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || '').trim());
    if (!m) return null;
    return momentTemplate.clone().year(+m[1]).month(+m[2] - 1).date(+m[3]).hour(0).minute(0).second(0).millisecond(0);
  };
  // 4) build the store values (only the keys the server provided).
  const vals = {};
  const texts = [['vessel', 'master_vessel_name'], ['voyage', 'master_voyage_code'],
                 ['carrier', 'carrier'], ['pol', 'pol'], ['pod', 'pod'],
                 ['etd', 'etd'], ['eta', 'eta'], ['pld_eta', 'pld_eta'], ['fd_eta', 'fd_eta']];
  const dates = [['cyclosing', 'pol_cyclosing'], ['vgmclosing', 'pol_vgm_closing'],
                 ['siclosing', 'pol_si_closing']];
  const ss = {};       // sailing_schedule.* (internal cut-offs)
  const forShipper = {};  // top-level *_for_shipper twins
  for (const [k, field] of texts) {
    if (params[k] !== undefined && params[k] !== '') vals[field] = String(params[k]);
  }
  for (const [k, field] of dates) {
    const m = mkMoment(params[k]);
    if (m) { ss[field] = m; forShipper[field + '_for_shipper'] = m; }
  }
  // PLD/FD ETA default to the POD ETA (the site's auto-update adds 0 days).
  if (vals.eta && params.pld_eta === undefined) vals.pld_eta = vals.eta;
  if (vals.eta && params.fd_eta === undefined) vals.fd_eta = vals.eta;
  if (!Object.keys(vals).length && !Object.keys(ss).length) return fail('nothing to set (no sailing values provided)');
  // top-level date pickers need MOMENT values (strings read back empty
  // through the picker's controlled value)
  if (params.etd && mkMoment(params.etd)) vals.etd = mkMoment(params.etd);
  if (params.eta && mkMoment(params.eta)) vals.eta = mkMoment(params.eta);
  if (vals.eta) { vals.pld_eta = vals.eta; vals.fd_eta = vals.eta; }
  const payload = Object.assign({}, vals, forShipper);
  if (Object.keys(ss).length) payload.sailing_schedule = ss;
  // 5) write the FORM store (validateFields reads it) and sync the SO state
  //    via the site's onValuesChange->handleChange — but NEVER pass the
  //    nested sailing_schedule through handleChange: it would CLOBBER the
  //    fully-attached sailing option in so.sailing_schedule (the option
  //    record from dup_resolve's onSelectSailingSchedule already carries
  //    carrier_scac/pol_etd/pod_eta/vessel/voyage AND the cut-offs — the
  //    confirm gate reads exactly those). manualEntryVessel stays OFF for
  //    the same reason: its merge branch shallow-overwrites
  //    so.sailing_schedule with the form's bare cut-off path. Two passes:
  //    the site's async re-renders wipe form-only values a beat after the
  //    first write (live 2026-10-21: vessel/ETD read back empty on one).
  const stateSyncPayload = Object.assign({}, payload);
  delete stateSyncPayload.sailing_schedule;
  const apply = () => {
    form.setFieldsValue(payload);
    if (onValuesChange) {
      try { onValuesChange(stateSyncPayload, form.getFieldsValue()); } catch (e) { /* non-fatal */ }
    }
  };
  apply();
  await realWait(1500);
  apply();
  // 6) read back and report per-field proof.
  const after = form.getFieldsValue();
  const fmt = (v) => (v && typeof v.format === 'function') ? v.format('YYYY-MM-DD') : (v === null || v === undefined || v === '' ? '' : String(v));
  const bits = [];
  const missed = [];
  for (const [k, field] of texts) {
    if (!(field in vals)) continue;
    const want = (vals[field] && typeof vals[field].format === 'function')
      ? vals[field].format('YYYY-MM-DD') : String(vals[field]);
    if (fmt(after[field]) === want) bits.push(`${field}=${want}`);
    else missed.push(field);
  }
  const afterSS = (after.sailing_schedule || {});
  for (const [k, field] of dates) {
    if (!(field in ss)) continue;
    if (fmt(afterSS[field]) === mkMoment(params[k]).format('YYYY-MM-DD'))
      bits.push(`sailing_schedule.${field}=${fmt(afterSS[field])}`);
    else missed.push('sailing_schedule.' + field);
  }
  const fsCount = Object.keys(forShipper).length;
  return `confirm_manual_sailing: OK — stored ${bits.length} field(s) + ${fsCount} for-shipper twin(s): ` +
    bits.join(', ') +
    (missed.length ? `; MISSED: ${missed.join(', ')}` : '') +
    (onValuesChange ? ' (onValuesChange sync fired)' : ' (WARN: onValuesChange not found — SO state may lag the store)');
}

// scope=modal variant: the "Add New Sailing Option" modal's OWN form store
// (2026-10-21 S20261089: the modal's DOM text/date fills never landed, its
// validation refused Create, and the flow died in recovery before the tab
// sync ever ran). Anchors on the topmost modal's sailing_modal.* Form.Items,
// writes the text/date fields only — the carrier/POL/POD selects keep the
// label-matched select_option path that works.
async function _confirmManualSailingModal(params, fail) {
  // NOTE: modal wraps are position:fixed — offsetParent is null for them
  // (2026-10-21 live S20261089: this filter reported "no open modal" while
  // the popup scan showed the modal right there). Visibility = computed
  // display, with a DOM-order fallback to the LAST wrap (modals portal in
  // open order, so the last one is the topmost).
  const wraps = [...document.querySelectorAll('.ant-modal-wrap')]
    .filter(w => { const m = w.querySelector('.ant-modal'); return m && getComputedStyle(w).display !== 'none'; });
  const wrap = wraps[wraps.length - 1]
    || document.querySelectorAll('.ant-modal-wrap').length
      ? [...document.querySelectorAll('.ant-modal-wrap')].pop() : null;
  if (!wrap) return fail('no open modal to sync (open the Create Manual Sailing modal first)');
  let modalForm = null;
  for (const item of wrap.querySelectorAll('.ant-form-item')) {
    let cur = _cmsFiberOf(item), d = 0;
    while (cur && d < 30) {
      const name = (cur.memoizedProps || {}).name;
      if (Array.isArray(name) && name[0] === 'sailing_modal') break;
      cur = cur.return; d++;
    }
    if (!cur) continue;
    let c = cur, i = 0;
    while (c && i < 150) {
      const p = c.memoizedProps || {};
      if (p.form && typeof p.form.getFieldsValue === 'function') { modalForm = p.form; break; }
      c = c.return; i++;
    }
    break;
  }
  if (!modalForm) return fail('modal form instance not found (no sailing_modal.* items with a form)');
  let momentTemplate = null;
  for (const pk of wrap.querySelectorAll('.ant-picker')) {
    let c = _cmsFiberOf(pk), i = 0;
    while (c && i < 20) {
      const st = (c.memoizedProps || {}).showTime;
      if (st && st.defaultValue && typeof st.defaultValue.clone === 'function') { momentTemplate = st.defaultValue; break; }
      c = c.return; i++;
    }
    if (momentTemplate) break;
  }
  if (!momentTemplate) {
    for (const pk of document.querySelectorAll('.ant-picker')) {
      if (!pk.offsetParent) continue;
      let c = _cmsFiberOf(pk), i = 0;
      while (c && i < 20) {
        const st = (c.memoizedProps || {}).showTime;
        if (st && st.defaultValue && typeof st.defaultValue.clone === 'function') { momentTemplate = st.defaultValue; break; }
        c = c.return; i++;
      }
      if (momentTemplate) break;
    }
  }
  if (!momentTemplate) return fail('no moment template found for the modal pickers');
  const mkMoment = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || '').trim());
    return m ? momentTemplate.clone().year(+m[1]).month(+m[2] - 1).date(+m[3]).hour(0).minute(0).second(0).millisecond(0) : null;
  };
  const sm = {};
  if (params.vessel) sm.create_master_vessel_name = String(params.vessel);
  if (params.voyage) sm.create_master_voyage_code = String(params.voyage);
  const dateMap = [['etd', 'create_pol_etd'], ['eta', 'create_pod_eta'],
                   ['cyclosing', 'create_pol_cyclosing'], ['vgmclosing', 'create_pol_vgm_closing'],
                   ['siclosing', 'create_pol_si_closing']];
  for (const [k, field] of dateMap) {
    const m = mkMoment(params[k]);
    if (m) sm[field] = m;
  }
  if (!Object.keys(sm).length) return fail('nothing to set for the modal');
  // The Create click consumes the modal component's STATE (onOk ->
  // onMoadlOk(this.state.sailingSchedule)), NOT the form store — setFieldsValue
  // alone leaves Create refusing. Find the class instance owning
  // state.sailingSchedule and setState the same patch the field onChange
  // handlers would write (2026-10-21 live S20261089: Create failed twice
  // with the store-only write; this state write is the one that counts).
  modalForm.setFieldsValue({ sailing_modal: sm });  // display + rules
  let modalInstance = null;
  {
    const item0 = wrap.querySelector('.ant-form-item');
    let cur = _cmsFiberOf(item0), d = 0;
    while (cur && d < 200) {
      const sn = cur.stateNode;
      if (sn && typeof sn === 'object' && sn.state && Object.prototype.hasOwnProperty.call(sn.state, 'sailingSchedule')
          && typeof sn.setState === 'function') { modalInstance = sn; break; }
      cur = cur.return; d++;
    }
  }
  if (!modalInstance) return fail('modal component state (sailingSchedule) not found — Create reads state, cannot sync');
  const patch = {};
  if (params.vessel) patch.master_vessel_name = String(params.vessel);
  if (params.voyage) patch.master_voyage_code = String(params.voyage);
  if (mkMoment(params.etd)) patch.pol_etd = mkMoment(params.etd);
  if (mkMoment(params.eta)) patch.pod_eta = mkMoment(params.eta);
  if (mkMoment(params.cyclosing)) patch.pol_cyclosing = mkMoment(params.cyclosing);
  if (mkMoment(params.vgmclosing)) patch.pol_vgm_closing = mkMoment(params.vgmclosing);
  if (mkMoment(params.siclosing)) patch.pol_si_closing = mkMoment(params.siclosing);
  modalInstance.setState({ sailingSchedule: Object.assign({}, modalInstance.state.sailingSchedule || {}, patch) });
  await realWait(600);
  const after = (modalInstance.state.sailingSchedule || {});
  // Snapshot the COMPLETE state while it is intact (carrier_scac + locodes
  // come from the modal's own prefills/Carrier select, the patch from the
  // slots). A refused Create flips the modal to SEARCH mode and WIPES this
  // state — the dup_resolve probe then needs the stash to send a full
  // payload (2026-10-22 live: without it the probe went out without
  // carrier/locodes, matched a blank record, and attaching it left the
  // confirm form sailing-less).
  try { window.__bmsManualSailingSnapshot = Object.assign({}, after); } catch (e) { /* never fail the sync */ }
  const fmt = (v) => (v && typeof v.format === 'function') ? v.format('YYYY-MM-DD') : (v === null || v === undefined || v === '' ? '' : String(v));
  const bits = [], missed = [];
  for (const [field, want] of Object.entries(patch)) {
    const got = fmt(after[field]);
    const wantS = fmt(want);
    (got === wantS ? bits : missed).push(`${field}=${got || '(empty)'}`);
  }
  return `confirm_manual_sailing(modal): ${missed.length ? 'FAILED — MISSED: ' + missed.join(', ') : 'OK — state ' + bits.join(', ') + '; form store synced'}`;
}

// scope=dup_resolve: the manual-sailing Create can be refused because an
// IDENTICAL sailing already exists — the upsert returns
// [{message:'hardstop', sailing_schedule_id:...}] and the site shows only a
// notification (no dialog to answer), leaving the modal open forever
// (2026-10-21 live S20261089: the exact Ever Given/1234-006E sailing from an
// earlier run hardstops every Create). Resolve it the site's own way: probe
// the upsert with the modal's current values, fetch the existing record, and
// attach it via the tab component's onSelectSailingSchedule — which appends
// the option, syncs the SO state and CLOSES the modal.
async function _confirmManualSailingDupResolve(params, fail) {
  const anyModalOpen = () => [...document.querySelectorAll('.ant-modal-wrap')]
    .some(w => getComputedStyle(w).display !== 'none');
  // 0) SOFT-WARNING case first: a duplicate-sailing confirm() on top offers
  //    "Create Additional Sailing" — answer it (footer OK). Without this the
  //    generic popup-confirm fallback clicked Close and killed the modal
  //    before anything could be resolved (2026-10-21 live S20261089).
  const confirmWrap = [...document.querySelectorAll('.ant-modal-wrap')]
    .find(w => { const m = w.querySelector('.ant-modal'); return m && getComputedStyle(w).display !== 'none' && m.querySelector('.ant-modal-confirm'); });
  if (confirmWrap) {
    const okBtn = confirmWrap.querySelector('.ant-modal-confirm-btns .ant-btn-primary, .ant-modal-confirm-btns button:last-child');
    if (okBtn) {
      okBtn.click();
      await realWait(2500);
      if (!anyModalOpen()) {
        return 'confirm_manual_sailing(dup_resolve): OK — answered the duplicate-sailing warning (create additional); modal closed';
      }
    }
  }
  if (!anyModalOpen()) {
    return 'confirm_manual_sailing(dup_resolve): OK — no modal open, nothing to resolve';
  }
  const fk = (el) => { const k = Object.keys(el || {}).find(k => k.startsWith('__reactFiber$') || k.startsWith('__reactInternalInstance$')); return k ? el[k] : null; };
  // Instance hunt (2026-10-21 live: after the Create click the modal flips
  // to SEARCH mode, so form-item name anchors vanish and the fiber walk from
  // them runs dry before the tab component). Anchor on the modal ELEMENT
  // itself: its chain reaches the modal component (state.sailingSchedule)
  // and, further up, the tab component (onSelectSailingSchedule) — one
  // chain, 400 hops.
  const wrapsAll = [...document.querySelectorAll('.ant-modal-wrap')]
    .filter(w => getComputedStyle(w).display !== 'none');
  const modalEl = wrapsAll.length ? wrapsAll[wrapsAll.length - 1].querySelector('.ant-modal') : null;
  let modalInst = null, tabInst = null;
  const roots = [];
  if (modalEl) roots.push(fk(modalEl));
  for (const item of document.querySelectorAll('.ant-form-item')) {
    if (roots.length >= 4) break;
    const f = fk(item);
    if (f) roots.push(f);
  }
  for (const root of roots) {
    let c = root, i = 0;
    while (c && i < 400) {
      const sn = c.stateNode;
      if (sn && typeof sn === 'object' && typeof sn.setState === 'function') {
        if (!modalInst && sn.state && Object.prototype.hasOwnProperty.call(sn.state, 'sailingSchedule')) modalInst = sn;
        if (!tabInst && typeof sn.onSelectSailingSchedule === 'function' && sn.state && Object.prototype.hasOwnProperty.call(sn.state, 'showModal')) tabInst = sn;
      }
      if (modalInst && tabInst) break;
      c = c.return; i++;
    }
    if (modalInst && tabInst) break;
  }
  if (!tabInst) return fail('sailing tab component (onSelectSailingSchedule) not found');
  if (!modalInst) return fail('modal component state (sailingSchedule) not found — cannot probe the duplicate');
  // Probe with the INTENDED sailing values, not the modal's current state
  // (2026-10-22 live: the refused Create flips the modal to SEARCH mode and
  // WIPES its sailingSchedule state, so a state-only probe went out EMPTY,
  // matched a blank record and attached it — the confirm form then had no
  // real sailing and Confirm Booking silently no-op'd; the booking never
  // reached Booking Confirmed). Base: the pre-Create SNAPSHOT stashed by the
  // modal sync (carrier_scac + locodes + synced values) when the live state
  // is wiped; the live state contributes when it still has a core value; the
  // action's own slot values (build_manual_sailing_dup_resolve_actions) win.
  const live = (modalInst.state && modalInst.state.sailingSchedule) || {};
  const snapshot = (typeof window !== 'undefined' && window.__bmsManualSailingSnapshot) || null;
  const liveHasCore = !!(String(live.master_vessel_name || '').trim()
                         || String(live.pol_etd || '').trim());
  const pickNonNull = (src) => {
    const o = {};
    for (const [k, v] of Object.entries(src || {})) {
      if (v !== null && v !== undefined && v !== '') o[k] = v;
    }
    return o;
  };
  const ss = Object.assign({}, (liveHasCore ? {} : (snapshot || {})), pickNonNull(live));
  if (params.vessel) ss.master_vessel_name = params.vessel;
  if (params.voyage) ss.master_voyage_code = params.voyage;
  if (params.etd) ss.pol_etd = params.etd;
  if (params.eta) ss.pod_eta = params.eta;
  if (params.cyclosing) ss.pol_cyclosing = params.cyclosing;
  if (params.vgmclosing) ss.pol_vgm_closing = params.vgmclosing;
  if (params.siclosing) ss.pol_si_closing = params.siclosing;
  const dateStr = (v) => {
    if (!v) return null;
    if (typeof v.format === 'function') return v.format('YYYY-MM-DD') + 'T00:00:00';
    const m = /(\d{4}-\d{2}-\d{2})/.exec(String(v));
    return m ? m[1] + 'T00:00:00' : null;
  };
  const payload = {
    sailingSchedule: {
      sailing_schedule_id: ss.sailing_schedule_id || null,
      carrier_scac: ss.carrier_scac, master_vessel_name: ss.master_vessel_name,
      master_voyage_code: ss.master_voyage_code,
      pol_locode: ss.pol_locode, pod_locode: ss.pod_locode,
      pol_etd: dateStr(ss.pol_etd), pod_eta: dateStr(ss.pod_eta),
      pol_cyclosing: dateStr(ss.pol_cyclosing),
      pol_vgm_closing: dateStr(ss.pol_vgm_closing),
      pol_si_closing: dateStr(ss.pol_si_closing),
      transshipment_count: ss.transshipment_count || 0,
    },
  };
  // --- site-API auth for the raw probe fetches (2026-10-23 live 401) -----
  // The probe bypasses the site's Ajax wrapper, so it must reproduce what
  // that wrapper does on EVERY call: the gateway's Mr-Token header and the
  // SITE session JWT. The old pickup sent the FIRST eyJ* value found in
  // localStorage — on a long-lived session that can be the cargosphere
  // token (wrong audience) or an id_token the site has since renewed
  // through axios (a raw fetch never sees the renewal), and the upsert
  // answered HTTP 401 (live S202610124: the whole confirm flow died in
  // recovery over it). Pick id_token deterministically, validate the JWT
  // (site tokens carry iss=mrspedag.com + exp), and on a 401 renew ONCE
  // through the site's own /api/v1/Auth/renewtoken — mirroring
  // AuthService.renewIfNeeded/setToken — before retrying.
  const MR_TOKEN = 'CAE983C3-7320-46C8-9F58-6C87DBF66EE9';
  const jwtPayload = (t) => {
    try {
      const part = String(t).split('.')[1];
      return JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    } catch (e) { return null; }
  };
  const pickSiteToken = () => {
    const idt = localStorage.getItem('id_token') || '';
    const nowS = Date.now() / 1000;
    const unexpired = (t) => {
      const pl = jwtPayload(t);
      return !!pl && (typeof pl.exp !== 'number' || pl.exp > nowS + 30);
    };
    if (idt.startsWith('eyJ') && unexpired(idt)) return idt;
    for (const k of Object.keys(localStorage)) {
      const v = localStorage.getItem(k) || '';
      if (!v.startsWith('eyJ') || v.length <= 100 || v === idt) continue;
      const pl = jwtPayload(v);
      if (!pl || pl.iss !== 'mrspedag.com') continue; // skip foreign JWTs (cargosphere etc.)
      if (unexpired(v)) return v;
    }
    // id_token even when stale — the renew attempt below may refresh it.
    return idt.startsWith('eyJ') ? idt : null;
  };
  let token = pickSiteToken();
  if (!token) return fail('no site session token in localStorage — log into the website and retry');
  const siteApiHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + token,
    'Mr-Token': MR_TOKEN,
  });
  let renewedNote = '';
  const renewSiteToken = async () => {
    try {
      const r = await fetch('/api/v1/Auth/renewtoken', { method: 'POST', headers: siteApiHeaders() });
      if (!r.ok) return false;
      const dat = await r.json();
      const nt = dat && dat.token;
      if (!nt || !jwtPayload(nt)) return false;
      localStorage.setItem('id_token', nt); // same as AuthService.setToken
      token = nt;
      renewedNote = ' (site token renewed after 401)';
      return true;
    } catch (e) { return false; }
  };
  const apiFetch = async (url, opts) => {
    let r = await fetch(url, Object.assign({}, opts, { headers: siteApiHeaders() }));
    if (r.status === 401 && await renewSiteToken()) {
      r = await fetch(url, Object.assign({}, opts, { headers: siteApiHeaders() }));
    }
    return r;
  };
  try {
    const r = await apiFetch('/api/v1/sailingschedule/upsert', { method: 'POST', body: JSON.stringify(payload) });
    if (!r.ok) return fail('upsert probe HTTP ' + r.status + renewedNote
      + (r.status === 401 ? ' — the website login may have expired; re-login and ask again' : ''));
    const resDat = await r.json();
    // A plain id/string = the probe CREATED the sailing (nothing duplicated):
    // attach that fresh one. An array = duplicate rows: attach the existing.
    let row = null;
    if (Array.isArray(resDat) && resDat.length && resDat[0] && resDat[0].sailing_schedule_id) {
      row = resDat[0];
    } else if (typeof resDat === 'string' && resDat) {
      row = { sailing_schedule_id: resDat, master_vessel_name: (payload.sailingSchedule.master_vessel_name || ''), master_voyage_code: (payload.sailingSchedule.master_voyage_code || '') };
    }
    if (!row) {
      return 'confirm_manual_sailing(dup_resolve): OK — upsert returned nothing actionable (' + JSON.stringify(resDat).slice(0, 80) + ')';
    }
    const r2 = await apiFetch('/api/v1/SailingSchedule/all/?sailing_schedule_id=' + encodeURIComponent(row.sailing_schedule_id), {});
    if (!r2.ok) return fail('record fetch HTTP ' + r2.status);
    const record = await r2.json();
    // Never attach a BLANK record (2026-10-22 live: an empty probe matched a
    // vessel-less row and the confirm form ended up with no real sailing —
    // Confirm Booking silently no-op'd and the booking never confirmed). A
    // record without vessel AND voyage cannot be the intended sailing.
    if (!(String(record.master_vessel_name || '').trim()
          || String(record.master_voyage_code || '').trim())) {
      return fail('the duplicate probe returned a BLANK record ('
        + JSON.stringify(record).slice(0, 120) + ') — refusing to attach it; '
        + 'the modal state was likely wiped after the refused Create');
    }
    await tabInst.onSelectSailingSchedule(Object.assign({}, record));
    await realWait(1200);
    // The attach updates the SO state, but the confirm form's OWN
    // "Selected Sailing Option" field can still be empty at validateFields
    // time (its value flows through re-rendered initialValues) — write the
    // form store directly so the required rule passes.
    let formSet = 'form not found';
    for (const item of document.querySelectorAll('.ant-form-item')) {
      if (!item.offsetParent) continue;
      let cur = fk(item), d = 0;
      while (cur && d < 30) {
        const name = (cur.memoizedProps || {}).name;
        if (name === 'sailing_schedule_id') break;
        cur = cur.return; d++;
      }
      if (!cur) continue;
      let c = cur, i = 0;
      while (c && i < 150) {
        const p = c.memoizedProps || {};
        if (p.form && typeof p.form.getFieldsValue === 'function') {
          p.form.setFieldsValue({ sailing_schedule_id: row.sailing_schedule_id });
          formSet = 'form sailing_schedule_id=' + row.sailing_schedule_id;
          break;
        }
        c = c.return; i++;
      }
      break;
    }
    return 'confirm_manual_sailing(dup_resolve): OK — attached existing sailing ' +
      row.sailing_schedule_id + ' (' + row.master_vessel_name + ' / ' + row.master_voyage_code + '); modal ' +
      (anyModalOpen() ? 'STILL OPEN' : 'closed') + '; ' + formSet;
  } catch (e) {
    return fail('upsert/attach failed: ' + (e && e.message ? e.message : String(e)));
  }
}

async function houseDocAction(params) {
  const op = String(params.op || '').trim().toLowerCase();
  const fail = (why) => ({ content: [{ type: 'text', text: `HD ${op || '(none)'}: FAILED — ${why}` }] });
  const ok = (detail) => ({ content: [{ type: 'text', text: `HD ${op}: OK — ${detail}` }] });

  // Topmost VISIBLE modal (modals portal to body; the last open wrap is on top)
  const openModalWrap = () => {
    const wraps = [...document.querySelectorAll('.ant-modal-wrap')].filter(w => {
      const m = w.querySelector('.ant-modal'); return m && isElementVisible(m);
    });
    return wraps.length ? wraps[wraps.length - 1] : null;
  };

  // ---- menu: open the hover Dropdown, click a Menu.Item by text -----------
  if (op === 'menu') {
    const btnText = String(params.button || 'Print/Other Actions').toLowerCase();
    const itemText = String(params.item || '').toLowerCase();
    if (!itemText) return fail('no menu item given');
    const btn = [...document.querySelectorAll('button, [role="button"]')]
      .filter(b => b.offsetParent !== null)
      .find(b => (b.textContent || '').trim().toLowerCase().includes(btnText));
    if (!btn) return fail(`button "${params.button}" not found`);
    // trigger=['hover']: dispatch the mouse events antd's Dropdown listens for
    btn.dispatchEvent(new MouseEvent('mouseover', { bubbles: true }));
    btn.dispatchEvent(new MouseEvent('mouseenter', { bubbles: false }));
    // Candidate set: antd dropdown MENU ITEMS plus BUTTONS inside any open
    // overlay — the site's "Print/Other Actions" menu renders plain buttons,
    // not role=menuitem rows (2026-10-13: the flow's menu op failed "item
    // not found" while the same item was clickable as a button a second
    // later; the interactive recovery had to open the menu by hand).
    const findItem = () => [...document.querySelectorAll(
        '.ant-dropdown:not(.ant-dropdown-hidden) li[role="menuitem"], ' +
        '.ant-dropdown:not(.ant-dropdown-hidden) .ant-dropdown-menu-item, ' +
        '.ant-dropdown:not(.ant-dropdown-hidden) button, ' +
        '.ant-popover:not(.ant-popover-hidden) button, ' +
        '[role="menu"]:not(.ant-dropdown-hidden) button')]
      .filter(li => li.offsetParent !== null)
      .find(li => {
        const t = (li.textContent || '').trim().toLowerCase();
        return t === itemText || t.startsWith(itemText);
      });
    let item = null;
    for (let i = 0; i < 20 && !item; i++) {   // up to ~3s for the overlay
      await realWait(150);
      item = findItem();
    }
    if (!item) {
      // one retry with a second hover pass (the overlay can close on miss)
      btn.dispatchEvent(new MouseEvent('mouseover', { bubbles: true }));
      await realWait(400);
      item = findItem();
    }
    if (!item) {
      // final pass: CLICK the trigger (some menus are click-to-open, not
      // hover) and re-scan the candidate set once more.
      btn.click();
      for (let i = 0; i < 10 && !item; i++) {
        await realWait(200);
        item = findItem();
      }
    }
    if (!item) return fail(`menu item "${params.item}" not found in the "${params.button}" dropdown`);
    item.click();
    await realWait(600);
    return ok(`clicked "${params.item}" via the "${params.button}" menu`);
  }

  // ---- footer: click a button in the topmost modal's footer, capture proof -
  if (op === 'footer') {
    const want = String(params.button || 'OK').toLowerCase().trim();
    let wrap = null, btn = null;
    for (let i = 0; i < 34 && !btn; i++) {    // up to ~5s for the modal/confirm to mount
      wrap = openModalWrap();
      btn = wrap && [...wrap.querySelectorAll(
          '.ant-modal-footer button, .ant-modal-confirm-btns button')]
        .find(b => b.offsetParent !== null
                   && (b.textContent || '').trim().toLowerCase() === want);
      if (!btn) await realWait(150);
    }
    if (!btn) return fail(`footer button "${params.button}" not found in an open modal`);
    const title = (wrap.querySelector(
      '.ant-modal-title, .ant-modal-header-title, [class*="confirm-title"]'
    )?.textContent || '').trim();
    btn.click();
    // capture the commit's own evidence (toast/notification/follow-up confirm)
    // but do NOT absorb the confirm — the flow answers it deliberately.
    const r = await captureActionResult(params.button, 6000);
    // 2026-10-13 print-commit race (wait_close): the print-draft OK runs an
    // async chain (validate -> auto-assign HBL# -> save) that can outlive the
    // 6s capture window while the modal sits open mid-flight — the flow's
    // verify then read a PRE-commit page and reported failure although the
    // print landed moments later (S20261046 / S20261047). Only the flow's
    // print step sets wait_close.
    // 2026-10-23 live repro (S202610128 / S202610130): the chain takes 5-10s+
    // with ZERO page feedback (no spinner, no toast until it lands), and the
    // doc-list label ('#1-HKJEA2611A13 [Draft Printed]') can lag the toast by
    // seconds more — the old 15s close-only wait exhausted into a bare
    // "RESULT: unknown" while the commit was still in flight, and the stale
    // first-sample toast could name the PREVIOUS step's success. Wait up to
    // 30s for the modal to close OR the commit's own proof (a FRESH toast or
    // the HBL# doc-list entry), sample evidence every poll, and on timeout
    // report the live doc-list state so the flow's verify gates on ground
    // truth instead of an empty unknown.
    let extra = '';
    if (params.wait_close && want === 'ok') {
      const docListState = () => {
        const t = (document.querySelector('main')?.innerText || '');
        return (t.match(/#[^\n]{0,60}/g) || []).join(' | ').slice(0, 160);
      };
      const commitProof = () =>
        /#\d+-[A-Z0-9]{6,}/.test(docListState()) || /\[Draft Printed\]/i.test(docListState());
      const freshToast = () => [...document.querySelectorAll('.ant-message-notice, .ant-notification-notice')]
        .filter(n => !n.dataset.__bmsCaptured)
        .map(n => (n.innerText || '').replace(/\s+/g, ' ').trim())
        .filter(Boolean).join('; ');
      let closed = !openModalWrap();
      let landed = commitProof();
      let lateEvidence = freshToast();
      for (let i = 0; i < 60 && !(closed || landed); i++) {
        await realWait(500);
        closed = !openModalWrap();
        landed = commitProof();
        if (!lateEvidence) lateEvidence = freshToast();
      }
      if (closed || landed) {
        extra = `; modal ${closed ? 'closed' : 'still open'} (commit completed`
          + `${landed ? ', HBL# visible on the doc list' : ''})`
          + `${lateEvidence ? `, late: ${lateEvidence.slice(0, 200)}` : ''}`;
      } else {
        extra = `; modal still open after 30s and no HBL# on the doc list `
          + `(state: ${docListState()}) — the commit did not land`;
      }
    }
    return { content: [{ type: 'text', text:
      `HD footer: clicked "${params.button}" on "${title || '(no title)'}". RESULT: ${r.outcome} (${r.evidence})${extra}` }] };
  }

  // ---- select / date / toggle: modal-scoped control drivers ---------------
  if (op === 'select' || op === 'date' || op === 'toggle') {
    let wrap = openModalWrap();
    for (let i = 0; i < 14 && !wrap; i++) {   // modal may still be mounting
      await realWait(150);
      wrap = openModalWrap();
    }
    if (!wrap) return fail('no open modal to drive');

    if (op === 'select') {
      const ph = String(params.placeholder || '').toLowerCase();
      const visSelects = [...wrap.querySelectorAll('.ant-select')]
        .filter(s => s.offsetParent !== null);
      let sel = visSelects.find(s => {
        const t = (s.querySelector('.ant-select-selection-placeholder')?.textContent || '')
          .trim().toLowerCase();
        return t && (t === ph || t.includes(ph));
      });
      // Fallback: an already-VALUED select shows no placeholder — the assign
      // modal PRE-FILLS Job Country/Job Service from the booking (e.g. a DXB
      // booking derives "INTER ASIA (Y)"), so a placeholder-only lookup can
      // never re-target them (2026-10-06 live finding on S20261015). Accept a
      // 1-based position among the modal's visible selects when given.
      const pos = parseInt(params.position, 10);
      if (!sel && Number.isInteger(pos) && pos >= 1 && pos <= visSelects.length) {
        sel = visSelects[pos - 1];
      }
      if (!sel) return fail(`Select with placeholder "${params.placeholder}" not found in the modal`);
      const done = await driveModalSelect(sel, String(params.value || ''));
      return done === true ? ok(`selected ${params.value} in "${params.placeholder}"`)
                           : fail(`could not select "${params.value}" in "${params.placeholder}" (${done})`);
    }

    if (op === 'date') {
      const label = String(params.label || '').toLowerCase();
      // The site's Projected-ETD picker only accepts dates between TOMORROW
      // and the end of the month three months out (Common.js
      // getDisabledPOLETDInitial); a date outside that window renders as a
      // disabled cell and antd silently drops typed values (live 2026-10-06:
      // the spec's 2026-09-18 had passed). Fail loudly with the window so the
      // flow can tell the user instead of looping on a silent rejection.
      if (/projected etd/.test(label)) {
        const t = new Date(String(params.value || ''));
        if (!isNaN(t.getTime())) {
          const now = new Date();
          const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
          const end = new Date(now.getFullYear(), now.getMonth() + 3, 0, 23, 59, 59);
          if (t < start || t > end) {
            const f = (d) => d.toISOString().slice(0, 10);
            return fail(`Projected ETD ${params.value} is outside the allowed window (${f(start)} .. ${f(end)} — the site only accepts a Projected ETD between tomorrow and the end of the month three months out). Give a date inside that window.`);
          }
        }
      }
      const candidates = [...wrap.querySelectorAll('.ant-picker')]
        .filter(p => p.offsetParent !== null && !p.querySelector('.ant-picker-disabled'));
      // the picker sits next to (or right after) the text element naming it
      let picker = null;
      for (const el of wrap.querySelectorAll('*')) {
        const t = (el.textContent || '').trim().toLowerCase();
        if (el.children.length === 0 && label && t && t.includes(label)) {
          let sib = el;
          for (let d = 0; d < 4 && sib && !picker; d++) {
            sib = sib.nextElementSibling || (sib.parentElement && sib.parentElement.nextElementSibling);
            if (!sib) break;
            const p = sib.matches && sib.matches('.ant-picker') ? sib
              : (sib.querySelector ? sib.querySelector('.ant-picker') : null);
            if (p && candidates.includes(p)) picker = p;
          }
          if (picker) break;
        }
      }
      // Fallback: the label can be RAW TEXT wrapping the picker ("Projected
      // ETD <DatePicker/>" renders one small element whose entire text is the
      // label and whose child IS the picker) — no leaf element carries the
      // text for the sibling scan above (live 2026-10-06 on S20261015).
      if (!picker) {
        for (const p of candidates) {
          let n = p;
          for (let d = 0; d < 4 && n && !picker; d++) {
            n = n.parentElement;
            const t = n && n.textContent ? n.textContent.trim() : '';
            if (n && t.length > 0 && t.length < 80
                && t.toLowerCase().includes(label)
                && n.querySelectorAll('.ant-picker').length === 1) {
              picker = p;
            }
          }
          if (picker) break;
        }
      }
      if (!picker) {
        // A DISABLED picker near the label means the field is locked because a
        // value is already saved on the booking — the Projected ETD locks once
        // pol_etd_initial is saved and a Job#/HBL# exists (HouseDoc.js:5106) —
        // report a skip, not a failure, so a reprint run stays green
        // (2026-10-13).
        const disabledPickers = [...wrap.querySelectorAll('.ant-picker')]
          .filter(p => p.offsetParent !== null && p.querySelector('.ant-picker-disabled'));
        for (const p of disabledPickers) {
          let n = p;
          for (let d = 0; d < 4 && n; d++) {
            n = n.parentElement;
            const t = n && n.textContent ? n.textContent.trim() : '';
            if (n && t.length > 0 && t.length < 80
                && t.toLowerCase().includes(label)
                && n.querySelectorAll('.ant-picker').length === 1) {
              return ok(`"${params.label}" picker is disabled — a value is already saved on the booking; skipped`);
            }
          }
        }
        // OPTIONAL date fills (engine's opportunistic job_ok batch, 2026-10-23
        // S202610139): the Assign Job# modal renders the Projected ETD picker
        // only for TBC sailings (HouseDoc.js:7462-7475) — on a KNOWN-sailing
        // booking the picker legitimately does not exist and the OK must still
        // commit. Skip quietly; non-optional fills stay loud FAILED so a real
        // TBC miss still hard-stops its gate.
        if (params.optional) {
          return ok(`no "${params.label}" picker in the modal — not applicable here (non-TBC sailing); skipped`);
        }
        return fail(`DatePicker for "${params.label}" not found in the modal`);
      }
      const input = picker.querySelector('input');
      if (!input) return fail(`DatePicker for "${params.label}" has no input`);
      const iso = String(params.value || '');
      // open the panel the reliable way (a bare picker click often only
      // focuses — live 2026-10-06), then commit by clicking the day cell;
      // typed-text + Enter is the fallback
      input.focus();
      input.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowDown', keyCode: 40, bubbles: true }));
      await realWait(500);
      let dd = document.querySelector('.ant-picker-dropdown:not(.ant-picker-dropdown-hidden)');
      let committed = false;
      if (dd) {
        const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        const [y, m] = iso.split('-').map(Number);
        for (let hop = 0; hop < 26; hop++) {
          const view = dd.querySelector('.ant-picker-header-view');
          const mm = view ? (view.textContent || '').trim().match(/([A-Za-z]{3})\s*(\d{4})/) : null;
          if (!mm) break;
          const vi = MONTHS.findIndex(x => x === mm[1]) + Number(mm[2]) * 12;
          const ti = (m - 1) + y * 12;
          if (vi === ti) break;
          const nav = dd.querySelector(vi < ti ? '.ant-picker-header-next-btn' : '.ant-picker-header-prev-btn');
          if (!nav) break;
          nav.click();
          await realWait(260);
          dd = document.querySelector('.ant-picker-dropdown:not(.ant-picker-dropdown-hidden)') || dd;
        }
        const cell = Array.from(dd.querySelectorAll('.ant-picker-cell'))
          .find(c => c.getAttribute('title') === iso
                     && !c.classList.contains('ant-picker-cell-disabled'));
        if (cell) {
          const inner = cell.querySelector('.ant-picker-cell-inner') || cell;
          const r = inner.getBoundingClientRect();
          for (const type of ['mousedown', 'mouseup', 'click']) {
            inner.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true, clientX: r.left + r.width / 2, clientY: r.top + r.height / 2, button: 0 }));
          }
          await realWait(600);
          committed = (input.value || '').trim() === iso;
        }
      }
      if (!committed) {
        picker.click();
        await realWait(300);
        input.focus();
        const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
        setter.call(input, iso);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        await realWait(200);
        input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', keyCode: 13, bubbles: true }));
        input.blur();
        await realWait(400);
        committed = (input.value || '').trim() === iso;
      }
      return committed ? ok(`set ${params.label} = ${iso}`)
                       : fail(`could not set ${params.label} = ${iso} (value did not stick)`);
    }

    if (op === 'toggle') {
      const label = String(params.label || '').toLowerCase();
      const want = !!params.checked;
      // find the small text element carrying the label, then the sibling switch
      let sw = null;
      for (const el of wrap.querySelectorAll('*')) {
        const t = (el.textContent || '').trim().toLowerCase();
        if (el.children.length <= 1 && label && t && t.includes(label)) {
          let sib = el;
          for (let d = 0; d < 4 && sib && !sw; d++) {
            sib = sib.nextElementSibling || (sib.parentElement && sib.parentElement.nextElementSibling);
            if (!sib) break;
            const s = sib.matches && sib.matches('.ant-switch') ? sib
              : (sib.querySelector ? sib.querySelector('.ant-switch') : null);
            if (s && s.offsetParent !== null) sw = s;
          }
          if (sw) break;
          // 2026-10-13: the print modal lays the switch BEFORE/BESIDE the label
          // (no forward sibling carries it) — walk ancestors and each ancestor's
          // full sibling subtree, forward AND backward, before giving up.
          let anc = el.parentElement;
          for (let d = 0; d < 5 && anc && !sw; d++) {
            const scope = anc.parentElement || anc;
            for (const cand of scope.querySelectorAll('.ant-switch')) {
              if (cand.offsetParent !== null) { sw = cand; break; }
            }
            if (!sw && anc.previousElementSibling) {
              const s = anc.previousElementSibling.matches('.ant-switch') ? anc.previousElementSibling
                : anc.previousElementSibling.querySelector('.ant-switch');
              if (s && s.offsetParent !== null) sw = s;
            }
            anc = anc.parentElement;
          }
          if (sw) break;
        }
      }
      if (!sw) {
        // 2026-10-13 (b): the label's smallest carrier can be a multi-child
        // wrapper (the modal concatenates field texts). When the label IS in
        // the modal and exactly ONE visible switch exists, it can only be
        // the target — the print modal's Auto-assign HBL# case.
        const labelInModal = (wrap.textContent || '').toLowerCase().includes(label);
        const visible = [...wrap.querySelectorAll('.ant-switch')]
          .filter(s => s.offsetParent !== null);
        if (labelInModal && visible.length === 1) sw = visible[0];
      }
      if (!sw) return fail(`Switch for "${params.label}" not found in the modal`);
      const isOn = sw.classList.contains('ant-switch-checked') || sw.getAttribute('aria-checked') === 'true';
      if (isOn === want) return ok(`"${params.label}" already ${want ? 'ON' : 'OFF'}`);
      sw.click();
      await realWait(400);
      const nowOn = sw.classList.contains('ant-switch-checked') || sw.getAttribute('aria-checked') === 'true';
      return nowOn === want ? ok(`switched "${params.label}" ${want ? 'ON' : 'OFF'}`)
                            : fail(`could not switch "${params.label}" to ${want ? 'ON' : 'OFF'}`);
    }
  }

  return fail(`unknown op "${params.op}" (use select|date|toggle|menu|footer)`);
}


/**
 * Execute multiple form actions in a single call.
 * Avoids multiple LLM round-trips by batching all operations client-side.
 */
async function batchFormActions({ actions }) {
  // Agent selects must run after the Consignee select (its React re-render
  // clears agent fields). The deterministic flows already emit the site's
  // visual order (Shipper, Consignee, Service, Business Type, Salesman,
  // Nomination Agent, Release Agent, Handling Office), so only agents that
  // appear BEFORE the Consignee are moved — to the end, before the trailing
  // read_form. Agents already after the Consignee keep their position:
  // reordering them to the very end would push Handling Office ahead of them
  // against the flow's intended fill order. A trailing read_form stays LAST.
  const agentKeywords = ['nomination agent', 'release agent'];
  const isAgentAction = (a) =>
    a.action === 'select_option' &&
    agentKeywords.some(kw => (a.label || '').toLowerCase().includes(kw));
  const isReadAction = (a) => a.action === 'read_form';
  const isConsigneeAction = (a) =>
    a.action === 'select_option' && (a.label || '').toLowerCase().includes('consignee');
  const consigneeIdx = actions.findIndex(isConsigneeAction);

  const kept = [], movedAgents = [], reads = [];
  actions.forEach((a, idx) => {
    if (isReadAction(a)) { reads.push(a); return; }
    if (consigneeIdx > -1 && isAgentAction(a) && idx < consigneeIdx) { movedAgents.push(a); return; }
    kept.push(a);
  });
  const reordered = [...kept, ...movedAgents, ...reads];

  const results = [];
  const partyLabels = ['shipper', 'consignee', 'nomination agent', 'release agent'];
  // Salesman / Business Type ride the same hold+repair machinery as the
  // parties: the site's async party callbacks re-default Business Type
  // (2026-09-21: every chatbot create saved Business Type = Freehand while
  // the required default is Nomination) and prefill Salesman from the
  // signed-in user. The flows set both explicitly; save_and_confirm holds
  // and re-applies them right up to the Save click.
  const holdLabels = partyLabels.concat(['salesman', 'business type']);
  const lastGood = new Map(); // label -> value that reported success
  // Per-action executeTool tails each append the form's own modal as an
  // "ACTIVE POPUPS" block (~700 chars). Inside a batch the SAME block lands
  // after EVERY action — N copies of noise for the server's classifiers and
  // the LLM. Strip the intermediates; ONE end-of-batch snapshot (the state
  // every consumer actually gates on) is appended before the return.
  const POPUP_BLOCK_RE = /\n*⚠️\s*ACTIVE POPUPS:[\s\S]*$/;

  // Run ONE batch action and return its result text (the caller adds the
  // numbered-line prefix). Encapsulates the dedicated dispatchers, text
  // extraction, the party lastGood bookkeeping and the per-action popup strip
  // — shared by the serial path and the parallel waves below.
  const runBatchAction = async (a) => {
    const { action: toolName, ...params } = a;
    console.log(`[BMS Tools] Batch action: ${toolName}`, params);
    try {
      let result;
      if (toolName === 'fill_details_field') {
        // Details-tab fast path — not a standalone tool: dispatch straight to
        // the instance-driven filler (creation-booking method, see above).
        const text = await fillDetailsField(params);
        const failed = /^(details form not found|no port matching|no commodity matching|could not resolve|invalid date|no number in|.+ is not a number|.+: empty value)/.test(text);
        // `error` mirrors what executeTool does for failures, so the text
        // extraction below prints the failure instead of a bare "Done".
        result = { success: !failed, error: failed ? text : undefined,
                   result: { content: [{ type: 'text', text }] } };
      } else if (toolName === 'fill_approval_remarks') {
        // Booking Approval tab: the remark is a BraftEditor contenteditable —
        // dedicated filler (see fillApprovalRemarks), same pattern as above.
        const text = await fillApprovalRemarks(params);
        const failed = /^(remarks: empty value|remarks editor not found|could not type)/.test(text);
        result = { success: !failed, error: failed ? text : undefined,
                   result: { content: [{ type: 'text', text }] } };
      } else if (toolName === 'ensure_si_containers') {
        // SI container lines: add/fill rows through the site's own context
        // methods so the Draft-HBL print gate's totals check passes (see
        // ensureSiContainers), same dispatch pattern as the two above.
        const text = await ensureSiContainers(params);
        const failed = /^(so context not found|si containers: )/.test(text) && !/^si containers: (already aligned|added |filled )/.test(text);
        result = { success: !failed, error: failed ? text : undefined,
                   result: { content: [{ type: 'text', text }] } };
      } else if (toolName === 'house_doc_action') {
        // House Doc tab modals + hover menu — dedicated driver (see
        // houseDocAction), same pattern as above. FAILURE is carried by the
        // 'FAILED' marker in the action's own output line; the flow's parse
        // gates on it.
        const r = await houseDocAction(params);
        const text = (r && r.content && r.content[0] && r.content[0].text) || 'Done';
        const failed = /FAILED/.test(text);
        result = { success: !failed, error: failed ? text : undefined,
                   result: { content: [{ type: 'text', text }] } };
      } else if (toolName === 'confirm_manual_sailing') {
        // Confirm Booking tab manual-sailing STORE SYNC (see
        // confirmManualSailing) — writes setFieldsValue + the site's
        // onValuesChange sync. FAILURE is carried by the 'FAILED' marker.
        const text = await confirmManualSailing(params);
        const failed = /FAILED|MISSED/.test(text);
        result = { success: !failed, error: failed ? text : undefined,
                   result: { content: [{ type: 'text', text }] } };
      } else {
        result = await executeTool(toolName, params);
      }

      let text;
      if (result.success && result.result?.content?.[0]?.text) {
        text = result.result.content[0].text;
      } else if (result.error) {
        text = `Error: ${result.error}`;
      } else {
        text = 'Done';
      }

      // Remember the party value that reported success — the repair pass
      // below re-applies it if a later select (Consignee) wiped the field.
      // FIRST success wins: the flows send fallback chains (party code ->
      // canonical name -> short tokens) for the same field, and the later
      // attempts usually come back "already set (skipped)" — recording those
      // would keep the least-precise spelling ("FORWARDING HK") for the
      // save-time re-apply, whose empty-field search is the one context where
      // a vague value actually has to land a dropdown row on its own.
      if (toolName === 'select_option' || toolName === 'select_radio') {
        const label = (params.label || '').toLowerCase();
        const okText = text.startsWith('Selected')
          || /: selected /.test(text)
          || text.includes('already set');
        if (holdLabels.includes(label) && okText) {
          if (!lastGood.has(label)) lastGood.set(label, params.value);
        }
      }

      return text.replace(POPUP_BLOCK_RE, '').trimEnd();
    } catch (err) {
      return `Error - ${err.message}`;
    }
  };

  // ---- Parallel waves (2026-10-23) ---------------------------------------
  // A long batch (the Details fill runs ~20 actions) spent most of its wall
  // clock on per-action overhead that does NOT conflict across fields: every
  // sub-action's executeTool network-settle gate (~0.5s of quiet polling),
  // per-field render waits and re-assert loops, all serialized. Actions that
  // write DIFFERENT fields through focus-free paths can overlap safely:
  //   fill_form         — native value setter + input event, no focus needed
  //   set_date          — React-fiber onChange call (+ its own re-assert loop)
  //   fill_details_field (generic path) — one synchronous setFieldsValue +
  //                       onValuesChange partial write per field
  // MUST stay serial (excluded below):
  //   - every select_option / select_radio / toggle_checkbox / click —
  //     browser focus is a singleton, only one dropdown can be open, and the
  //     party selects' async chains wipe each other's fields;
  //   - fill_details_field for PORT fields — their site handlers cascade
  //     (paired *_name_hbl autofill, PLD copy) and re-write the store only
  //     after an async setState->resetFields chain; two ports interleaved
  //     could wipe each other's store write;
  //   - fill_details_field notify_party_id — rides the party handleChange
  //     chain (resetFields) like a header party select.
  // Duplicate field targets never share a wave; relative order between a
  // wave and its serial neighbours is preserved (numbered result lines stay
  // in execution order, which every parser of the batch text relies on).
  const SERIAL_DETAIL_FIELDS = new Set(['pol_locode', 'por_locode', 'pod_locode',
                                        'pld_locode', 'fd_locode', 'notify_party_id']);
  const parallelKeys = (a) => {
    if (a.action === 'fill_details_field') {
      const f = String(a.field || '').toLowerCase();
      return f && !SERIAL_DETAIL_FIELDS.has(f) ? ['df:' + f] : null;
    }
    if (a.action === 'set_date') {
      const k = String(a.label || a.name || '').toLowerCase();
      return k ? ['sd:' + k] : null;
    }
    if (a.action === 'fill_form') {
      const ks = (Array.isArray(a.fields) ? a.fields : [])
        .map(f => String((f && (f.label || f.name)) || '').toLowerCase())
        .filter(Boolean).map(k => 'ff:' + k);
      return ks.length ? ks : null;
    }
    return null;
  };
  const WAVE_SIZE = 5;
  const units = []; // { solo: action } | { wave: [action, ...] }
  let _run = [], _runKeys = new Set();
  const _flushRun = () => {
    for (let i = 0; i < _run.length; i += WAVE_SIZE) units.push({ wave: _run.slice(i, i + WAVE_SIZE) });
    _run = []; _runKeys = new Set();
  };
  for (const a of reordered) {
    const ks = parallelKeys(a);
    if (ks && ks.every(k => !_runKeys.has(k))) {
      _run.push(a); ks.forEach(k => _runKeys.add(k));
    } else {
      _flushRun(); units.push({ solo: a });
    }
  }
  _flushRun();

  let n = 0;
  for (const unit of units) {
    if (unit.solo) {
      const a = unit.solo;
      const batchDesc = String(a.label || a.button_name || a.page || a.field || a.action);
      // Live sub-phase + watchdog feed: a single action (a party dropdown's
      // search ladder) can legitimately run tens of seconds on a loaded box
      // with nothing to report — the heartbeat keeps the 300s no-progress
      // watchdog in Chatbot.js re-armed so the turn is never killed mid-batch.
      reportProgress(`(${n + 1}/${reordered.length}) ${batchDesc}…`);
      const heartbeat = setInterval(
        () => reportProgress(`(${n + 1}/${reordered.length}) still working: ${batchDesc}…`),
        30000
      );
      try {
        const text = await runBatchAction(a);
        results.push(`${n + 1}. ${a.action}: ${text}`);
      } finally {
        clearInterval(heartbeat);
      }
      n += 1;
    } else {
      const wave = unit.wave;
      const names = wave.map(a => a.label || a.field || a.action).join(', ');
      reportProgress(`(${n + 1}-${n + wave.length}/${reordered.length}) filling ${wave.length} fields in parallel (${names})…`);
      const heartbeat = setInterval(
        () => reportProgress(`(${n + 1}-${n + wave.length}/${reordered.length}) still working: ${wave.length} parallel fields…`),
        30000
      );
      try {
        const texts = await Promise.all(wave.map(a => runBatchAction(a)));
        texts.forEach((t, j) => results.push(`${n + j + 1}. ${wave[j].action}: ${t}`));
      } finally {
        clearInterval(heartbeat);
      }
      n += wave.length;
    }
  }

  // Party wipe ABSORPTION (one-pass fill — user request 2026-10-05). Selecting
  // the Consignee arms the site's async related-party chain (Header.js:
  // getRelatedParty -> handleChange(..., true, () => resetFields()) — the
  // consignee's fetch completes seconds later and nulls/re-defaults the
  // agents or blanks the header form). The old design let this land during
  // the confirm card and absorbed it at SAVE time: Step-0 re-applied
  // shipper/consignee/agents visibly after the Save click (the refill the
  // user watched) plus a 5-8s stabilization gate on EVERY save. Now: watch
  // the header HERE, absorb the wipe BEFORE the pre-flight card, and re-apply
  // the wiped fields in one pass. Exit when every held field holds through a
  // quiet window; bounded at 24s — a wipe that lands later than that stays
  // covered by save_and_confirm's on-demand check.
  // The settle+flush only run when the batch actually TOUCHED a hold field —
  // a modal fill (sailing options, edit forms) selects no parties, so its
  // 2.5s lead-in and modal flush were pure dead time (2026-10-23).
  const touchedHoldFields = reordered.some(a =>
    a.action === 'select_option' && holdLabels.includes((a.label || '').toLowerCase()));
  if (touchedHoldFields) {
    await realWait(2500); // let the chain's first moves land before reading

    // Flush unanswered PRS-overwrite modals first: a party select whose PRS
    // Modal.confirm was never answered never applied its value to the form
    // (the field is empty even though the selection label rendered).
    for (let flush = 0; flush < 3; flush++) {
      const pendingModal = [...document.querySelectorAll('.ant-modal-wrap')]
        .map(wrap => wrap.querySelector('.ant-modal'))
        .find(modal => modal && /overwrite the following e-Booking info by PRS info|auto save record is found/i.test(modal.textContent || '') && modal.getBoundingClientRect().width > 0);
      if (!pendingModal) break;
      const body = pendingModal.textContent || '';
      const want = /auto save record is found/i.test(body) ? 'No' : 'Confirm';
      const footer = pendingModal.querySelector('.ant-modal-confirm-btns') || pendingModal.querySelector('.ant-modal-footer');
      const btn = footer ? [...footer.querySelectorAll('button')].find(b => new RegExp('^' + want + '$', 'i').test((b.textContent || '').trim())) : null;
      if (!btn) break;
      console.log(`[BMS Tools] Flushing unanswered modal with "${want}"`);
      btn.click();
      await realWait(900);
    }
  }

  for (const a of reordered) {
    if (a.action !== 'select_option') continue;
    const label = (a.label || '').toLowerCase();
    if (!holdLabels.includes(label)) continue;
    if (!lastGood.has(label)) lastGood.set(label, a.value);
  }
  const repairLog = [];
  // Held fields that currently render their intended value (renders can lag
  // the context after a wipe — this is what the user SEES, and what Save
  // validation reads).
  const holdStates = () => {
    const states = [];
    for (const [label, value] of lastGood) {
      const found = findFormFieldsByLabel(label);
      if (found.length === 0) continue; // not rendered (hidden tab) — cannot judge
      const cur = readHoldValueText(found).toLowerCase();
      const val = String(value || '').trim().toLowerCase();
      const valWords = val.split(/[^a-z0-9]+/).filter(w => w.length >= 2);
      const holds = !!cur && (cur.includes(val) || (valWords.length > 0 && valWords.every(w => cur.includes(w))));
      states.push({ label, value, cur, holds });
    }
    return states;
  };
  const repairWiped = async () => {
    for (const s of holdStates()) {
      if (s.holds) continue;
      console.log(`[BMS Tools] Party wipe repair: re-selecting "${s.value}" in ${s.label} (field now: "${s.cur}")`);
      try {
        if (SAVE_PARTY_RESTORE_KEYS[s.label]) {
          const src = reordered.find(a => a.action === 'select_option' && (a.label || '').toLowerCase() === s.label && a.party_id);
          const r = await executeTool('select_option', src
            ? { label: s.label, value: s.value, party_id: src.party_id }
            : { label: s.label, value: s.value });
          const text = r?.result?.content?.[0]?.text || (r?.error ?? 'Done');
          repairLog.push(`[repair] ${s.label}: ${text}`);
        } else {
          const r = await reapplyHoldValue(s.label, s.value);
          repairLog.push(`[repair] ${s.label}: ${r.text}`);
        }
      } catch (err) {
        repairLog.push(`[repair] ${s.label}: Error - ${err.message}`);
      }
    }
  };
  const watchStart = Date.now();
  let cleanStreak = 0;
  let settledClean = false;
  if (lastGood.size === 0) {
    // Nothing held on this batch (e.g. a Details-only fill): nothing armed a
    // header chain, so treat the header as settled as of now.
    settledClean = true;
  }
  while (lastGood.size > 0 && Date.now() - watchStart < 24000) {
    const dirty = holdStates().filter(s => !s.holds);
    if (dirty.length === 0) {
      cleanStreak++;
      // ~5s of quiet after at least ~7s of watching = the chain is done.
      if (cleanStreak >= 4 && Date.now() - watchStart >= 7000) { settledClean = true; break; }
      await realWait(1200);
      continue;
    }
    cleanStreak = 0;
    reportProgress(`Header shifted (${dirty.map(s => s.label).join(', ')}) — re-applying in one pass…`);
    // Give the cascade a moment to finish its moves before repairing, or the
    // next link blanks the field right after the refill (the chain applies
    // its writes one fetch at a time).
    await realWait(1800);
    await repairWiped();
    await realWait(1200);
  }
  _lastFillSettledAt = settledClean ? Date.now() : 0;
  if (repairLog.length) {
    results.push(...repairLog);
  }

  // Remember the intended header parties so save_and_confirm can re-apply them
  // right before its Save click (the header can drop a selection at any moment
  // via its async resetFields chain).
  if (lastGood.size > 0) {
    _lastHeaderParties = new Map(lastGood);
    _lastHeaderPartyIds = new Map();
    for (const a of reordered) {
      if (a.action !== 'select_option' || !a.party_id) continue;
      const label = (a.label || '').toLowerCase();
      if (!_lastHeaderPartyIds.has(label)) _lastHeaderPartyIds.set(label, a.party_id);
    }
  }

  // ONE popup snapshot for the whole batch (the per-action copies were
  // stripped above): this is the end state every consumer gates on — the
  // flow engine's "active popup after a mutating step" check and the LLM's
  // popup handling both read exactly this.
  const finalPopupBlock = formatBlockingPopups(detectPopups());
  if (finalPopupBlock) {
    results.push(finalPopupBlock.trim());
  }

  return {
    content: [{ type: "text", text: results.join('\n') }]
  };
}


/**
 * Open a shipping order, click Edit, switch tab, and optionally read form.
 * Combines 3-4 separate tool calls into one client-side execution.
 */
async function openSoForEdit({ so_number, tab_name, read_form_after }) {
  const steps = [];

  // Step 1: Navigate to SO
  const navResult = await executeTool('open_shipping_order', { so_number });
  const navText = (navResult && navResult.success && navResult.result?.content?.[0]?.text) ? navResult.result.content[0].text : '';
  if (!navResult || !navResult.success || navResult.error || /^ERROR:/i.test(navText)) {
    const detail = (navResult && navResult.error) ? (': ' + navResult.error) : (navText ? (': ' + navText) : '');
    return { content: [{ type: 'text', text: `open_so_for_edit failed: could not open SO ${so_number}${detail}` }] };
  }
  // Leave-guard: the user clicked Stay on the "Are you sure you want to
  // leave?" confirm (current page has unsaved/edited changes — e.g. they typed
  // into the form while the flow was waiting). Report an honest failure
  // ("could not open") so the flow's verification stops it — NEVER click Edit
  // on the wrong page.
  if (/navigation was canceled|chose to stay/i.test(navText)) {
    return { content: [{ type: 'text', text: `open_so_for_edit failed: could not open SO ${so_number} — the user chose to stay on the current page to keep unsaved edits, so navigation was canceled. Ask the user to save or discard those changes first, then retry.` }] };
  }
  steps.push(`Opened SO ${so_number}`);

  // Step 2: Click Edit
  await executeTool('click_button', { button_name: 'Edit' });
  steps.push('Clicked Edit');

  // Step 3: Switch tab if specified
  if (tab_name) {
    await executeTool('switch_so_tab', { tab_name });
    steps.push(`Switched to tab: ${tab_name}`);
  }

  // Step 4: Read form if requested
  let formContent = '';
  if (read_form_after) {
    const formResult = await executeTool('read_form', {});
    if (formResult.success && formResult.result?.content?.[0]?.text) {
      formContent = formResult.result.content[0].text;
    }
    steps.push('Read form values');
  }

  const combinedText = steps.join('\n');
  const fullText = formContent
    ? `${combinedText}\n\nCurrent form values:\n${formContent}`
    : combinedText;

  return {
    content: [{ type: "text", text: fullText }]
  };
}


/**
 * Navigate to a list/dashboard page, (optionally filter/search), click Search, and read it.
 * Combines navigate_to_page + apply_filters + click_button("Search") + read_current_page
 * into one client-side execution (one LLM round-trip).
 */
async function showList({ page, filters, search_term }) {
  const steps = [];

  if (search_term) {
    await executeTool('search_booking', { search_term });
    steps.push(`Searched bookings for "${search_term}"`);
  } else {
    await executeTool('navigate_to_page', { page });
    steps.push(`Navigated to ${page}`);
  }

  if (Array.isArray(filters) && filters.length) {
    await executeTool('apply_filters', { filters });
    steps.push(`Applied ${filters.length} filter(s)`);
  }

  // Click Search to load results (skip if search_booking already searched). Best-effort.
  if (!search_term) {
    try {
      const r = await executeTool('click_button', { button_name: 'Search' });
      steps.push(r && r.success ? 'Clicked Search' : 'No Search button (page may auto-load)');
    } catch (e) {
      steps.push('No Search button (page may auto-load)');
    }
  }

  let pageContent = '';
  const rd = await executeTool('read_current_page', {});
  if (rd && rd.success && rd.result && rd.result.content && rd.result.content[0]) {
    pageContent = rd.result.content[0].text || '';
  }

  const text = pageContent
    ? `Loaded ${page || 'list'}. Page content:\n${pageContent}`
    : `Loaded ${page || 'list'} but could not read content. Steps: ${steps.join('; ')}.`;
  return { content: [{ type: 'text', text }] };
}


/**
 * Open a shipping order and read a tab (or the header) — non-mutating (no Edit click).
 * Combines open_shipping_order + read_so_tab (or read_so_header) into one call.
 */
async function readSo({ so_number, tab_name }) {
  reportProgress(`Opening SO ${so_number}…`);
  const open = await executeTool('open_shipping_order', { so_number });

  // Propagate ANY non-success open (403/AccessDeclined, typo, settle-timeout, or the
  // user staying on an edited page) instead of blindly reading a tab/header:
  // (a) on 403 the open ERROR text carries the diagnosis + similar-SO suggestions +
  //     STOP instructions for the LLM (and Chatbot.js's 403 loop-guard keys on it) —
  //     swallowing it made read_so report "Opened SO X but could not read header",
  //     sending the agent into pointless retry hops (the frozen-CoT symptom);
  // (b) after a failed open, findSimilarShippingOrders has already navigated to the
  //     booking list, so reading a tab would describe the WRONG page.
  // open_shipping_order confirms success in exactly one wording, so whitelist it.
  const openText = (open && open.result && open.result.content && open.result.content[0]
    && open.result.content[0].text) || '';
  if (open && open.error) {
    return { content: [{ type: 'text', text: `ERROR: open_shipping_order failed: ${open.error}` }] };
  }
  if (!/^Opened shipping order /i.test(openText.trim())) {
    return { content: [{ type: 'text', text: openText || 'ERROR: open_shipping_order returned no result.' }] };
  }

  reportProgress(tab_name ? `Reading tab "${tab_name}"…` : 'Reading header…');
  const readTool = tab_name
    ? await executeTool('read_so_tab', { tab_name })
    : await executeTool('read_so_header', {});

  let content = '';
  if (readTool && readTool.success && readTool.result && readTool.result.content && readTool.result.content[0]) {
    content = readTool.result.content[0].text || '';
  }

  const label = tab_name ? `tab "${tab_name}"` : 'header';
  const text = content
    ? `Opened SO ${so_number}, read ${label}:\n${content}`
    : `Opened SO ${so_number} but could not read ${label}.`;
  return { content: [{ type: 'text', text }] };
}


// ==================== Tool Dispatcher ====================

/**
 * Force a fresh scan for blocking popups, giving a late async confirm modal
 * time to mount first. Escape hatch for the case where a confirm dialog
 * (e.g. PRS-overwrite) appeared AFTER the tool-result snapshot was sent and the
 * turn already ended (route_after_tool ends the turn once a result is returned).
 * The LLM calls this as the first action of the NEXT turn when a mutating action
 * showed no success signal and no popup. (M1 recovery path.)
 */
async function pollPopups() {
  await waitForPopup(4000);
  const popups = detectPopups();
  const info = formatBlockingPopups(popups);
  return { content: [{ type: "text", text: info ? info.replace(/^\n\n/, '') : 'No blocking popups detected' }] };
}

/**
 * Select the Nth proposed sailing option on the Booking Approval tab.
 *
 * "Approved Sailing" is NOT a dropdown — it's a read-only field that gets set by
 * CLICKING one of the proposed-sailing cards (.scheduleItem div inside the Nth
 * li.ant-list-item of .sailingScheduleResultList; onClick -> selectSailingSchedule).
 * select_option/click_button/click_list_item can't reach it, so the chatbot uses
 * this dedicated tool after setting Approval Status = "Approved".
 */
async function selectProposedSailing({ option_number }) {
  const n = parseInt(option_number, 10);
  if (!n || n < 1) {
    return { content: [{ type: "text", text: `Invalid option_number "${option_number}". Use a 1-based index (1, 2, ...).` }] };
  }

  const list = document.querySelector('.sailingScheduleResultList');
  if (!list) {
    // TBC sailings render a literal "TBC — Sailing Option will be provided"
    // card instead of the proposed-sailing list (BookingApproval.js renders
    // the TBC card when sailing_schedule_id === 'TBC'). There is nothing to
    // pick: the TBC option IS the approved sailing. Report an explicit SUCCESS
    // so the flow's pick step does not poison the approval (2026-10-05).
    const tbcCard = document.querySelector('.TBCschedule')
      || ([...document.querySelectorAll('div, p')].find(function (el) {
        return /sailing option will be provided|sailing option to be provided/i.test(el.textContent || '')
          && el.children.length <= 2;
      }));
    if (tbcCard) {
      return { content: [{ type: 'text', text: 'RESULT: success (sailing is TBC — "Sailing Option will be provided"; the TBC option is the approved sailing, nothing to pick)' }] };
    }
    return { content: [{ type: 'text', text: 'No proposed sailing list (.sailingScheduleResultList) found. Make sure you are on the Booking Approval tab AND Approval Status is set to "Approved" — the sailing options only render then.' }] };
  }

  // The clickable card is .scheduleItem.expanded (onClick -> selectSailingSchedule).
  // It renders inside .sailingScheduleResultList as a div.ant-list-item (NOT <li>),
  // so target the card class directly.
  const cards = Array.from(list.querySelectorAll('.scheduleItem.expanded'));
  if (cards.length < n) {
    return { content: [{ type: "text", text: `Only ${cards.length} proposed sailing option(s) found; cannot pick option ${n}. Read the tab to see how many options are available.` }] };
  }

  const target = cards[n - 1];
  target.click();
  await realWait(1500);

  // Verify via the "Approved Sailing" FormItem control (shows "Option N" once set).
  const items = Array.from(document.querySelectorAll('.ant-form-item'));
  const f = items.find(function (it) { const l = it.querySelector('.ant-form-item-label'); return l && /Approved Sailing/.test(l.textContent); });
  const now = f ? ((f.querySelector('.ant-form-item-control') || {}).innerText || '') : '';
  const matched = new RegExp('Option ' + n + '(\\D|$)').test(now || '');
  return {
    content: [{
      type: "text",
      text: matched
        ? `Selected proposed sailing option ${n}. Approved Sailing now shows "${now.trim()}". Next: click_button("Save") to finalize, then handle any confirm popup (handle_popup_confirm).`
        : `Clicked proposed sailing option ${n}. Approved Sailing now reads "${(now || '(not visible yet)').trim()}". If it did not update, the option may be Unavailable; try the other option, then click_button("Save").`
    }]
  };
}


/**
 * Pick the Nth result in the sailing-SEARCH modal ("Add New Sailing Option" ->
 * "Search Sailing" mode) on the Confirm Booking tab (2026-10-05).
 *
 * The modal's results render in .sailingScheduleList as .scheduleItem cards.
 * The cards carry NO Select button — the selection affordance is the STAR icon
 * (".iconTopRight", tooltip "Select as the Selected Sailing Option";
 * starOnClick -> onSelectSailingSchedule), which appends the option, writes its
 * ETD/ETA/cut-offs into the form (handleSelectSailing) and CLOSES the modal.
 * Clicking the card div is a no-op in the modal and used to report success
 * while Confirm Booking refused with "Please fill in all required fields".
 * The search API is async: poll for the results list before picking.
 */
async function selectSailingSearchResult({ option_number }) {
  const n = parseInt(option_number, 10);
  if (!n || n < 1) {
    return { content: [{ type: 'text', text: `Invalid option_number "${option_number}". Use a 1-based index (1, 2, ...).` }] };
  }
  // Scope to the OPEN sailing modal (the .sailingScheduleList list or, when
  // the sidebar filter renders results elsewhere in the modal, ANY visible
  // .scheduleItem cards inside the modal). Live finding 2026-10-05: the List
  // class existed but rendered empty while the results were visible in the
  // modal - card-scoped lookup is the robust target.
  const modalScope = () => Array.from(document.querySelectorAll('.ant-modal-wrap'))
    .filter(w => { const m = w.querySelector('.ant-modal'); return m && getComputedStyle(w).display !== 'none' && m.offsetHeight > 0; })
    .find(w => /Add New Sailing Option/i.test((w.textContent || '').slice(0, 400))) || null;
  const visibleList = () => {
    const modal = modalScope();
    if (modal) {
      const list = Array.from(modal.querySelectorAll('.sailingScheduleList')).filter(el => el.offsetParent !== null)[0];
      return list || modal;
    }
    // The search results can also render in the Confirm Booking tab's
    // "Search Sailing & Proposed Sailing Option" panel BEHIND the modal
    // (live finding 2026-10-05: the AI read the result rows there while the
    // modal itself rendered no cards).
    const pane = document.querySelector('.ant-tabs-tabpane-active');
    if (!pane) return null;
    const list = Array.from(pane.querySelectorAll('.sailingScheduleList')).filter(el => el.offsetParent !== null)[0];
    return list || pane;
  };
  const getCards = (scope) => scope ? Array.from(scope.querySelectorAll('.scheduleItem')).filter(c => c.offsetParent !== null) : [];
  let list = null, cards = [];
  // Phase 1: cards inside the modal - the Project44 + CARGO merge takes
  // ~30-60s after the Search click before the cards render, and closing the
  // modal early DESTROYS them (destroyOnClose). Wait long.
  for (let i = 0; i < 70; i++) {
    list = visibleList();
    cards = getCards(list);
    if (cards.length >= n) break;
    await realWait(1000);
  }
  // Phase 2: the modal's search often leaves its OWN list empty and instead
  // populates the Confirm Booking tab's proposed-sailing panel — which only
  // renders once the modal is CLOSED (live finding 2026-10-05: 0 cards
  // anywhere while the modal was open; the results appeared in the tab's
  // panel after the flow aborted and the modal was gone). Close the modal,
  // wait for the tab cards, pick from there.
  if (cards.length < n) {
    const modal = modalScope();
    if (modal) {
      const closeBtn = Array.from(modal.querySelectorAll('button'))
        .filter(b => b.offsetParent !== null)
        .find(b => /^close$/i.test((b.textContent || '').trim()));
      if (closeBtn) { closeBtn.click(); await realWait(1500); }
    }
    const pane = document.querySelector('.ant-tabs-tabpane-active') || document;
    // The proposed list merges Project44 + the internal CARGO schedule and can
    // take ~30-60s to render after the search (live finding 2026-10-05).
    for (let i = 0; i < 20; i++) {
      cards = getCards(pane);
      if (cards.length >= n) break;
      await realWait(1000);
    }
    list = pane;
  }
  if (!list) {
    return { content: [{ type: 'text', text: 'No sailing-search modal ("Add New Sailing Option") is open. Make sure the Confirm Booking tab is in edit mode and the Search button was clicked.' }] };
  }
  if (cards.length < n) {
    // Structure dump: name what IS rendered so the caller (and we) can see
    // the real results container instead of guessing.
    let structure = '';
    try {
      const scope = list || document;
      const tables = Array.from(scope.querySelectorAll('.ant-table')).filter(t => t.offsetParent !== null);
      structure = ' tables=' + tables.length;
      if (tables.length) {
        const rows = Array.from(tables[0].querySelectorAll('tbody tr')).filter(r => r.offsetParent !== null);
        structure += ' rows=' + rows.length;
        if (rows.length) {
          const r0 = rows[0];
          structure += ' row0btns=[' + Array.from(r0.querySelectorAll('button')).map(b => (b.textContent || '').trim()).join('/') + ']';
          structure += ' row0text=' + (r0.textContent || '').replace(/\s+/g, ' ').slice(0, 80);
        }
      }
    } catch (e) {}
    let where = '';
    try {
      const parts = [];
      document.querySelectorAll('.ant-modal-wrap').forEach((w, i) => {
        const vis = getComputedStyle(w).display !== 'none';
        parts.push('wrap' + i + (vis ? ':V' : ':H') + 'cards=' + w.querySelectorAll('.scheduleItem').length + ',tbl=' + w.querySelectorAll('.ant-table').length);
      });
      const pane = document.querySelector('.ant-tabs-tabpane-active');
      if (pane) parts.push('pane:cards=' + pane.querySelectorAll('.scheduleItem').length + ',tbl=' + pane.querySelectorAll('.ant-table').length + ',items=' + pane.querySelectorAll('.sailingScheduleResultList, .sailingScheduleList').length);
      parts.push('body:cards=' + document.querySelectorAll('.scheduleItem').length);
      where = ' WHERE[' + parts.join(' | ') + ']';
    } catch (e) {}
    return { content: [{ type: 'text', text: `Only ${cards.length} sailing search result card(s) rendered; cannot pick option ${n}.${structure}${where}` }] };
  }
  const target = cards[n - 1];
  const summary = (target.innerText || '').replace(/\s+/g, ' ').trim().slice(0, 300);
  // The modal's result cards have NO Select button (probe 2026-10-05: card
  // buttons = none) — the selection affordance is the STAR icon in the card's
  // top-right corner (SailingScheduleItem starOnClick -> "Select as the
  // Selected Sailing Option" tooltip), an antd Icon SPAN, not a button.
  // Clicking the card div itself is a no-op in the modal (its onClick prop is
  // not passed there) — that path reported success while the sailing never
  // applied and Confirm Booking refused with 'Please fill in all required
  // fields'. Try the star first; keep the legacy fallbacks for the tab panel.
  let clicked = 'card';
  const star = Array.from(target.querySelectorAll('.iconTopRight, .anticon-star'))
    .find(el => el.offsetParent !== null);
  if (star) {
    star.click();
    clicked = 'star icon';
  } else {
    const selectBtn = Array.from(target.querySelectorAll('button'))
      .find(b => (b.textContent || '').trim().toLowerCase() === 'select' && b.offsetParent !== null);
    if (selectBtn) {
      selectBtn.click();
      clicked = 'Select button';
    } else {
      target.click();
    }
  }
  for (let i = 0; i < 15 && visibleList(); i++) await realWait(1000);
  const closed = !visibleList();
  return { content: [{ type: 'text', text: (closed ? 'RESULT: success. ' : '') + `Clicked ${clicked} on sailing search option ${n}: ${summary}` + (closed ? ' - modal closed, the sailing was added as the booking option.' : ' - the modal is still open; the select may not have registered. Check the tab and re-pick if needed.') }] };
}


/**
 * Click a Save-style button AND confirm the popup it triggers, in one call.
 * Collapses click_button(Save) + handle_popup_confirm(ok) — the tail of every
 * BMS mutation — into a single client-side execution (one LLM round-trip).
 * Optionally reads the page back after the confirm so the caller can verify the
 * new state in the SAME turn. Never throws: returns an error string naming the
 * failed step.
 */

// save-time party restore, keyed by the lowercase field label used in flows.
// partyType is the segment of the SAME search endpoint the header dropdowns
// use (/api/v1/party/{shippers|consignees|agents}/{query}); resultKey is the
// Header state list the antd Select renders its options from (optionLabelProp
// "text" needs the row present or the label falls back to the raw id);
// soField is the context field the SAVE payload reads.
const SAVE_PARTY_RESTORE_KEYS = {
  'shipper': { partyType: 'shippers', resultKey: 'shipperResult', soField: 'shipper_party_id', handler: 'handleShipperSelect' },
  'consignee': { partyType: 'consignees', resultKey: 'consigneeResult', soField: 'consignee_party_id', handler: 'handleConsigneeSelect' },
  'nomination agent': { partyType: 'agents', resultKey: 'nominatAgentResult', soField: 'nomination_agent_party_id', handler: 'handleAgentSelect', agentType: 'nominatAgent' },
  'release agent': { partyType: 'agents', resultKey: 'releaseAgentResult', soField: 'release_agent_party_id', handler: 'handleAgentSelect', agentType: 'releaseAgent' },
};

// Walk the React fiber tree up from a DOM node to the SO Header class
// instance (the component that owns the handle*Select methods).
function findHeaderInstance(el) {
  const fiberKey = Object.keys(el).find(k => k.startsWith('__reactFiber$') || k.startsWith('__reactInternalInstance$'));
  if (!fiberKey) return null;
  let f = el[fiberKey];
  for (let depth = 0; f && depth < 80; depth++) {
    const n = f.stateNode;
    if (n && typeof n.handlePartyChange === 'function') return n;
    f = f.return;
  }
  return null;
}

// Walk the fiber tree up to the ShippingOrder context/screen instance (the
// class in screens/ShippingOrder/context.js — owns loadSO / handleEdit /
// handleCancel and the `editable` state the router <Prompt> keys on).
function findSoContextInstance() {
  const el = document.querySelector('[role="tab"], .ant-tabs-tab');
  if (!el) return null;
  const fiberKey = Object.keys(el).find(k => k.startsWith('__reactFiber$') || k.startsWith('__reactInternalInstance$'));
  if (!fiberKey) return null;
  let f = el[fiberKey];
  for (let depth = 0; f && depth < 150; depth++) {
    const n = f.stateNode;
    if (n && typeof n.loadSO === 'function' && typeof n.handleCancel === 'function') return n;
    f = f.return;
  }
  return null;
}

// ==================== SO edit-session ownership + clean exit ====================
// The SO screen stays in EDIT mode after a save (only Cancel/Back disarms it —
// ShippingOrder/context.js handleCancel), and an editable SO (a) arms the
// leave-guard for every later navigation and (b) silently refuses switching to
// the House-Doc/Booking-Approval tabs (ShippingOrder.js handleTabClick shows a
// warning toast and returns — the tab never activates). Flows used to leave
// that state behind, so the NEXT flow either froze at a native confirm or
// drove a HIDDEN tab pane (2026-10-22 live: Assign Job# clicked on a hidden
// pane — its modal mounted invisible, poll_popups saw nothing, hard stop).
//
// Ownership rule: a chatbot tool call that flips the SO INTO edit mode marks
// the session chatbot-owned (flows arm it via open_so_for_edit's Edit click);
// a REAL user input event (isTrusted) while editable marks it human-owned.
// Only a chatbot-owned session with no human input is auto-exited — the site's
// own handleCancel() (restores the saved record, flips editables off, strips
// ?editable=true). A human-owned edit session is NEVER discarded.
let _chatbotEditArmed = false;
let _humanEditInput = false;
(function installHumanEditWatcher() {
  const mark = (e) => { if (e && e.isTrusted) _humanEditInput = true; };
  // Capture phase, passive by default for non-blocking events; synthetic
  // fills/clicks from bmsTools are NOT trusted, so only real keyboard/mouse
  // input flags the session.
  document.addEventListener('input', mark, true);
  document.addEventListener('change', mark, true);
})();

function _soIsEditable() {
  try {
    const inst = findSoContextInstance();
    return !!(inst && inst.state && inst.state.editable);
  } catch (e) {
    return false;
  }
}

// Sample the SO edit state around every chatbot tool call: a flip ON during a
// chatbot action marks the session chatbot-owned (reset the human flag); a
// flip OFF clears ownership. Called from the executeTool funnel.
function _trackSoEditState(before, after) {
  if (!before && after) {
    _chatbotEditArmed = true;
    _humanEditInput = false;
  } else if (!after) {
    _chatbotEditArmed = false;
  }
}

// Exit a chatbot-owned SO edit session through the site's own cancel-edit.
// Returns { needed, exited, blocked } — blocked='human' means the session has
// real human edits (or was armed by the human) and must NOT be discarded.
async function _exitSoEditMode() {
  if (!_soIsEditable()) return { needed: false, exited: false, blocked: null };
  if (!_chatbotEditArmed || _humanEditInput) {
    return { needed: true, exited: false, blocked: 'human' };
  }
  try {
    const inst = findSoContextInstance();
    if (!inst || typeof inst.handleCancel !== 'function') {
      return { needed: true, exited: false, blocked: 'no-instance' };
    }
    inst.handleCancel();
    // handleCancel flips the editables off synchronously through setState and
    // strips ?editable=true; poll for the settled state (bounded, then report
    // honestly either way — a still-editable page must never read as exited).
    const off = () => !_soIsEditable() && !/editable=true/.test(window.location.search);
    let settled = off();
    if (!settled) {
      try {
        await pollUntil(off, 4000);
        settled = off();
      } catch (e) { /* timeout — settled stays false */ }
    }
    _chatbotEditArmed = false;
    _humanEditInput = false;
    if (!settled) return { needed: true, exited: false, blocked: 'still-editable' };
    return { needed: true, exited: true, blocked: null };
  } catch (e) {
    return { needed: true, exited: false, blocked: (e && e.message) || 'error' };
  }
}

// exit_so_edit — deterministic flows' post-commit cleanup step: leave the SO
// in its clean saved view so the guard never arms and gated tabs open.
async function exitSoEdit() {
  const r = await _exitSoEditMode();
  if (!r.needed) {
    return { content: [{ type: 'text', text: 'RESULT: success (no edit session active — the SO is already in view mode)' }] };
  }
  if (r.exited) {
    return { content: [{ type: 'text', text: 'RESULT: success (exited edit mode — the SO now shows its saved view; navigation guard disarmed)' }] };
  }
  return { content: [{ type: 'text', text: `RESULT: error (an edit session is still active but was not exited: ${r.blocked === 'human' ? 'the page has human-made unsaved edits — ask the user to save or cancel them first' : r.blocked})` }] };
}

// Party lookup through the SAME endpoint the header dropdowns search.
// searchPartyRows returns the full row list (null = request FAILED — distinct
// from [] which is an answered-but-empty search); searchPartyRow keeps the
// { row, empty } contract the restore paths use.
async function searchPartyRows(partyType, query, byId) {
  const token = (window.localStorage.getItem('id_token') || window.localStorage.getItem('token') || '').replace(/"/g, '');
  const headers = { 'Authorization': 'Bearer ' + token, 'Mr-Token': 'CAE983C3-7320-46C8-9F58-6C87DBF66EE9' };
  const url = `${window.location.origin}/api/v1/party/${partyType}/${encodeURIComponent(query)}${byId ? '?search_type=party_id' : ''}`;
  // Hard 8s timeout: a fetch that never settles (e.g. the browser's
  // 6-connections-per-host queue saturated by leaked SSE streams) would hang
  // the whole fill batch — a failed lookup retries/fails honestly instead.
  const r = await fetch(url, { headers, signal: AbortSignal.timeout(8000) });
  if (!r.ok) return null;
  const rows = await r.json();
  return Array.isArray(rows) ? rows : null;
}

async function searchPartyRow(partyType, query, byId) {
  let rows;
  try {
    rows = await searchPartyRows(partyType, query, byId);
  } catch (e) {
    return { row: null, empty: false };
  }
  if (rows === null) return { row: null, empty: false };
  if (rows.length === 0) return { row: null, empty: true };
  return { row: rows[0], empty: false };
}

// Exported for the flow pre-flight card's party dropdown (v2 cards): search the
// SAME site party API the SO header dropdowns use and map the rows to the
// lightweight option objects PartySelect renders. `segment` must be the SEARCH
// segment per field (shippers|consignees|nominationagents|releaseagents) — NOT
// the fill-time restore segment ('agents' covers both agent kinds there).
// Returns null on failure (distinct from [] = answered-but-empty) so the widget
// can fall back to the free-text "type the exact name" path. party_id stays in
// the site's id space and is metadata only — it never drives a form selection.
export async function searchPartyOptions(segment, query) {
  let rows;
  try {
    rows = await searchPartyRows(segment, query, false);
  } catch (e) {
    return null;
  }
  if (!Array.isArray(rows)) return null;
  return rows.slice(0, 20).map((r) => ({
    name: r.full_name,
    code: r.party_code_display || r.party_code,
    office: r.office_code,
    addr: r.full_addr,
    party_id: r.party_id,
    customer_id: r.customer_id,
    customer_full_name: r.customer_full_name,
    customer_code: r.customer_code,
  }));
}

// The generic placeholder customer: the party search returns it for consignees
// with no customer linkage. It is NOT a resolution — writing it is exactly the
// "Customer filled to Other" bug (2026-10-02).
function isGenericOtherCustomer(cust) {
  if (!cust || cust.customer_id == null || cust.customer_id === '') return true;
  const name = String(cust.customer_full_name || '').trim().toLowerCase();
  const code = String(cust.customer_code || '').trim().toLowerCase();
  return name === 'other' || code === 'other';
}

const _foldCompanyName = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

// Distinctive company token of a party name — the fragment a human types into
// the dropdown ("ske" for SKECHERS CHINA LIMITED). Legal-suffix filler is
// skipped so "RIESS KELOMAT GMBH" searches RIESS, not GMBH.
function companyToken(name) {
  const legal = new Set(['ltd', 'limited', 'co', 'company', 'gmbh', 'inc', 'llc',
    'sa', 'spa', 'srl', 'bv', 'nv', 'plc', 'corp', 'corporation', 'pte', 'pty',
    'the', 'and', 'of', 'as', 'ab', 'oy', 'ag', 'sarl']);
  const tokens = String(name || '').split(/[^A-Za-z0-9]+/).filter(Boolean);
  for (const t of tokens) {
    if (t.length >= 3 && !legal.has(t.toLowerCase())) return t;
  }
  return '';
}

// Resolve the Customer for the SO's CURRENT consignee through the party search
// API (the same /api/v1/party/consignees/{q} the dropdown itself uses; its
// rows carry customer_id/full_name/code — user-verified 2026-10-02 with
// /party/consignees/ske). Order:
//   1. the selected consignee's exact row (by party_id) when it carries a REAL
//      customer;
//   2. a same-company row from a name-fragment search — consignees without
//      their own linkage resolve to the generic "Other", but a sibling entity
//      of the same company usually carries the company account (SKECHERS
//      CHINA LIMITED -> the SKECHERS row -> customer "Skechers"). Only a
//      sibling whose CUSTOMER name belongs to the same company is accepted,
//      so a partner's account (e.g. customer "ABC" on a distribution-centre
//      party) is never inferred.
// Returns { customer_id, customer_full_name, customer_code, via } or null.
async function deriveConsigneeCustomer(inst) {
  const so = inst && inst.props ? inst.props.so : null;
  const pick = (row) => ({
    customer_id: row.customer_id,
    customer_full_name: row.customer_full_name || '',
    customer_code: row.customer_code || '',
  });
  let self = null;
  if (so && so.consignee_party_id) {
    self = (await searchPartyRow('consignees', so.consignee_party_id, true)).row;
    if (self && !isGenericOtherCustomer(self)) {
      return Object.assign(pick(self), { via: `consignee row (${self.full_name})` });
    }
  }
  const tail = await deriveCustomerFromSelf(self,
    so && so.consignee_detail && (so.consignee_detail.master_name || so.consignee_detail.name));
  return tail;
}

// Same-company tail of the Customer derivation, shared by the fill-time path
// (restoreHeaderParty — the consignee row is already in hand), the save-time
// re-check and the pre-flight card preview. The consignee's own row carries no
// customer (or the generic "Other"), so search the company token and accept
// only a sibling row whose CUSTOMER belongs to the same company.
async function deriveCustomerFromSelf(self, fallbackName) {
  const name = (self && self.full_name) || String(fallbackName || '') || '';
  const token = companyToken(name);
  const folded = _foldCompanyName(token);
  if (token && folded) {
    const rows = await searchPartyRows('consignees', token);
    if (rows) {
      const cand = rows.find((r) => r && r.party_id != null
        && (!self || String(r.party_id) !== String(self.party_id))
        && !isGenericOtherCustomer(r)
        && _foldCompanyName(r.full_name).includes(folded)
        && (folded.includes(_foldCompanyName(r.customer_full_name))
          || _foldCompanyName(r.customer_full_name).includes(folded)));
      if (cand) {
        return {
          customer_id: cand.customer_id,
          customer_full_name: cand.customer_full_name || '',
          customer_code: cand.customer_code || '',
          via: `same-company row ${cand.full_name} (searched "${token}")`,
        };
      }
    }
  }
  return null;
}

// Mirror of ShippingOrder/common.js GetPartyOption's <Option> prop mapping,
// as a plain object: the handle*Select methods destructure these exact props.
function buildPartyOption(row) {
  return Object.assign({}, row, {
    value: row.party_id,
    party_name: row.full_name,
    party_code: row.party_code,
    zip: row.zip_postal,
    text: row.party_code_display ? `${row.full_name} (${row.party_code_display})` : row.full_name,
  });
}

// Card-time preview of the Customer a create flow will stamp for a consignee
// NAME (2026-10-02): same rules as the fill-time deriveConsigneeCustomer, but
// driven by the name on the pre-flight card instead of the open form's party
// id — so the card can SHOW the customer BEFORE the user confirms (user
// request: "shouldn't the customer be filled before I confirm the booking?").
// Best-effort and advisory only: the by-id derivation at fill time stays
// authoritative. Returns { customer_full_name, customer_code } or null.
export async function previewConsigneeCustomer(consigneeName) {
  const name = String(consigneeName || '').trim();
  if (!name) return null;
  try {
    const self = (await searchPartyRow('consignees', name)).row;
    if (self && !isGenericOtherCustomer(self)) {
      return { customer_full_name: self.customer_full_name || '',
               customer_code: self.customer_code || '' };
    }
    const derived = await deriveCustomerFromSelf(self, name);
    if (derived) {
      return { customer_full_name: derived.customer_full_name || '',
               customer_code: derived.customer_code || '' };
    }
  } catch (e) {
    console.log('[BMS Tools] customer preview failed:', e && e.message);
  }
  return null;
}

// Write a derived Customer to the SO header in one shot — the customerResult
// list entry the Customer Select renders from (GetCustomerOption reads
// full_name/code), the antd form store the save validation reads, and the
// context so.customer_* the save payload carries. Used at fill time (one pass)
// and by the save-time re-check.
function applyConsigneeCustomer(inst, cust) {
  inst.setState({ customerResult: [{
    customer_id: cust.customer_id,
    code: cust.customer_code || '',
    customer_code: cust.customer_code || '',
    customer_full_name: cust.customer_full_name || '',
    full_name: cust.customer_full_name || '',
  }] });
  const form = inst.formRef && inst.formRef.current;
  if (form && typeof form.setFieldsValue === 'function') {
    form.setFieldsValue({ customer_id: cust.customer_id });
  }
  const hc = inst.props && inst.props.handleChange;
  if (typeof hc === 'function') {
    hc({
      customer_id: cust.customer_id,
      customer_full_name: cust.customer_full_name,
      customer_code: cust.customer_code,
    }, false);
  }
}

// One-party restore. FAST path: build the same context payload the site's own
// handle*Select methods build and push it through props.handleChange directly —
// awaiting the full handlers costs 10-30s each on a loaded box (their PRS
// checks, default-agent lookups and diff walks are slow) which made the fill
// batch take minutes (2026-09-21). The save payload reads exactly these
// context fields, so the committed result is identical. FALLBACK: the real
// handlers, then the typed selectOption primitive.
// Returns {ok, via}: via 'store' = context+form verified, 'label' = fallback.
async function restoreHeaderParty(label, value, restoreKey, partyId) {
  const lower = (label || '').toLowerCase();
  if (restoreKey) {
    try {
      const found = findFormFieldsByLabel(label);
      const selectEl = found.length ? found[0].formItem.querySelector('.ant-select') : null;
      const instance = selectEl ? findHeaderInstance(selectEl) : null;
      // Exact-id first (the flow resolver's party_id); fall back to name search.
      // An id miss or an EMPTY search answer stops here: the resolver already
      // picked THE party, and a degraded party API makes the typed dropdown
      // path pure waste (10-30s of dropdown waits per attempt).
      let row = null;
      let emptySearch = false;
      if (instance && partyId) {
        const res = await searchPartyRow(restoreKey.partyType, partyId, true);
        row = res.row; emptySearch = res.empty;
      } else if (instance) {
        const res = await searchPartyRow(restoreKey.partyType, value);
        row = res.row; emptySearch = res.empty;
      }
      if (instance && row && row.party_id != null) {
        instance.setState({ [restoreKey.resultKey]: [row] });
        let data = null;
        let fillCustomer = null; // consignee branch fills this (one-pass Customer)
        if (lower === 'shipper') {
          data = {
            shipper_party_id: row.party_id,
            // Full HBL detail block — mirrors what handleShipperSelect applies
            // after its PRS-diff modal (name/address/tel/email included). A
            // minimal id+code payload leaves shipper_detail.name empty and the
            // save fails with "Name in HBL () doesn't align with the selected
            // shipper code" (2026-09-21 user report).
            shipper_detail: {
              party_code: row.party_code,
              office_code: row.office_code,
              fax: row.fax,
              master_name: row.full_name,
              name: row.full_name,
              address1: row.address1,
              address2: row.address2,
              address3: row.address3,
              address4: row.address4,
              city: row.city,
              city_name: row.city_name,
              state: row.state,
              zip: row.zip_postal,
              country_code: row.country_code,
              country_name: row.country_name,
              tel: row.tel,
              email: row.email,
            },
            shipper_party_code: row.party_code,
            shipper_party_status: row.status,
          };
        } else if (lower === 'consignee') {
          data = {
            consignee_party_id: row.party_id,
            consignee_detail: {
              party_code: row.party_code,
              office_code: row.office_code,
              fax: row.fax,
              master_name: row.full_name,
              name: row.full_name,
              address1: row.address1,
              address2: row.address2,
              address3: row.address3,
              address4: row.address4,
              city: row.city,
              city_name: row.city_name,
              state: row.state,
              zip: row.zip_postal,
              country_code: row.country_code,
              country_name: row.country_name,
              tel: row.tel,
              email: row.email,
            },
            consignee_party_code: row.party_code,
            consignee_party_status: row.status,
          };
          // Customer (fill-time, ONE PASS — user request 2026-10-05: the field
          // must show the customer on the form BEFORE the save card, not be
          // stamped during save_and_confirm). The consignee's own row when
          // it carries a REAL customer; otherwise a same-company sibling row
          // (unlinked consignees resolve to the company account, e.g.
          // SKECHERS JAPAN G.K. -> customer "Skechers USA Inc."); and when NO
          // real customer exists anywhere, the row's own generic "Other" —
          // exactly what the site's own handleConsigneeSelect writes for a
          // human picking this consignee, so the field fills in the SAME pass
          // instead of at save time. The save-time re-check still re-derives
          // when the field renders "Other", so this never blocks an upgrade.
          try {
            const derivedCustomer = !isGenericOtherCustomer(row)
              ? {
                customer_id: row.customer_id,
                customer_full_name: row.customer_full_name || '',
                customer_code: row.customer_code || '',
                via: `consignee row (${row.full_name})`,
              }
              : await deriveCustomerFromSelf(row, null);
            fillCustomer = (derivedCustomer && derivedCustomer.customer_id != null)
              ? derivedCustomer
              : {
                customer_id: row.customer_id,
                customer_full_name: row.customer_full_name || '',
                customer_code: row.customer_code || '',
              };
          } catch (e) {
            console.log('[BMS Tools] fill-time customer derivation failed:', e && e.message);
            fillCustomer = {
              customer_id: row.customer_id,
              customer_full_name: row.customer_full_name || '',
              customer_code: row.customer_code || '',
            };
          }
          if (fillCustomer.customer_id != null) {
            data.customer_id = fillCustomer.customer_id;
            data.customer_full_name = fillCustomer.customer_full_name;
            data.customer_code = fillCustomer.customer_code;
          }
        } else {
          data = {
            [restoreKey.soField]: row.party_id,
            [restoreKey.soField.replace('_party_id', '_party_name')]: row.full_name,
            [restoreKey.soField.replace('_party_id', '_party_code')]: row.party_code,
          };
        }
        const hc = instance.props && instance.props.handleChange;
        if (typeof hc === 'function') {
          // autosave=false: handleChange's autosave flag fires a server
          // round-trip per call (10-40s each on a loaded box — the batch cost
          // 160s for 4 parties) and is what seeds "auto save record" modals.
          // The explicit save happens moments later; no autosave needed.
          hc(data, false);
          // Stamp for executeTool: the direct context set bypasses the UI
          // select flow that arms the PRS modal, so its 12s popup wait can be
          // skipped for this action (see the fastPartySet check there).
          window.__fastPartySetAt = Date.now();
        } else {
          // legacy fallback: the real handler (slower, same effect)
          const opt = buildPartyOption(row);
          if (restoreKey.handler === 'handleAgentSelect') await instance.handleAgentSelect(restoreKey.agentType, row.party_id, opt);
          else await instance[restoreKey.handler](row.party_id, opt);
        }
        // antd form store must match too — Save validation reads it
        const form = instance.formRef && instance.formRef.current;
        if (form && typeof form.setFieldsValue === 'function') {
          const fields = { [restoreKey.soField]: row.party_id };
          // Consignee drives the required Customer field: the save validation
          // reads customer_id from the FORM STORE, and the Customer Select
          // renders from the customerResult list — without both it renders ""
          // and Save refuses with "Please fill in all required fields"
          // (2026-09-22 regression when the site's slow related-party
          // callback lost the race with the fast path). fillCustomer (above)
          // is the one-pass value: the row's own, a same-company sibling, or
          // the site's own "Other" default. GetCustomerOption reads
          // full_name/code off the list entries.
          if (lower === 'consignee' && fillCustomer && fillCustomer.customer_id != null) {
            fields.customer_id = fillCustomer.customer_id;
            const custName = fillCustomer.customer_full_name || '';
            instance.setState({ customerResult: [Object.assign({}, row, {
              customer_id: fillCustomer.customer_id,
              code: fillCustomer.customer_code || '',
              customer_code: fillCustomer.customer_code || '',
              customer_full_name: custName,
              full_name: custName,
            })] });
          }
          form.setFieldsValue(fields);
        }
        // brief poll for the context write (handleChange is sync-ish; the
        // slow handler fallback may need a beat)
        for (let p = 0; p < 10; p++) {
          const soId = instance.props && instance.props.so ? instance.props.so[restoreKey.soField] : null;
          if (soId != null && String(soId) === String(row.party_id)) return { ok: true, via: 'store', id: row.party_id };
          await realWait(300);
        }
      }
      if (partyId || emptySearch) {
        // id given but not found, or the party search answered EMPTY: honest
        // fast failure — the typed path would re-run the same doomed search.
        return { ok: false, via: partyId ? 'id-not-found' : 'empty-search' };
      }
    } catch (e) {
      console.log(`[BMS Tools] save: ${label} fast restore failed (${e.message}), falling back to select`);
    }
  }
  try {
    await selectOption({ label, value });
  } catch (e) { /* fall through to label check */ }
  const check = findFormFieldsByLabel(label);
  const now = (check.length ? (check[0].formItem.querySelector('.ant-select-selection-item')?.textContent || '') : '').trim().toLowerCase();
  const wantWords = String(value || '').split(/[^a-z0-9]+/).filter(w => w.length >= 2);
  const labelOk = !!(now && wantWords.every(w => now.includes(w)));
  return { ok: labelOk, via: 'label' };
}

// =============================================================================
// Details-tab fast path (2026-09-30) — the creation-booking method applied to
// the Details tab. The save payload is built from the `so` CONTEXT, so the old
// label-based DOM typing rendered briefly and was silently dropped at save
// time (and half the fields were "not found" because the form's real labels
// differ from the field map's card labels: "Contact Person" renders as
// "Booking Contact", "Gross Weight" as "Total KG", ...). Instead: locate the
// ShippingOrderDetails instance, write the antd form store AND push the value
// through its own onValuesChange -> handleChange context sync — exactly what
// restoreHeaderParty does for the header parties. Ports resolve against the
// same context port lists and go through the site's own handle*Select
// handlers (HBL-name autofill, PLD copy for port-to-port, sailing-form
// reload), the commodity resolves to an option key, notify handles the
// Same-as-Consignee switch, and the date stores a moment in the form plus a
// moment-parseable string in the context.
// =============================================================================

function findDetailsInstance(el) {
  if (!el) return null;
  const fiberKey = Object.keys(el).find(k => k.startsWith('__reactFiber$') || k.startsWith('__reactInternalInstance$'));
  if (!fiberKey) return null;
  let f = el[fiberKey];
  for (let depth = 0; f && depth < 200; depth++) {
    const n = f.stateNode;
    // Duck-type on the Details tab's own port handler — survives minification.
    if (n && typeof n.handlePODSelect === 'function' && n.formRef && typeof n.onValuesChange === 'function') return n;
    f = f.return;
  }
  return null;
}

function getDetailsInstance() {
  // Anchor on fields that only exist on the Details tab so we never bind the
  // Header component (which also has a formRef).
  const anchors = ['Port of Discharge', 'Total CBM', 'Cargo Ready Date', 'Incoterms Location', 'Marks & No'];
  for (const anchor of anchors) {
    const found = findFormFieldsByLabel(anchor);
    const formItem = found.length ? found[0].formItem : null;
    const el = formItem && formItem.querySelector('.ant-select, .ant-input-number, .ant-picker, input, textarea');
    if (el) {
      const inst = findDetailsInstance(el);
      if (inst) return inst;
    }
  }
  return null;
}

// Resolve a user string ("Jebel Ali DXB", "DXB", "hong kong") to a port row
// from the context port list: exact locode, exact name, then substring both
// ways — retrying with trailing tokens stripped ("Jebel Ali DXB" -> "Jebel
// Ali") because users append the city/airport code. Case-insensitive upgrade
// of the site's own (case-sensitive) name callback match.
function resolvePortFromList(portList, query) {
  if (!Array.isArray(portList) || portList.length === 0) return null;
  const norm = (s) => String(s || '').trim().toLowerCase();
  const q = norm(query);
  if (!q) return null;
  let m = portList.find(p => norm(p.unCode) === q) || portList.find(p => norm(p.name) === q);
  if (!m && q.length >= 3) {
    // airport/city code inside the UN/LOCODE ("DXB" matches "AEDXB")
    m = portList.find(p => norm(p.unCode).includes(q));
  }
  if (!m) {
    const toks = q.split(/\s+/);
    const tries = [q];
    for (let cut = 1; cut <= 2 && toks.length > cut; cut++) tries.push(toks.slice(0, toks.length - cut).join(' '));
    for (const t of tries) {
      if (!t) continue;
      m = portList.find(p => { const n = norm(p.name); return n.includes(t) || t.includes(n); });
      if (m) break;
    }
  }
  return m || null;
}

const _DETAILS_PORT_LISTS = {
  pol_locode: 'polList', por_locode: 'porList', pod_locode: 'podList',
  pld_locode: 'pldList', fd_locode: 'fdList',
};
const _DETAILS_PORT_HANDLERS = {
  pol_locode: 'handlePOLSelect', por_locode: 'handlePORSelect',
  pod_locode: 'handlePODSelect', pld_locode: 'handlePLDSelect',
  fd_locode: 'handleFDSelect',
};
const _DETAILS_NUMBER_FIELDS = new Set(['total_package_qty', 'total_cbm', 'total_gross_weight']);
const _DETAILS_UPPER_FIELDS = new Set(['marks_and_number', 'goods_description']);
const _DETAILS_BOOL_FIELDS = new Set(['is_dangerous_goods_confirm', 'is_same_as_consignee']);

async function fillDetailsField({ field, value }) {
  const inst = getDetailsInstance();
  if (!inst) return 'details form not found — is the Details tab open in edit mode?';
  const form = inst.formRef.current;
  const so = inst.props.so || {};
  field = String(field || '').trim();
  const strVal = String(value ?? '').trim();
  if (!strVal) return `${field}: empty value`;

  // Ports: resolve in the context port list, then run the site's own
  // selection handler — it fills locode + name + country, autofills the
  // paired *_name_hbl (required at save) and cascades PLD for port-to-port.
  if (_DETAILS_PORT_LISTS[field]) {
    const port = resolvePortFromList(inst.props[_DETAILS_PORT_LISTS[field]], strVal);
    if (!port) return `no port matching "${strVal}" in ${field}`;
    const opt = {
      port_name: port.name,
      countrycode: String(port.countryCode || '').toLowerCase(),
      countryname: port.countryName,
    };
    await Promise.resolve(inst[_DETAILS_PORT_HANDLERS[field]](port.unCode, opt));
    await waitForRender(500);
    // Synchronous store write after the handler: the handler updates the
    // context asynchronously (setState -> handleChange -> resetFields), and
    // the store value can lag a render behind — the save's antd validation
    // must never race it (2026-09-30: FD filled fine but the Save clicks ran
    // inside the lag window and validation failed three times).
    form.setFieldsValue({ [field]: port.unCode });
    return `Selected ${port.name} (${port.unCode}) in ${field}`;
  }

  // Commodity: the Select's value is the option KEY (pkey) — resolve the same
  // way the site's own filterCommodity matches (case-insensitive contains).
  // The instance's commodityList loads ASYNC after the tab mounts; when the
  // list is still empty, wait briefly and re-read before declaring no match
  // (2026-10-05: the fill ran ahead of the load and the save was then refused
  // with 'Please select a Commodity').
  if (field === 'commodity') {
    let list = (inst.state && inst.state.commodityList) || [];
    if (list.length === 0) {
      for (let w = 0; w < 8 && list.length === 0; w++) {
        await realWait(500);
        list = (inst.state && inst.state.commodityList) || [];
      }
    }
    const q = strVal.toLowerCase();
    let m = list.find(p => String(p.pkey).toLowerCase() === q || String(p.pvalue).toLowerCase() === q);
    if (!m) m = list.find(p => {
      const k = String(p.pkey).toLowerCase(); const v = String(p.pvalue).toLowerCase();
      return k.includes(q) || v.includes(q) || q.includes(k);
    });
    if (!m) return `no commodity matching "${strVal}"`;
    form.setFieldsValue({ commodity: m.pkey, commodity_extra_info: '' });
    inst.onValuesChange({ commodity: m.pkey, commodity_extra_info: '' });
    return `Selected commodity "${m.pkey}"`;
  }

  // Notify party: header-party-style fast path. Same party as the consignee
  // keeps the site's own "Same as Consignee" copy (its canonical form);
  // a different party switches it off and pushes the full notify payload
  // (with HBL detail block) through the context.
  if (field === 'notify_party_id') {
    const res = await searchPartyRow('consignees', strVal);
    const row = res.row;
    if (!row || row.party_id == null) return `could not resolve notify party "${strVal}"`;
    if (String(row.party_id) === String(so.consignee_party_id || '')) {
      if (!so.is_same_as_consignee) {
        await Promise.resolve(inst.handleSameAsConsigneeChange(true));
        await waitForRender(400);
      }
      return `Notify party = consignee (${row.full_name}) via Same as Consignee`;
    }
    const payload = {
      is_same_as_consignee: false,
      notify_party_id: row.party_id,
      notify_party_code: row.party_code,
      notify_party_status: row.status,
      notify_detail: {
        party_code: row.party_code, office_code: row.office_code, fax: row.fax,
        master_name: row.full_name, name: row.full_name,
        address1: row.address1, address2: row.address2, address3: row.address3, address4: row.address4,
        city: row.city, city_name: row.city_name, state: row.state, zip: row.zip_postal,
        country_code: row.country_code, country_name: row.country_name,
        tel: row.tel, email: row.email,
      },
    };
    await new Promise(resolve => {
      let done = false;
      const fin = () => { if (!done) { done = true; resolve(); } };
      if (inst.props.handleChange) inst.props.handleChange(payload, true, fin);
      setTimeout(fin, 4000);
    });
    form.setFieldsValue({ notify_party_id: row.party_id });
    if (inst.state && inst.state.useConsigneeList) inst.setState({ useConsigneeList: false });
    return `Selected notify party "${row.full_name}" (${row.party_code})`;
  }

  // Date: the DatePicker wants a moment in the form store; the context keeps
  // a moment-parseable string (the site's save converts with moment()).
  if (field === 'cargo_ready_date') {
    const m = moment(strVal, ['YYYY-MM-DD', 'DD/MM/YYYY']);
    if (!m.isValid()) return `invalid date "${strVal}" (expected YYYY-MM-DD)`;
    form.setFieldsValue({ cargo_ready_date: m });
    inst.onValuesChange({ cargo_ready_date: m.format('YYYY-MM-DD') });
    return `Set Cargo Ready Date to ${strVal}`;
  }

  // Generic text / number / checkbox fields: form store + context in one shot.
  let parsed = strVal;
  if (_DETAILS_NUMBER_FIELDS.has(field)) {
    // lenient: slot values can arrive with units ("120 cartons", "9,500 kg")
    const num = strVal.match(/-?\d[\d,]*(?:\.\d+)?/);
    if (!num) return `${field}: no number in "${strVal}"`;
    parsed = Number(num[0].replace(/,/g, ''));
    if (Number.isNaN(parsed)) return `${field}: "${strVal}" is not a number`;
  } else if (_DETAILS_BOOL_FIELDS.has(field)) {
    parsed = /^(true|on|yes|1|checked)$/i.test(strVal); // valuePropName='checked'
  } else if (_DETAILS_UPPER_FIELDS.has(field)) {
    parsed = strVal.toUpperCase(); // the site uppercases these on blur
  }
  form.setFieldsValue({ [field]: parsed });
  inst.onValuesChange({ [field]: parsed });
  return `${field}: set to "${strVal}"`;
}

// =============================================================================
// Shipping Instruction container lines (2026-10-13)
// =============================================================================

// The SO screen's context provider owns so.containers (addContainer /
// changeContainerLine, context.js) — the same code path the SI tab's own
// "Add a container line" button drives. Duck-type on those two methods.
function getSoContextInstance() {
  const anchors = ['Port of Discharge', 'Total CBM', 'Cargo Ready Date',
                   'Incoterms Location', 'Marks & No', 'Shipper'];
  for (const anchor of anchors) {
    const found = findFormFieldsByLabel(anchor);
    const formItem = found.length ? found[0].formItem : null;
    const el = formItem && formItem.querySelector('.ant-select, .ant-input-number, .ant-picker, input, textarea');
    if (!el) continue;
    const fiberKey = Object.keys(el).find(k => k.startsWith('__reactFiber$') || k.startsWith('__reactInternalInstance$'));
    if (!fiberKey) continue;
    let f = el[fiberKey];
    for (let depth = 0; f && depth < 300; depth++) {
      const n = f.stateNode;
      if (n && typeof n.addContainer === 'function'
          && typeof n.changeContainerLine === 'function'
          && n.state && n.state.so) return n;
      f = f.return;
    }
  }
  return null;
}

/**
 * Make the SI container lines carry the booking totals.
 *
 * The Draft-HBL print gate (HouseDoc.js confirmPrintModalOnOK) sums
 * so.containers' cbm / gross_weight / package_qty and refuses to print when
 * they don't match the SO totals — a booking whose cargo lives only in the
 * Details totals has an empty SI grid and every print dies with "Total CBM
 * between container (0.0000) and SO (28.4) is not align." (observed
 * 2026-10-13, SO S20261046). This action adds/fills container line(s) through
 * the site's own addContainer / changeContainerLine so the save persists them:
 *   - targets default to the SO's own totals when not passed explicitly;
 *   - idempotent: already-aligned rows are left alone (skipped);
 *   - user-valued rows that MISalign are never touched (failure text — the
 *     flow's recovery takes over, this action never guesses a split);
 *   - rows that exist but carry no values get the totals written into row 0;
 *   - empty grid: one line per container_qty, totals split evenly, remainder
 *     on the last line (a single container carries the full totals).
 */
async function ensureSiContainers({ package_qty, gross_weight, cbm, container_size, container_qty }) {
  const inst = getSoContextInstance();
  if (!inst) return 'so context not found — open the booking for editing first';
  const so = inst.state.so;
  if (!so) return 'so context not found — no booking loaded';
  if (!Array.isArray(so.containers)) so.containers = [];

  const num = (v, dflt) => {
    if (v !== undefined && v !== null && String(v).trim() !== '') {
      const m = String(v).match(/-?\d[\d,]*(?:\.\d+)?/);
      if (m) return Number(m[0].replace(/,/g, ''));
    }
    return dflt;
  };
  const tPkg = num(package_qty, num(so.total_package_qty, null));
  const tKg = num(gross_weight, num(so.total_gross_weight, null));
  const tCbm = num(cbm, num(so.total_cbm, null));
  if (tPkg == null || tKg == null || tCbm == null) {
    return 'si containers: no totals available (pass package_qty / gross_weight / cbm)';
  }

  const sum = (fld) => so.containers.reduce((s, c) => s + (parseFloat(c[fld]) || 0), 0);
  // same precisions the print gate compares at (toFixed(4)/(3)/(2))
  const eq = (a, b, dp) => Math.abs(a - b) < Math.pow(10, -dp) / 2;
  const aligned = () => eq(sum('cbm'), tCbm, 4) && eq(sum('gross_weight'), tKg, 3) && eq(sum('package_qty'), tPkg, 2);

  if (so.containers.length > 0) {
    if (aligned()) {
      return `si containers: already aligned (CBM ${tCbm}, KG ${tKg}, PKG ${tPkg}) — skipped`;
    }
    const allUnvalued = so.containers.every(c =>
      String(c.cbm ?? '').trim() === '' && String(c.gross_weight ?? '').trim() === '' && String(c.package_qty ?? '').trim() === '');
    if (!allUnvalued) {
      return `si containers: existing ${so.containers.length} row(s) sum to CBM ${sum('cbm')}, KG ${sum('gross_weight')}, PKG ${sum('package_qty')} — expected ${tCbm} / ${tKg} / ${tPkg}; not touching user-valued rows`;
    }
    // rows exist but carry nothing: write the totals into the first row
    inst.changeContainerLine(0, 'cbm', tCbm);
    inst.changeContainerLine(0, 'gross_weight', tKg);
    inst.changeContainerLine(0, 'package_qty', tPkg);
    try { inst.formRef && inst.formRef.instruction && inst.formRef.instruction.current && inst.formRef.instruction.current.setFieldsValue({ containers: so.containers }); } catch (e) { /* form not mounted — so carries the values */ }
    await waitForRender(300);
    return `si containers: filled row 1 with CBM ${tCbm}, KG ${tKg}, PKG ${tPkg} to match the booking totals`;
  }

  const rows = Math.max(1, Math.min(20, parseInt(container_qty, 10) || 1));
  const size = String(container_size || '').trim() || null;
  const firstIdx = so.containers.length;
  for (let i = 0; i < rows; i++) inst.addContainer(null, size, so);
  // split at the gate's own precision; the last row absorbs the remainder so
  // the sums land exactly on the totals
  const part = (total, i, dp) => {
    const scale = Math.pow(10, dp);
    const base = Math.round((total / rows) * scale) / scale;
    if (i < rows - 1) return base;
    return Math.round((total - base * (rows - 1)) * scale) / scale;
  };
  for (let i = 0; i < rows; i++) {
    const idx = firstIdx + i;
    inst.changeContainerLine(idx, 'cbm', part(tCbm, i, 4));
    inst.changeContainerLine(idx, 'gross_weight', part(tKg, i, 3));
    inst.changeContainerLine(idx, 'package_qty', part(tPkg, i, 2));
  }
  try { inst.formRef && inst.formRef.instruction && inst.formRef.instruction.current && inst.formRef.instruction.current.setFieldsValue({ containers: so.containers }); } catch (e) { /* form not mounted — so carries the values */ }
  await waitForRender(300);
  return `si containers: added ${rows} container line(s) (size ${size || 'site default'}) carrying CBM ${tCbm}, KG ${tKg}, PKG ${tPkg} to match the booking totals`;
}

/**
 * Type the approval remark into the Booking Approval tab's BraftEditor.
 *
 * The remark text on that tab is a rich-text editor (a draft-js contenteditable
 * inside .RemarkBraftEditor) with no antd label/input the label-based fillers
 * can target ("New Remarks" is the label of the Remark TYPE select, and the
 * old textarea is commented out in BookingApproval.js), so the deterministic
 * approve flow drives it through this dedicated batch action — the same
 * pattern as the Details tab's fill_details_field fast path. select-all +
 * execCommand('insertText') produces the real input events draft-js listens
 * for, so the site's own onChange wires the value into the form state the
 * save posts.
 */
async function fillApprovalRemarks({ value }) {
  const strVal = String(value ?? '').trim();
  if (!strVal) return 'remarks: empty value';

  const pane = document.querySelector('.ant-tabs-tabpane-active');
  if (!pane) return 'remarks editor not found — no active tab pane';

  // The remarks row carries a REQUIRED "Select a Remark" type selector before
  // the editor (BookingApproval.js FormItem4 remark_type, rules required) —
  // the approval Save is refused with "Please fill in the required column(s)"
  // when it is empty, no matter that the editor text is filled (2026-10-05).
  // Pick its first option, exactly what a human does.
  const typeSelect = [...pane.querySelectorAll('.ant-select')].find(function (s) {
    const ph = s.querySelector('.ant-select-selection-placeholder');
    const fi = s.closest('.ant-form-item');
    return (ph && /select a remark/i.test(ph.textContent || ''))
      || (fi && /new remarks|remark/i.test((fi.querySelector('.ant-form-item-label') || {}).textContent || '') && !s.querySelector('.ant-select-selection-item'));
  });
  let typePicked = '';
  if (typeSelect && !typeSelect.classList.contains('ant-select-disabled')) {
    typeSelect.querySelector('.ant-select-selector').dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
    for (let w = 0; w < 10; w++) {
      await waitForRender(300);
      const dds = [...document.querySelectorAll('.ant-select-dropdown')].filter(function (d) { return d.getBoundingClientRect().height > 0; });
      if (dds.length) {
        const opt = dds[dds.length - 1].querySelector('.ant-select-item-option:not(.ant-select-item-option-disabled)');
        if (opt) {
          opt.click();
          typePicked = (opt.textContent || '').trim();
        }
        break;
      }
    }
    await waitForRender(500);
  }

  const editor = pane.querySelector('.RemarkBraftEditor .public-DraftEditor-content')
    || pane.querySelector('.public-DraftEditor-content');
  if (!editor || editor.getAttribute('contenteditable') !== 'true') {
    return 'remarks editor not found — open the Booking Approval tab first';
  }

  editor.focus();
  const sel = window.getSelection();
  const range = document.createRange();
  range.selectNodeContents(editor);
  sel.removeAllRanges();
  sel.addRange(range);
  let inserted = false;
  try {
    const dt = new DataTransfer();
    dt.setData('text/plain', strVal);
    const pasteEvt = new ClipboardEvent('paste', { bubbles: true, cancelable: true, clipboardData: dt });
    editor.dispatchEvent(pasteEvt);
    await waitForRender(500);
    inserted = (editor.innerText || '').includes(strVal);
  } catch (e) { /* fall through to execCommand */ }
  if (!inserted) {
    inserted = document.execCommand('insertText', false, strVal);
    await waitForRender(600);
  }

  const now = (editor.innerText || '').trim();
  const landed = now.toLowerCase().includes(strVal.toLowerCase().slice(0, 40));
  if (!inserted && !landed) {
    return 'could not type into the remarks editor (insertText failed)';
  }
  return `Remarks: filled "${strVal.slice(0, 120)}"${typePicked ? ` (remark type: ${typePicked})` : ''}`;
}

async function saveAndConfirm({ button_name, expect_popup, verify_read }) {
  const btn = button_name || 'Save';
  const steps = [];

  // Duplicate-create guard (2026-10-22 live): a re-execution of this save
  // (widget watchdog cancel+resend after a render desync, or an engine grace
  // re-emit) used to click Save AGAIN on the still-mounted create form and
  // create a SECOND booking (S202610117 + S202610118 from one request). A
  // re-execution never re-navigates (the checkpoint is the save step), so a
  // stamped SO number on the create form means THIS form session already
  // committed — report it instead of clicking. A fresh create always passes
  // through navigateToPage, which clears the stamp on the create route.
  if (/shipping_order\/new(\?|#|$)/.test(location.pathname)
      && window.__lastSaveSoNo
      && (Date.now() - (window.__lastSaveSoNoAt || 0)) < 15 * 60 * 1000) {
    const so = window.__lastSaveSoNo;
    console.warn('[Chatbot] save_and_confirm: duplicate-create guard — already saved as ' + so + ' in this form session, NOT clicking Save again');
    return { content: [{ type: 'text', text:
      `RESULT: success (save POST observed; the server accepted the save as ${so})\n`
      + `Created SO#: ${so}\n`
      + `[dup-guard] a Save from this form session already created ${so} — the repeat Save click was suppressed to avoid a duplicate booking.`
      + (expect_popup ? '' : '') }] };
  }

  // Step -1: ALWAYS flush unanswered PRS / auto-save modals before the Save
  // click (2026-10-12: opening a tab for edit pops "An auto save record is
  // found…" — for the DETAILS save (no header parties) the old flush lived
  // inside the party-repair guard below and never ran, the open modal
  // swallowed the Save click, the flow's DOM read-back then "verified" the
  // still-filled-but-UNSAVED form, and every later confirm failed validation
  // on a booking whose details were never committed). The flush is a
  // precondition of the click itself, not of the party repair.
  for (let flush = 0; flush < 3; flush++) {
    const pendingModal = [...document.querySelectorAll('.ant-modal-wrap')]
      .map(wrap => wrap.querySelector('.ant-modal'))
      .find(modal => modal && /overwrite the following e-Booking info by PRS info|auto save record is found/i.test(modal.textContent || '') && modal.getBoundingClientRect().width > 0);
    if (!pendingModal) break;
    const body = pendingModal.textContent || '';
    const want = /auto save record is found/i.test(body) ? 'No' : 'Confirm';
    const footer = pendingModal.querySelector('.ant-modal-confirm-btns') || pendingModal.querySelector('.ant-modal-footer');
    const btn2 = footer ? [...footer.querySelectorAll('button')].find(b => new RegExp('^' + want + '$', 'i').test((b.textContent || '').trim())) : null;
    if (!btn2) break;
    console.log(`[BMS Tools] save: flushing unanswered modal with "${want}"`);
    btn2.click();
    await realWait(900);
  }

  // Step 0: VERIFY the header parties the fill batch set — repair is now the
  // EXCEPTION, not the routine. The fill pass absorbs the consignee's wipe
  // chain before the confirm card (batchFormActions watch loop, 2026-10-05),
  // so this normally finds everything holding and does nothing. Only a wipe
  // landing between that window and this click (slow chain, or the user sat
  // on the card for a long time) triggers a re-apply — which re-arms the
  // chain, and then (and only then) the stabilization gate below runs.
  let repairedAny = false;
  if (_lastHeaderParties && _lastHeaderParties.size > 0) {
    reportProgress('Verifying header parties before Save…');
    for (const [label, value] of _lastHeaderParties) {
      const found = findFormFieldsByLabel(label);
      if (found.length === 0) continue;
      const current = readHoldValueText(found).toLowerCase();
      if (current) continue; // still set — nothing to do
      repairedAny = true;
      reportProgress(`Re-applying ${label}…`);
      console.log(`[BMS Tools] save: ${label} was wiped, restoring "${value}"`);
      // Direct restore (NOT executeTool): after a reset the party's own
      // detail fetch re-clears the field ~1-2 min later, so the gap between
      // this re-apply and the Save click must be as short as possible.
      //
      // Primary path — the site's OWN select handlers (Header.handleShipperSelect
      // / handleConsigneeSelect / handleAgentSelect): exactly what a human's
      // dropdown click runs, WITHOUT the resetFields tail that the party_id
      // restore path carries (resetFields on the create form IS the wipe
      // mechanism — the consignee's related-party callback nulls the agents
      // and resets the whole header when it finally resolves). The row comes
      // from the same search endpoint the dropdown uses, so both the payload
      // context (so.xxx_party_id) and the rendered label update.
      // Fallback path — the UI primitive selectOption (typed dropdown pick).
      // Bounded retries with GROWING waits: the wipe fires from the consignee's
      // async related-party callback, which can land a minute+ after the
      // selection on a loaded box — restore BEFORE it lands and the field is
      // blanked again (2026-09-18: both agents still empty after 4 x 2.5s).
      const REAPPLY_WAITS = [2500, 2500, 4000, 6000, 8000, 10000, 12000];
      const restoreKey = SAVE_PARTY_RESTORE_KEYS[label];
      for (let attempt = 1; attempt <= REAPPLY_WAITS.length + 1; attempt++) {
        let ok;
        if (restoreKey) {
          ok = await restoreHeaderParty(label, value, restoreKey,
            _lastHeaderPartyIds ? _lastHeaderPartyIds.get(label) : undefined);
        } else {
          // Plain value field (Salesman / Business Type): no party context to
          // restore — re-run the control's own select (radio-aware).
          ok = await reapplyHoldValue(label, value);
        }
        if (ok.ok) {
          steps.push(`[re-apply] ${label}: restored "${value}" (attempt ${attempt}${ok.via === 'store' ? ', set in form+context' : ''})`);
          break;
        }
        if (ok.via === 'empty-search' || ok.via === 'id-not-found') {
          // Definitive: the party search cannot find this party at all, so
          // waiting and retrying cannot succeed — stop burning the growing
          // waits (45s per party) and let the save validation speak.
          steps.push(`[re-apply] ${label}: party not found in the BMS party master ("${value}") — no further retries`);
          break;
        }
        if (attempt <= REAPPLY_WAITS.length) {
          console.log(`[BMS Tools] save: ${label} still empty after restore attempt ${attempt}, retrying in ${REAPPLY_WAITS[attempt - 1]}ms...`);
          await realWait(REAPPLY_WAITS[attempt - 1]);
        } else {
          steps.push(`[re-apply] ${label}: could not restore "${value}" after ${attempt} attempts`);
        }
      }
    }

    // Step 0a — CUSTOMER (verify-only safety net). The fill pass now derives
    // and writes the Customer together with the Consignee (restoreHeaderParty,
    // 2026-10-05), so this block should find the field already holding the
    // real customer and do nothing. It only acts when the field is empty or
    // renders the generic "Other" — e.g. the site's related-party chain
    // re-stamped it, or the fill-time derivation could not resolve one.
    // Resolution order (deriveConsigneeCustomer): the selected consignee's own
    // row, then a same-company sibling row; "Other" remains only the LAST
    // resort so Save's required-field validation still passes.
    try {
      const custFound = findFormFieldsByLabel('Customer');
      const curCust = readHoldValueText(custFound).trim().toLowerCase();
      if (custFound.length && (!curCust || curCust === 'other')) {
        const cnsFound = findFormFieldsByLabel('Consignee');
        const cnsSel = cnsFound.length ? cnsFound[0].formItem.querySelector('.ant-select') : null;
        const inst = cnsSel ? findHeaderInstance(cnsSel) : null;
        const derived = inst ? await deriveConsigneeCustomer(inst) : null;
        if (inst && derived && derived.customer_id != null) {
          applyConsigneeCustomer(inst, derived);
          steps.push(`[customer] re-derived at save (${derived.via}): customer_id ${derived.customer_id} (${derived.customer_full_name})`);
        } else {
          const r = await executeTool('select_option', { label: 'Customer', value: 'Other' });
          const t2 = (r && r.result?.content?.[0]?.text) || (r && r.error) || 'Done';
          steps.push(`[customer] no customer resolved via the consignee search — set to Other: ${t2}`);
        }
      }
    } catch (e) { steps.push(`[customer] derivation failed: ${e.message}`); }

    // Step 0a2 — NOTIFY PARTY. The save demands a notify (validateSO refuses an
    // empty notify_party_id), but the create page renders NO Notify field — the
    // Details tab that owns it is filtered out of the tab list in create mode —
    // and the site's related-party callback NULLS the notify when the customer
    // has no configured default (customer "Other"), so the create hard-fails at
    // Save for human and chatbot alike (2026-09-19 UAT "notify_party_id" binding
    // error). ~80% of existing bookings store notify = consignee, and the site's
    // own "Same as consignee" checkbox (Details tab) is the canonical way to
    // express that, so when notify is EMPTY and a consignee is set, apply the
    // exact same-as-consignee shape through the site's OWN handleChange (party
    // id/code/status := consignee, notify_detail := consignee_detail with a
    // blanked address and name "SAME AS CONSIGNEE", is_same_as_consignee := true
    // — which also skips the notify-detail save checks). Fires only when notify
    // is EMPTY: a customer's CONFIGURED notify default (e.g. a US customs
    // broker) is never overwritten. Fail-open: any error logs a step and lets
    // the save validation speak.
    try {
      const cnsNotify = findFormFieldsByLabel('Consignee');
      const cnsSelNotify = cnsNotify.length ? cnsNotify[0].formItem.querySelector('.ant-select') : null;
      const soInst = cnsSelNotify ? findHeaderInstance(cnsSelNotify) : null;
      const soNow = soInst && soInst.props ? soInst.props.so : null;
      if (soInst && soNow && !parseInt(soNow.notify_party_id) && parseInt(soNow.consignee_party_id)) {
        const notifyPayload = {
          notify_party_id: soNow.consignee_party_id,
          notify_party_code: soNow.consignee_party_code ||
            (soNow.consignee_detail && soNow.consignee_detail.party_code) || '',
          notify_party_status: soNow.consignee_party_status || 'A',
          notify_detail: Object.assign({}, soNow.consignee_detail || {},
            { address1: '', address2: '', address3: '', address4: '', name: 'SAME AS CONSIGNEE' }),
          is_same_as_consignee: true,
        };
        await new Promise((resolve, reject) => {
          const timer = setTimeout(() => reject(new Error('handleChange callback timed out')), 5000);
          soInst.props.handleChange(notifyPayload, false, () => { clearTimeout(timer); resolve(); });
        });
        steps.push(`[notify] empty -> set to SAME AS CONSIGNEE (consignee party ${soNow.consignee_party_id})`);
      }
    } catch (e) { steps.push(`[notify] same-as-consignee default skipped: ${e.message}`); }

    // Step 0b — STABILIZATION GATE (conditional, 2026-10-05). Re-applying a
    // party re-arms the site's related-party callback: seconds later it nulls
    // the Nomination Agent and re-defaults the Release Agent (observed
    // 2026-09-21: nom -> null, rel -> default 625483), so a
    // restore-then-immediately-click loses the race and Save is refused with
    // "Please fill in all required fields". The gate resolves each intended
    // party's id ONCE, then polls: any field whose context id drifted (nulled
    // OR re-defaulted) is re-applied; the click only happens after one full
    // clean pass followed by a settle window with no drift.
    // SKIP it when there is nothing pending: the fill batch already absorbed
    // the wipe chain (_lastFillSettledAt) and Step 0 above found no wiped
    // field — no cascade is in flight, so the settle rounds and the 4 party
    // searches were pure dead time on every clean save (user request
    // 2026-10-05: "save and confirm" must stop re-waiting on parties that
    // are already set).
    if (!repairedAny && _lastFillSettledAt) {
      steps.push('[stabilize] skipped — header settled during the fill pass; nothing needed repair');
    } else {
    const partyTargets = [];
    for (const [label, value] of _lastHeaderParties) {
      const key = SAVE_PARTY_RESTORE_KEYS[label];
      if (!key) {
        // Plain value field (Salesman / Business Type): held on the RENDERED
        // text, not a context id — the settle loop compares the label below.
        // (Skip the header-instance lookup: Business Type is a radio group
        // with no .ant-select, and findHeaderInstance(null) throws.)
        partyTargets.push({ label, value, key: null, inst: null, targetId: null });
        continue;
      }
      const found = findFormFieldsByLabel(label);
      const inst = found.length ? findHeaderInstance(found[0].formItem.querySelector('.ant-select')) : null;
      let targetId = null;
      try {
        const row = await searchPartyRow(key.partyType, value);
        targetId = row ? row.party_id : null;
      } catch (e) { /* search hiccup — fall back to context-equality checks */ }
      partyTargets.push({ label, value, key, inst: null, targetId });
    }
    let stablePasses = 0;
    for (let round = 1; round <= 8 && stablePasses < 2; round++) {
      reportProgress(`Waiting for the header to settle (round ${round}/8)…`);
      await realWait(round === 1 ? 2500 : 2000);
      let allHeld = true;
      let aliveDrift = false; // at least one drifted field that can still be fixed
      for (const t of partyTargets) {
        if (t.dead) continue; // definitively unfixable (party not found) — skip
        const found = findFormFieldsByLabel(t.label);
        if (found.length === 0) continue;
        let inst = null;
        if (t.key) {
          inst = findHeaderInstance(found[0].formItem.querySelector('.ant-select'));
          if (!inst) continue;
          t.inst = inst;
        }
        let holds;
        if (t.key) {
          const soId = inst.props && inst.props.so ? inst.props.so[t.key.soField] : undefined;
          holds = t.targetId != null
            ? String(soId) === String(t.targetId)
            : (soId !== undefined && soId !== null && soId !== '');
        } else {
          // Plain value field (radio or select): the rendered selection must
          // still cover the intended value ("Nomination",
          // "SEA FREIGHT NOMINATION (9000)").
          const current = readHoldValueText(found).toLowerCase();
          const want = String(t.value || '').trim().toLowerCase();
          const wantWords = want.split(/[^a-z0-9]+/).filter(w => w.length >= 2);
          holds = !!current && (current.includes(want)
            || (wantWords.length > 0 && wantWords.every(w => current.includes(w))));
        }
        if (!holds) {
          allHeld = false;
          console.log(`[BMS Tools] save: stabilize round ${round}: ${t.label} drifted, re-applying "${t.value}"`);
          const ok = t.key
            ? await restoreHeaderParty(t.label, t.value, t.key,
                _lastHeaderPartyIds ? _lastHeaderPartyIds.get(t.label) : undefined)
            : await reapplyHoldValue(t.label, t.value);
          if (!ok.ok && (ok.via === 'empty-search' || ok.via === 'id-not-found' || ok.via === 'no-option')) {
            // Definitive failure — re-applying every round just adds ~20s of
            // dead rounds to the save. Mark and skip it from here on.
            t.dead = true;
            steps.push(`[stabilize] ${t.label}: "${t.value}" cannot be set (${ok.via}) — skipped`);
          } else {
            aliveDrift = true;
            steps.push(`[stabilize] ${t.label}: ${ok.ok ? `re-applied "${t.value}"` : 're-apply failed'}`);
          }
        }
      }
      stablePasses = allHeld ? stablePasses + 1 : 0;
      // Every drifted field is definitively unfixable: more rounds cannot
      // converge, and the Save validation will refuse the click honestly.
      if (!allHeld && !aliveDrift && round >= 2) break;
    }
    if (stablePasses >= 2) steps.push('[stabilize] all header parties held through the settle window');
    }
  }

  // Step 1: click the Save button — VERIFIED. A click can be swallowed when
  // the header re-renders right after the Step-0 restore (the button node is
  // replaced between lookup and click): no popup, no message, and crucially
  // no save POST — the flow then hard-stops believing the save failed while
  // nothing was ever submitted (observed 2026-09-21). The XHR hook stamps
  // __lastSavePostAt when the SO-save POST actually fires, so retry the click
  // (with a settle) until the request is observed, bounded to 3 attempts.
  // The click tool's evidence may contain an "ACTIVE POPUPS" detection block
  // (it SEES the Warning but does not answer it) — strip it: this tool's own
  // Step 2 answers the chain, and the server's leftover-popup gate must judge
  // the END state, not the mid-flight detection (2026-09-21: gate false-fired
  // on the click evidence and hard-stopped a save that was mid-confirm).
  // The create/edit header sits under a react-router <Prompt while editable,
  // so the post-save redirect fires the NATIVE "Are you sure you want to
  // leave?" window.confirm — it freezes ALL page JS until a human answers.
  // While it was up this tool's checks found nothing, and after a "Leave" the
  // page was elsewhere: S20260990/S20260991 both committed yet the flow
  // reported unknown/failure and hard-stopped (observed 2026-09-21). The
  // commit is already user-approved at the save card, so while this tool owns
  // the page, auto-answer ONLY that leave guard; any other confirm still goes
  // to the real dialog. try/finally below: every exit path hands the page
  // back with the original confirm restored.
  const _prevWindowConfirm = window.confirm;
  window.confirm = function (message) {
    if (/\bleave\b/i.test(String(message || ''))) {
      console.warn('[Chatbot] save_and_confirm: auto-confirmed leave guard:', message);
      return true;
    }
    return _prevWindowConfirm(message);
  };
  const saveSoNoStampBefore = window.__lastSaveSoNoAt || 0;
  const postStampBefore = window.__lastSavePostAt || 0;
  // Create-form discriminator for the post-create landing fix below: only a
  // save from /shipping_order/new redirects to ?editable=true&create_success.
  const wasCreateForm = /shipping_order\/new(\?|#|$)/.test(location.pathname);
  try {
    let clickText = '';
    try {
      reportProgress(`Clicking ${btn}…`);
      let r = await executeTool('click_button', { button_name: btn });
      if (r && r.error) {
        return { content: [{ type: 'text', text: `save_and_confirm failed at click_button(${btn}): ${r.error}` }] };
      }
      clickText = (r && r.success && r.result?.content?.[0]?.text) ? r.result.content[0].text : '';
      clickText = clickText.replace(/\n*⚠️\s*ACTIVE POPUPS:[\s\S]*$/g, ' (confirm popup detected — handled below)');
      steps.push(`Clicked ${btn}${clickText ? ': ' + clickText : ''}`);
      for (let attempt = 2; attempt <= 3; attempt++) {
        await pollUntil(() => (window.__lastSavePostAt || 0) > postStampBefore, 2500);
        if ((window.__lastSavePostAt || 0) > postStampBefore) break; // save POST is away
        const popupProbe = document.querySelector('.ant-modal-confirm, .ant-modal-wrap .ant-modal');
        if (popupProbe && popupProbe.getBoundingClientRect().width > 0) break; // chain started
        console.log(`[BMS Tools] save: no POST/popup after click ${attempt - 1}, clicking again`);
        r = await executeTool('click_button', { button_name: btn });
        const t2 = (r && r.success && r.result?.content?.[0]?.text) ? r.result.content[0].text : '';
        steps.push(`Clicked ${btn} again (attempt ${attempt})${t2 ? ': ' + t2.replace(/\n*⚠️\s*ACTIVE POPUPS:[\s\S]*$/g, ' (confirm popup detected — handled below)') : ''}`);
      }
      steps.push((window.__lastSavePostAt || 0) > postStampBefore
        ? 'Save POST observed (request left the browser)'
        : 'Save POST not observed within the click window');
    } catch (err) {
      return { content: [{ type: 'text', text: `save_and_confirm failed at click_button(${btn}): ${err.message}` }] };
    }

    // Step 2: drain the confirm-popup chain. BMS pops a SEQUENCE after Save:
    // the agent-default Warning → Yes, then a "send email" confirm → answer No
    // (never send). Each must be answered for the save to complete; the save
    // POST only fires once the email confirm is answered (its onOk/onCancel
    // callback is what calls saveSO). Rounds are generous because on a loaded
    // box each popup mounts seconds after the previous one is answered
    // (2026-09-21: 4 rounds x 5s gave up before the email popup mounted and
    // the save never fired).
    let popupText = '';
    if (expect_popup) {
      for (let round = 0; round < 6; round++) {
        // Popups historically mount 1-3s after the previous answer; 8s/5s
        // windows cover slow boxes without adding ~20s of dead waits to a
        // popup-free save (the old 12s/8s windows did exactly that).
        reportProgress(round === 0 ? 'Waiting for the save confirmation popup…'
                                   : `Checking for the next popup (round ${round + 1}/6)…`);
        // Popup-less saves happen when the site skips its confirm chain (the
        // parties already match the site defaults — no agent Warning, no email
        // prompt) and the save POST fires straight away. Race the popup wait
        // against the POST stamp so that case ends the drain immediately
        // instead of idling the full window (S2026107: 8s of dead wait,
        // 2026-10-05).
        const modal = await Promise.race([
          waitForPopupEl(round === 0 ? 8000 : 5000),
          pollUntil(() => (window.__lastSavePostAt || 0) > postStampBefore,
            round === 0 ? 8000 : 5000).then(() => null),
        ]);
        if (!modal) { popupText = 'No confirm popup appeared (save may have completed without one)'; steps.push(popupText); break; }
        const title = (modal.querySelector('.ant-modal-confirm-title, .ant-modal-title')?.textContent || '').trim();
        const body = (modal.textContent || '');
        const footer = modal.querySelector('.ant-modal-confirm-btns') || modal.querySelector('.ant-modal-footer');
        const btns = footer ? [...footer.querySelectorAll('button')] : [];
        let target;
        if (/send email/i.test(body)) {
          // Playbook rule: never send the booking email — pick the No/cancel button.
          target = btns.find(b => /^(no|don.?t|cancel)/i.test((b.textContent || '').trim()));
        } else {
          target = btns.find(b => /^(yes|ok|confirm|continue)$/i.test((b.textContent || '').trim()))
            || btns.find(b => b.classList.contains('ant-btn-primary'));
        }
        if (!target) { popupText = `Popup "${title}" had no clickable button`; steps.push(popupText); break; }
        const label = (target.textContent || '').trim();
        target.click();
        popupText = `Answered "${title || 'confirm'}" with "${label}"`;
        steps.push(popupText);
        // Early exit: the save POST fires as soon as the email confirm is
        // answered (its handler calls saveSO) — once observed, the chain is
        // done and lingering in the per-round waits just idles (speed pass
        // 2026-09-21: cut ~8-16s of dead waiting per save). Polled instead of
        // one flat 2s sleep, so the chain ends the moment the POST lands
        // (speed pass 2026-09-25); the next popup's mount is already covered
        // by waitForPopupEl above, which resolves as soon as a modal appears.
        if (await pollUntil(() => (window.__lastSavePostAt || 0) > postStampBefore, 2000)) {
          steps.push('Save POST observed — popup chain complete');
          break;
        }
      }
      if (!popupText) popupText = 'SAVE UNCERTAIN: no popup handling was performed';
      // END-state report. The leftover-popup marker must only appear when the
      // save POST has NOT fired — that is the genuine unknown-outcome case the
      // server gate must stop on. After the POST the commit is done; any popup
      // still visible is post-save noise and is reported neutrally (a marker
      // here once hard-stopped a flow whose booking had already committed —
      // the read-back carried the block for a post-save popup, 2026-09-21).
      if ((window.__lastSavePostAt || 0) > postStampBefore) {
        const leftover = formatBlockingPopups(detectPopups());
        if (leftover) steps.push('Post-save popup still open (commit already confirmed by the save POST):' + leftover.replace('ACTIVE POPUPS', 'details'));
      } else {
        const leftover = formatBlockingPopups(detectPopups());
        if (leftover) steps.push(leftover.trim());
      }
    }

    // Step 3: optionally read the page back so the caller can verify the result.
    // The save POST's own response carrying the new SO# is the authoritative
    // commit proof (the XHR hook stamps it above) — when present, skip the
    // read-back entirely: the final summary is a fixed template that needs
    // nothing from the page, and the read cost ~3-6s of render waits per save
    // (speed pass 2026-09-25). Still read when the response was silent, so the
    // URL-redirect fallback verification stays intact.
    let readText = '';
    const respSoNo = ((window.__lastSaveSoNoAt || 0) > saveSoNoStampBefore
      && window.__lastSaveSoNo) ? window.__lastSaveSoNo : '';
    if (verify_read && !respSoNo) {
      try {
        // The SPA can sit in its "Saving..." state long after the popup chain
        // drains; reading immediately yielded "Shipping Order: undefined" and
        // the flow could never report the created number (2026-09-18). The
        // create flow navigates to /shipping_order/<SO>?...create_success=true
        // once the save commits — wait for that signal (edits are already on a
        // numbered URL and break out on the first check). The save RESPONSE
        // carrying the new so_no is an equally final signal — on it, skip the
        // remaining URL wait (the redirect may be interrupted entirely).
        for (let wait = 0; wait < 40; wait++) {
          if (/shipping_order\/(?!new(\?|#|$))[^/?#]+/.test(location.href)) break;
          if ((window.__lastSaveSoNoAt || 0) > saveSoNoStampBefore) break;
          if (wait % 5 === 0) reportProgress('Waiting for the save to complete…');
          await realWait(500);
        }
        const r = await executeTool('read_current_page', {});
        if (r && r.error) {
          return { content: [{ type: 'text', text: `save_and_confirm failed at read_current_page: ${r.error}` }] };
        }
        readText = (r && r.success && r.result?.content?.[0]?.text) ? r.result.content[0].text : '';
        // The read-back appends an "ACTIVE POPUPS" detection block for any open
        // modal; the server's leftover-popup gate keycases on that phrase and
        // would hard-stop a COMMITTED save over post-save noise (the outcome is
        // already known once the save POST fired). Strip it here — genuine
        // unknown-outcome leftovers are reported explicitly in Step 2 above.
        readText = readText.replace(/\n*⚠️\s*ACTIVE POPUPS:[\s\S]*$/g, '').trimEnd();
      } catch (err) {
        return { content: [{ type: 'text', text: `save_and_confirm failed at read_current_page: ${err.message}` }] };
      }
    } else if (verify_read) {
      steps.push(`Skipped the post-save page read — the save response already confirmed ${respSoNo}`);
    }

    const combined = steps.join('\n');
    // Lead with OUR verdict, not the click tool's: the classifier takes the
    // FIRST "RESULT:" token in the text, and the embedded click evidence often
    // reads "RESULT: unknown (no popup detected within timeout)" — the click
    // verification window is shorter than the popup mount on a loaded box. The
    // commit is a fact when the save POST was observed AND either the browser
    // landed on the created order's URL or the save RESPONSE carried the new
    // so_no — the server's own answer outranks anything on the page, because
    // the redirect can be interrupted or land on a list page (2026-09-21:
    // S20260990/S20260991 committed but reported as failures over the URL
    // test alone).
    const committed = (window.__lastSavePostAt || 0) > postStampBefore
      && (/shipping_order\/(?!new(\?|#|$))[^/?#]+/.test(location.href) || !!respSoNo);

    // Post-create landing (2026-10-02): the site redirects a CREATED booking to
    // /shipping_order/<SO>?editable=true&create_success=true#detail — EDIT
    // mode, even though the record is committed. That arms the "Are you sure
    // you want to leave?" guard for EVERY later navigation, so the follow-up
    // (fill-details) started from an "unsaved" page and tripped the guard. Exit
    // edit mode through the site's OWN handleCancel(): restores the saved
    // record, flips the editable flags off, strips ?editable=true, reloads the
    // SO — the chatbot ends on the SAVED view page and the guard is disarmed.
    // Only for a save that started on the create form; amendment saves already
    // land in view mode by themselves (the site reloads the SO in place). The
    // save RESPONSE can beat the SPA's redirect, so poll briefly for the
    // create_success param before deciding.
    if (committed && wasCreateForm) {
      // Land check: the ONLY thing we must detect is whether the redirect put
      // the new booking in EDIT mode (editable=true arms the leave-guard that
      // breaks the follow-up flow). Polling specifically for create_success
      // burned the full 6s whenever the SPA redirected without that param
      // (observed on UAT, 2026-10-12) — poll for the edit-mode signal itself:
      // done the moment the URL lands, view-mode or editable.
      const inEdit = () => /editable=true/.test(location.search);
      const landed = () => /shipping_order\/(?!new(\?|#|$))[^/?#]+/.test(location.href);
      let editableNow = inEdit();
      if (!editableNow && !landed()) {
        await pollUntil(() => landed() || inEdit(), 5000);
        editableNow = inEdit();
      }
      if (editableNow) {
        try {
          const soInst = findSoContextInstance();
          if (soInst && typeof soInst.handleCancel === 'function') {
            reportProgress('Returning to the saved view of the new booking…');
            soInst.handleCancel();
            // Confirm the strip instead of a flat sleep: handleCancel removes
            // ?editable=true and reloads — the moment it's gone, we're done.
            await pollUntil(() => !/editable=true/.test(location.search), 3000);
            steps.push('Exited edit mode — showing the new booking in its SAVED state');
          }
        } catch (e) {
          // Best-effort: never fail a committed save over the landing state.
          console.warn('[Chatbot] post-create exit-edit failed:', e && e.message);
        }
      }
    }

    const lead = committed
      ? ('RESULT: success (save POST observed; '
         + (respSoNo
            ? `the server accepted the save as ${respSoNo}`
            : 'the browser is now on the created shipping order')
         + ')\n'
         + (respSoNo ? `Created SO#: ${respSoNo}\n` : ''))
      : '';
    const text = lead + (readText ? `${combined}\n\nPage after save:\n${readText}` : combined);
    return { content: [{ type: 'text', text }] };
  } finally {
    window.confirm = _prevWindowConfirm;
  }
}


/**
 * Approve a shipping order end-to-end in ONE call: open the SO, load the
 * Booking Approval tab, set Approval Status = Approved, pick the Nth proposed
 * sailing, then Save + confirm the "Confirm Booking Approval" popup.
 * Collapses the 6-step approve flow into a single client-side execution.
 * Approval Status is set BEFORE the sailing pick (the proposed-sailing options
 * only render once status is "Approved"). Never throws: returns an error string
 * naming the failed step.
 */
async function approveBooking({ so_number, sailing_option }) {
  const opt = parseInt(sailing_option, 10) || 1;
  const steps = [];

  // Run one primitive; on failure return an error string naming the step.
  const run = async function (label, toolName, params) {
    try {
      const r = await executeTool(toolName, params);
      if (r && r.error) {
        return { ok: false, text: `approve_booking failed at ${label}: ${r.error}` };
      }
      const t = (r && r.success && r.result?.content?.[0]?.text) ? r.result.content[0].text : '';
      // Primitives return soft failures as normal success text wrapped by
      // executeTool as {success:true} with no r.error: open_shipping_order emits
      // "ERROR: Could not open..." on /AccessDeclined, read_so_tab emits
      // "ERROR: No Shipping Order is currently open", select_option emits
      // "Select not found" / "Could not find option", and select_proposed_sailing
      // emits "No proposed sailing list" / "Only N proposed". Detect these and halt
      // so a mutating Save never fires on bad state, matching the docstring contract
      // "Never throws: returns an error string naming the failed step".
      const failed = /^ERROR:/i.test(t)
        || /not found|Could not find|Could not access|No proposed sailing list|Only \d+ proposed/i.test(t);
      if (failed) {
        return { ok: false, text: `approve_booking failed at ${label}: ${t}` };
      }
      steps.push(`${label}${t ? ': ' + t : ''}`);
      return { ok: true };
    } catch (err) {
      return { ok: false, text: `approve_booking failed at ${label}: ${err.message}` };
    }
  };

  // 1. Open the shipping order.
  let f = await run(`open_shipping_order(${so_number})`, 'open_shipping_order', { so_number });
  if (!f.ok) return { content: [{ type: 'text', text: f.text }] };

  // 2. Load the Booking Approval tab (switches + reads; reveals proposed sailings).
  f = await run('read_so_tab(Booking Approval)', 'read_so_tab', { tab_name: 'Booking Approval' });
  if (!f.ok) return { content: [{ type: 'text', text: f.text }] };

  // 3. Set Approval Status = Approved (MUST precede the sailing pick).
  f = await run('select_option(Approval Status=Approved)', 'select_option', { label: 'Approval Status', value: 'Approved' });
  if (!f.ok) return { content: [{ type: 'text', text: f.text }] };

  // 4. Pick the Nth proposed sailing.
  f = await run(`select_proposed_sailing(option ${opt})`, 'select_proposed_sailing', { option_number: opt });
  if (!f.ok) return { content: [{ type: 'text', text: f.text }] };

  // 5. Save + confirm the "Confirm Booking Approval" popup (collapses the save tail).
  let saveText = '';
  try {
    const sr = await saveAndConfirm({ button_name: 'Save', expect_popup: true, verify_read: false });
    saveText = (sr && sr.content?.[0]?.text) ? sr.content[0].text : '';
    // The run() helper guards steps 1-4 but step 5 bypassed it, so a failed Save
    // (e.g. 'save_and_confirm failed at ...') was reported as 'Approved ...'. Halt
    // on a failed save just like run() does for the earlier steps.
    if (/save_and_confirm failed at/i.test(saveText)) {
      return { content: [{ type: 'text', text: `approve_booking failed at save_and_confirm: ${saveText}` }] };
    }
    steps.push(`save_and_confirm${saveText ? ': ' + saveText : ''}`);
  } catch (err) {
    return { content: [{ type: 'text', text: `approve_booking failed at save_and_confirm: ${err.message}` }] };
  }

  const text = `Approved ${so_number} sailing option ${opt}.\n${steps.join('\n')}`;
  return { content: [{ type: 'text', text }] };
}


// ==================== Ref Tools (fallback targeting layer, 2026-10-23) ====================
// read_page_interactive inventories visible interactive elements with short
// refs ([e12] button "Save"); click_ref / fill_ref / select_ref act on those
// refs exactly (no text-match ambiguity — the modal-Search vs page-Search
// class of bugs). Fallback ONLY: the named tools above stay the default path,
// and deterministic flows never emit these (the server layer lives in
// bms_tools.py; bms_flows/bms_flow_engine are untouched).
let _refMap = new Map(); // "e12" -> { el, kind, label, disabled }

/** Synchronous ref lookup for Chatbot.js gates (confirmation card). */
export function getRefInfo(ref) {
  const info = _refMap.get(String(ref || '').trim());
  if (!info || !info.el || !info.el.isConnected) return null;
  return { kind: info.kind, label: info.label, disabled: !!info.disabled };
}

const REF_MAX_ELEMENTS = 80;
const REF_MAX_CHARS = 4500;

// DOM never inventoried: the chat widget itself, transient toasts and
// notifications, portaled select dropdowns (select_ref owns those interactions).
// The page pass additionally skips open modal wraps (inventoried separately).
function _refExcluded(el, withModal) {
  if (!el.closest) return false;
  const sel = withModal
    ? '.chat-window, .chatbot-table-wrap, .ant-message, .ant-notification, .ant-select-dropdown, .ant-modal-wrap'
    : '.chat-window, .chatbot-table-wrap, .ant-message, .ant-notification, .ant-select-dropdown';
  return !!el.closest(sel);
}

function _refDisabled(el, kind) {
  if (kind === 'select') {
    const s = el.classList.contains('ant-select') ? el : el.closest('.ant-select');
    return !!(s && s.classList.contains('ant-select-disabled'));
  }
  if (el.disabled === true) return true;
  if (el.getAttribute && el.getAttribute('aria-disabled') === 'true') return true;
  return el.classList && el.classList.contains('ant-btn-disabled');
}

function _refLabel(el, kind) {
  const formItem = el.closest ? el.closest('.ant-form-item') : null;
  if (formItem) {
    const labelEl = formItem.querySelector('.ant-form-item-label label');
    if (labelEl && labelEl.textContent.trim()) return labelEl.textContent.trim();
  }
  const aria = el.getAttribute && (el.getAttribute('aria-label') || el.getAttribute('title'));
  if (aria && aria.trim()) return aria.trim();
  const ph = el.getAttribute && el.getAttribute('placeholder');
  if (ph && ph.trim()) return ph.trim();
  if (kind === 'checkbox' || kind === 'radio') {
    const t = (el.textContent || '').trim();
    if (t) return t;
  }
  return '';
}

function _refValue(el, kind) {
  try {
    if (kind === 'select') {
      const s = el.classList.contains('ant-select') ? el : el.closest('.ant-select');
      const item = s && s.querySelector('.ant-select-selection-item');
      return item ? (item.textContent || '').trim() : '';
    }
    if (kind === 'input' || kind === 'textarea') return el.value == null ? '' : String(el.value);
    if (kind === 'checkbox') {
      const box = el.querySelector('.ant-checkbox');
      const input = el.matches('input[type="checkbox"]') ? el : el.querySelector('input[type="checkbox"]');
      return !!(box && box.classList.contains('ant-checkbox-checked')) || !!(input && input.checked);
    }
  } catch (e) { /* best effort */ }
  return undefined;
}

/**
 * Build the interactive inventory and REPLACE _refMap wholesale (every build
 * restarts refs at e1; the previous page state's refs die with it). Returns
 * the text reported by read_page_interactive. Open modals are inventoried
 * FIRST (they portal to body-end and block the page behind them).
 */
function buildInteractiveInventory() {
  const nextMap = new Map();
  const lines = [];
  const notes = [];
  let n = 0;
  let capped = false;

  const add = (el, kind, label, value, disabled) => {
    if (nextMap.size >= REF_MAX_ELEMENTS) { capped = true; return; }
    n += 1;
    const ref = 'e' + n;
    const lab = String(label || '').substring(0, 40);
    nextMap.set(ref, { el, kind, label: lab, disabled: !!disabled });
    let line = `[${ref}] ${kind}`;
    if (lab) line += ` "${lab}"`;
    if (value === true) line += ' (checked)';
    else if (value === false) line += ' (unchecked)';
    else if (value !== undefined && value !== null && String(value) !== '') line += ` = "${String(value).substring(0, 40)}"`;
    if (disabled) line += ' (disabled)';
    lines.push(line);
  };

  const scanButtons = (root, withModal) => {
    for (const b of root.querySelectorAll('button, [role="button"], input[type="submit"]')) {
      if (nextMap.size >= REF_MAX_ELEMENTS) { capped = true; return; }
      if (!isElementVisible(b) || _refExcluded(b, withModal)) continue;
      const text = (b.textContent || '').trim();
      const label = text || (b.getAttribute('aria-label') || '').trim()
        || (b.getAttribute('title') || '').trim() || '(icon)';
      add(b, 'button', label, undefined, _refDisabled(b, 'button'));
    }
  };
  const scanTabs = (root, withModal) => {
    for (const t of root.querySelectorAll('.ant-tabs-tab')) {
      if (!isElementVisible(t) || _refExcluded(t, withModal)) continue;
      const btn = t.querySelector('.ant-tabs-tab-btn');
      const label = ((btn && btn.textContent) || t.textContent || '').trim();
      if (!label) continue;
      add(t, 'tab', label, undefined, false);
      if (t.classList.contains('ant-tabs-tab-active')) lines[lines.length - 1] += ' (active)';
    }
  };
  const scanSelects = (root, withModal) => {
    for (const s of root.querySelectorAll('.ant-select')) {
      if (nextMap.size >= REF_MAX_ELEMENTS) { capped = true; return; }
      if (!isElementVisible(s) || _refExcluded(s, withModal)) continue;
      add(s, 'select', _refLabel(s, 'select'), _refValue(s, 'select'), _refDisabled(s, 'select'));
    }
  };
  const scanInputs = (root, withModal) => {
    for (const inp of root.querySelectorAll('input, textarea')) {
      if (nextMap.size >= REF_MAX_ELEMENTS) { capped = true; return; }
      const type = (inp.getAttribute('type') || '').toLowerCase();
      if (['hidden', 'checkbox', 'radio', 'submit', 'button', 'file'].includes(type)) continue;
      if (!isElementVisible(inp) || _refExcluded(inp, withModal)) continue;
      if (inp.closest('.ant-select')) continue;                                  // select search input
      if (inp.closest('.ant-picker')) { notes.push('date fields: use set_date(label, value)'); continue; }
      const kind = inp.tagName === 'TEXTAREA' ? 'textarea' : 'input';
      add(inp, kind, _refLabel(inp, kind), _refValue(inp, kind), _refDisabled(inp, kind));
    }
  };
  const scanChecks = (root, withModal) => {
    for (const w of root.querySelectorAll('.ant-checkbox-wrapper, .ant-radio-wrapper')) {
      if (nextMap.size >= REF_MAX_ELEMENTS) { capped = true; return; }
      if (!isElementVisible(w) || _refExcluded(w, withModal)) continue;
      const kind = w.classList.contains('ant-checkbox-wrapper') ? 'checkbox' : 'radio';
      add(w, kind, _refLabel(w, kind), _refValue(w, kind), _refDisabled(w, kind));
    }
  };
  const scanRows = (root) => {
    const thead = root.querySelector('.ant-table-thead');
    if (thead) {
      const cols = Array.from(thead.querySelectorAll('th'))
        .map(th => (th.textContent || '').trim()).filter(Boolean);
      if (cols.length) lines.push(`columns: ${cols.slice(0, 12).join(', ')}${cols.length > 12 ? ', …' : ''}`);
    }
    const rows = Array.from(root.querySelectorAll('.ant-table-row'))
      .filter(r => isElementVisible(r) && !_refExcluded(r, true));
    if (rows.length === 0) return;
    if (rows.length > 8) {
      lines.push(`rows: ${rows.length} data rows — use filters / paginate / click_list_item instead of row refs`);
      return;
    }
    for (const row of rows) {
      if (nextMap.size >= REF_MAX_ELEMENTS) { capped = true; return; }
      const firstCell = row.querySelector('td');
      const label = firstCell ? (firstCell.textContent || '').trim() : '';
      // The SO link inside the row is the real opener; fall back to the row.
      add(row.querySelector('a') || row, 'row', label || '(row)', undefined, false);
    }
  };
  const scanLinks = (root, limit) => {
    let count = 0;
    for (const a of root.querySelectorAll('a')) {
      if (count >= limit) { break; }
      if (nextMap.size >= REF_MAX_ELEMENTS) { capped = true; return; }
      if (!isElementVisible(a) || _refExcluded(a, true)) continue;
      if (a.closest('.ant-table-row')) continue; // rows capture their own link
      const label = (a.textContent || '').trim();
      if (!label) continue;
      add(a, 'link', label, undefined, false);
      count += 1;
    }
  };

  // Open modals first — they block the page behind them until resolved.
  const openModals = Array.from(document.querySelectorAll('.ant-modal-wrap'))
    .filter(w => getComputedStyle(w).display !== 'none' && w.querySelector('.ant-modal'));
  for (const wrap of openModals) {
    const modal = wrap.querySelector('.ant-modal');
    const titleEl = modal.querySelector('.ant-modal-title');
    lines.push(`MODAL${titleEl ? ` "${(titleEl.textContent || '').trim().substring(0, 50)}"` : ''} (open — resolve before using PAGE refs):`);
    scanButtons(modal, false); scanSelects(modal, false);
    scanInputs(modal, false); scanChecks(modal, false);
    lines.push('');
  }

  const main = document.querySelector('main');
  lines.push(`PAGE ${window.location.pathname}:`);
  scanButtons(main || document.body, !main);
  scanTabs(main || document.body, !main);
  scanSelects(main || document.body, !main);
  scanInputs(main || document.body, !main);
  scanChecks(main || document.body, !main);
  if (main) { scanRows(main); scanLinks(main, 15); }

  let text = 'INTERACTIVE ELEMENTS (refs valid for the CURRENT page state only; on STALE_REF re-run read_page_interactive)\n'
    + `URL: ${window.location.pathname}\n\n` + lines.join('\n');
  if (capped) text += `\n…(element cap ${REF_MAX_ELEMENTS} reached — some elements not listed)`;
  if (notes.length) text += `\n(${Array.from(new Set(notes)).join('; ')})`;
  const popupInfo = formatBlockingPopups(detectPopups());
  if (popupInfo) text += popupInfo;
  if (text.length > REF_MAX_CHARS) text = text.substring(0, REF_MAX_CHARS) + '\n…(truncated)';

  _refMap = nextMap;
  return text;
}

/** Validate a ref against the live map. Returns { info } or { err }. */
function _resolveRef(ref) {
  const key = String(ref || '').trim();
  const info = _refMap.get(key);
  if (!info) {
    return { err: `STALE_REF: "${key}" is not a known ref. Call read_page_interactive first and use a ref from its output.` };
  }
  if (!info.el || !info.el.isConnected) {
    return { err: `STALE_REF: ${key} (${info.kind}${info.label ? ` "${info.label}"` : ''}) is gone from the page — call read_page_interactive again for fresh refs.` };
  }
  return { info };
}

async function readPageInteractive() {
  reportProgress('Scanning page for interactive elements…');
  await waitForRender();
  const text = buildInteractiveInventory();
  return { content: [{ type: 'text', text }] };
}

async function clickRef({ ref }) {
  const r = _resolveRef(ref);
  if (r.err) return { content: [{ type: 'text', text: r.err }] };
  const { el, kind, label, disabled } = r.info;
  if (disabled) {
    return { content: [{ type: 'text', text: `[${ref}] ${kind}${label ? ` "${label}"` : ''} is disabled — cannot click.` }] };
  }
  if (!isElementVisible(el)) {
    return { content: [{ type: 'text', text: `STALE_REF: ${ref} is no longer visible — call read_page_interactive again.` }] };
  }
  try { el.scrollIntoView({ block: 'center' }); } catch (e) { /* best effort */ }
  await realWait(80);
  el.click();
  // Outcome evidence for state-changing buttons — same contract as click_button
  // (a click is an attempt, not an outcome; late confirm modals mount 1-3s late).
  const name = label || kind;
  if (kind === 'button' && ACTION_VERIFY_BUTTONS.test(name)) {
    const res = await captureActionResult(name);
    return { content: [{ type: 'text', text: `Clicked [${ref}] button "${name}". RESULT: ${res.outcome} (${res.evidence})` }] };
  }
  await waitForRender();
  return { content: [{ type: 'text', text: `Clicked [${ref}] ${kind}${name && name !== '(icon)' ? ` "${name}"` : ''}.` }] };
}

async function fillRef({ ref, value }) {
  const r = _resolveRef(ref);
  if (r.err) return { content: [{ type: 'text', text: r.err }] };
  const { el, kind, label, disabled } = r.info;
  if (disabled) {
    return { content: [{ type: 'text', text: `[${ref}] ${kind}${label ? ` "${label}"` : ''} is disabled — cannot fill.` }] };
  }
  if (kind === 'select') {
    return { content: [{ type: 'text', text: `[${ref}] is a select — use select_ref("${ref}", option_text) instead.` }] };
  }
  if (kind === 'checkbox' || kind === 'radio') {
    return { content: [{ type: 'text', text: `[${ref}] is a ${kind} — use toggle_checkbox / select_radio with its label instead.` }] };
  }
  if (kind === 'button' || kind === 'link' || kind === 'tab' || kind === 'row') {
    return { content: [{ type: 'text', text: `[${ref}] is a ${kind}, not fillable — use click_ref.` }] };
  }
  if (el.closest && el.closest('.ant-picker')) {
    return { content: [{ type: 'text', text: `[${ref}] is a date field — use set_date(label, value) instead.` }] };
  }
  setReactInputValue(el, String(value == null ? '' : value));
  return { content: [{ type: 'text', text: `Filled [${ref}]${label ? ` "${label}"` : ''} = "${value}".` }] };
}

async function selectRef({ ref, option_text }) {
  const r = _resolveRef(ref);
  if (r.err) return { content: [{ type: 'text', text: r.err }] };
  const { el, kind, label } = r.info;
  if (kind !== 'select') {
    return { content: [{ type: 'text', text: `[${ref}] is a ${kind}, not a select — check read_page_interactive.` }] };
  }
  const sel = el.classList.contains('ant-select') ? el : el.closest('.ant-select');
  if (!sel || !sel.isConnected) {
    return { content: [{ type: 'text', text: `STALE_REF: ${ref} is no longer on the page — call read_page_interactive again.` }] };
  }
  if (sel.classList.contains('ant-select-disabled')) {
    return { content: [{ type: 'text', text: `Select [${ref}]${label ? ` "${label}"` : ''} is disabled — cannot change it.` }] };
  }
  const target = String(option_text || '').trim();
  if (!target) {
    return { content: [{ type: 'text', text: 'select_ref needs option_text — the visible text of the option to pick.' }] };
  }
  const picked = await triggerAntdSelectChange(sel, target, label || '');
  if (!picked) {
    return { content: [{ type: 'text', text: `No matching option for "${target}" in select [${ref}]${label ? ` "${label}"` : ''}. Re-check the option text, or use select_option with the field label.` }] };
  }
  return { content: [{ type: 'text', text: `Selected "${picked}" in select [${ref}]${label ? ` "${label}"` : ''}.` }] };
}


const TOOLS = {
  navigate_to_page:   navigateToPage,
  read_current_page:  readCurrentPage,
  click_button:       clickButton,
  click_list_item:    clickListItem,
  switch_so_tab:      switchSoTab,
  read_so_tab:        readSoTab,
  read_so_header:     readSoHeader,
  open_shipping_order: openShippingOrder,
  apply_custom_filter: applyCustomFilter,
  apply_filters:        applyFilters,
  paginate:           paginate,
  search_booking:     searchBooking,
  read_form:          readForm,
  fill_form:          fillForm,
  select_option:      selectOption,
  set_date:           setDate,
  toggle_checkbox:    toggleCheckbox,
  select_radio:       selectRadio,
  handle_popup_confirm: handlePopupConfirm,
  dismiss_all_popups: dismissAllPopups,
  poll_popups:        pollPopups,
  fill_table_cell:    fillTableCell,
  batch_form_actions: batchFormActions,
  open_so_for_edit:   openSoForEdit,
  exit_so_edit:       exitSoEdit,
  show_list:          showList,
  read_so:            readSo,
  select_proposed_sailing: selectProposedSailing,
  select_sailing_search_result: selectSailingSearchResult,
  save_and_confirm:   saveAndConfirm,
  approve_booking:    approveBooking,
  read_page_interactive: readPageInteractive,
  click_ref:          clickRef,
  fill_ref:           fillRef,
  select_ref:         selectRef,
};

// Tools whose result snapshot must wait for network settle (executeTool tail).
// Raw state-changing tools + view switches that trigger async renders. The
// self-verifying composite tools (batch_form_actions, save_and_confirm,
// approve_booking) are EXCLUDED: they run their own stabilization/verify waits
// internally and return their own RESULT verdict, so an external network-quiet
// gate only adds dead seconds behind the post-save page load (observed +11s
// and +5s on one save, 2026-10-12). Pure reads of the current page are
// excluded too — they trigger no traffic of their own.
const SETTLE_AFTER_TOOLS = new Set([
  'click_button', 'click_list_item', 'fill_form',
  'fill_table_cell', 'select_option', 'set_date', 'toggle_checkbox',
  'select_radio', 'handle_popup_confirm',
  'open_so_for_edit', 'open_shipping_order',
  'switch_so_tab', 'navigate_to_page', 'apply_filters', 'apply_custom_filter',
  'select_proposed_sailing', 'select_sailing_search_result',
  'click_ref', 'fill_ref', 'select_ref',
]);

/**
 * Execute a tool by name. Returns { success, result, toolName } or { error }.
 * Same return format as dummy_website2's executeWebMCPTool() function.
 */
export async function executeTool(toolName, params) {
  try {
    // Arm the network watch for this tool run (see installNotifyPartyPatch):
    // XHRs the page fires now were caused by this tool's clicks/fills, so the
    // save-hook may observe (and notify-default) them. Refreshed by every
    // tool; the TTL bleed after a crashed flow is bounded, never stuck on.
    window.__bmsWatchUntil = Date.now() + 180000;
    console.log(`[BMS Tools] Executing ${toolName} with params:`, params);

    const toolFn = TOOLS[toolName];
    if (!toolFn) {
      return {
        error: `Tool '${toolName}' not found. Available: ${Object.keys(TOOLS).join(', ')}`
      };
    }

    const _editBefore = _soIsEditable();
    const result = await toolFn(params);
    console.log(`[BMS Tools] ${toolName} completed:`, result);

    // Edit-session ownership sampling (2026-10-22): a chatbot action that
    // flips the SO INTO edit mode marks the session chatbot-owned (flows arm
    // it via open_so_for_edit's Edit click); a flip OFF clears it. Powers the
    // switch_so_tab self-heal and exit_so_edit — human-made edit sessions are
    // never discarded.
    _trackSoEditState(_editBefore, _soIsEditable());

    // Truth-channel settle gate (2026-10-12): after state-changing tools, wait
    // for XHR traffic to go quiet so the snapshot below (popup state + result
    // text the server verifies against) reflects the SETTLED page, not a
    // mid-render one. Reads of the current page (read_form/read_so_tab/...)
    // don't trigger renders and skip the gate. Time-boxed, never throws; a
    // long settle is surfaced in the text for the server-side classifiers.
    if (SETTLE_AFTER_TOOLS.has(toolName)) {
      // Skip the gate when the tool already produced its own verdict or made
      // no change (its own waits ran; the gate would only trail page-load
      // traffic and burn seconds — the +16s save regression, 2026-10-12).
      const _txt = result?.content?.[0]?.text || '';
      const selfVerified = /\bRESULT:\s/i.test(_txt) || /skipped redundant/i.test(_txt);
      if (!selfVerified) {
        const q = await waitForQuiet({ timeout: 5000 });
        if (q.waitedMs > 600 && result?.content?.[0]?.text) {
          result.content[0].text += `\n[settled after ${q.waitedMs}ms${q.settled ? '' : '; network still busy'}]`;
        }
      }
    }

    // Detect blocking popups and append to the result text so the LLM learns a
    // popup is blocking. Detect BEFORE auto-dismiss so the signal is not
    // destroyed first (M5: the old order hid success/error toasts). For
    // popup-prone tools, poll briefly — async server round-trips can mount a
    // confirm modal 1-3s after the click and a single snapshot misses it (M1).
    // Skip for the popup-handling tools themselves (they report their own result
    // and re-detecting the modal they just dismissed can loop on close animation).
    const isPopupTool = toolName === 'handle_popup_confirm' || toolName === 'dismiss_all_popups';
    if (!isPopupTool) {
      // Mutating actions can trigger an ASYNC server-side confirm modal that
      // mounts 1-3s AFTER the action — most notably the PRS-overwrite
      // Modal.confirm "Do you want to overwrite the following e-Booking info by
      // PRS info?" shown after a Shipper/Consignee change. A single snapshot
      // misses it and the modal is left orphaned (blocking the next turn). For
      // popup-prone tools AND party-field changes, wait up to ~4s for a blocking
      // popup so the agent can handle_popup_confirm it instead.
      const popupProneTools = ['batch_form_actions', 'fill_table_cell', 'navigate_to_page', 'open_so_for_edit', 'switch_so_tab'];
      const isVerifyClick = (toolName === 'click_button'
        && params && ACTION_VERIFY_BUTTONS.test(String(params.button_name || '').trim()))
        || (toolName === 'click_ref' && params && (() => {
          // A ref click on a Save/Submit-class button gets the same late-popup
          // watch as click_button (the ref's label carries the button text).
          const info = _refMap.get(String(params.ref || '').trim());
          return !!(info && info.kind === 'button' && info.el && info.el.isConnected
            && ACTION_VERIFY_BUTTONS.test(info.label || ''));
        })());
      const isPartyChange = (toolName === 'select_option' || toolName === 'select_radio' || toolName === 'toggle_checkbox')
        && params && /shipper|consignee|notify|agent/i.test(String(params.label || params.name || ''));
      // Party changes on the SO header gate the form apply behind a PRS
      // Modal.confirm — if it is not answered, the selection never reaches the
      // form state (the field stays empty even though the label renders).
      // Allow a generous window; the modal can mount late.
      // SKIP the wait right after a fast-path context set (restoreHeaderParty
      // stamps __fastPartySetAt): that path bypasses the UI select flow that
      // arms the PRS round-trip, so the 12s wait was pure dead time — 12 party
      // actions x 12s was the whole batch cost (~2 minutes, 2026-09-21).
      const fastPartySet = Date.now() - (window.__fastPartySetAt || 0) < 3000;
      // A "skipped redundant select" fired NO site handler, so no PRS/confirm
      // modal can follow it — the 12s popup watch is pure dead time. On a
      // leftover-filled form (a failed save leaves parties set) EVERY party
      // action skips, and 12 skips x 12s burned ~2.5 minutes (2026-10-12).
      const toolText = result?.content?.[0]?.text || '';
      const skippedSelect = /skipped redundant select|already set \(party id/i.test(toolText);
      if (!skippedSelect && (popupProneTools.includes(toolName) || isVerifyClick || (isPartyChange && !fastPartySet))) {
        await waitForPopup(isPartyChange ? 12000 : 4000);
      }
      let popupInfo = formatBlockingPopups(detectPopups());
      // Opportunistic SAFE-modal drain (2026-10-05): two modals pop at
      // unpredictable moments during the confirm/approve phases and BLOCK every
      // later click while they sit there (observed: the TBC-ETD Warning and the
      // auto-save recovery modal — the Confirm Booking click was swallowed
      // under one and the flow aborted opaquely). Their answers are already
      // decided by the flow's own steps (Warning -> OK, auto-save -> No), so
      // ANY tool that detects one answers it immediately instead of letting it
      // block the rest of the sequence. PRS-overwrite stays party-change-only
      // (it must not fire outside an intended party change).
      if (popupInfo) {
        await waitForQuiet({ timeout: 2000, quietMs: 250 });
        const safeRules = [
          // TBC ETD warning: OK -> dismiss (the ETD auto-update is the point of TBC mode).
          { label: 'TBC-ETD warning', re: /ETD of TBC Sailing Schedule/i, btn: /^(OK|Ok)$/i },
          // Auto-save recovery: No -> don't load a stale autosave over current state.
          { label: 'auto-save recovery', re: /auto save record is found/i, btn: /^No$/i },
        ];
        // ETD-vs-CargoReady gate: Yes -> proceed — but ONLY for the Confirm
        // Booking click. The SAVE click pops the same-titled validation as
        // well, and answering it inside the save chain made the post-save
        // redirect a FULL page navigation that killed the widget mid-chain
        // (2026-10-22 live: runs with the unconditional rule lost the thread
        // at every create; scoped to the confirm click the save path is
        // untouched and the confirm's own gate is answered).
        if (toolName === 'click_button' && /confirm booking/i.test(String(params.button_name || ''))) {
          safeRules.push({ label: 'ETD-vs-CargoReady warning',
                           re: /ETD is more than 14 days after Cargo Ready Date/i,
                           btn: /^Yes$/i });
        }
        // Auto-handle the blocking confirm modals that follow a party change so
        // they aren't orphaned on screen (the agent often didn't click through).
        // Direct DOM clicks (not handlePopupConfirm) avoid visibility races when
        // the PRS modal and an auto-save recovery modal stack on top of each other.
        const rules = (isPartyChange
          ? [{ label: 'PRS-overwrite', re: /overwrite the following e-Booking info by PRS info/i, btn: /^Confirm$/i }]
          : []).concat(safeRules);
        const did = [];
        for (const wrap of document.querySelectorAll('.ant-modal-wrap')) {
          const modal = wrap.querySelector('.ant-modal');
          if (!modal) continue;
          const body = modal.textContent || '';
          const footer = modalFooterContainer(modal);
          const btns = footer ? Array.from(footer.querySelectorAll('button')) : [];
          for (const r of rules) {
            if (r.re.test(body)) {
              const b = btns.find(function (x) { return r.btn.test((x.textContent || '').trim()); });
              if (b) { b.click(); did.push(r.label + ' -> "' + (b.textContent || '').trim() + '"'); }
              break;
            }
          }
        }
        if (did.length) {
          await waitForQuiet({ timeout: 2500, quietMs: 300 });
          popupInfo = formatBlockingPopups(detectPopups());
          if (result?.content?.[0]?.text) {
            result.content[0].text += `\n\n[AUTO] ${did.join('; ')}.`;
          }
        }
      }
      if (popupInfo && result?.content?.[0]?.text) {
        result.content[0].text += popupInfo;
      }
    }

    // After detection: silently dismiss non-critical popups (toasts/notifications).
    autoDismissNonCriticalPopups();

    return {
      success: true,
      result: result,
      toolName: toolName
    };
  } catch (error) {
    console.error(`[BMS Tools] Error executing ${toolName}:`, error);
    return { error: error.message };
  }
}

/**
 * Get list of available tool names (for debugging).
 */
export function getAvailableTools() {
  return Object.keys(TOOLS);
}
